import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  LayoutDashboard, Users, ShoppingCart, Package, Calendar, Truck, DollarSign, BarChart3,
  Search, Bell, Menu, User as UserIcon, Settings, LogOut, Plus, Heart,
  Clock, Edit, Trash2, Eye, X, Save, MessageCircle, Cake, Gift, ChevronLeft, ChevronRight, Printer, Home, Store, BookOpen, Instagram, MapPin, Image as ImageIcon, MessageSquare, VolumeX, ArrowUpCircle, ArrowDownCircle, Banknote, PackagePlus, Ticket,
  Key // Ícone adicionado
} from 'lucide-react';

// --- CORREÇÃO ---
// Importando 'functions' do seu arquivo de configuração do Firebase.
import { auth, db, storage, functions, firebasePublicConfig } from './firebaseConfig.js';
//import { firebaseConfig } from './firebaseConfig.js';

// --- CORREÇÃO ---
// Importando 'httpsCallable' para poder chamar suas Cloud Functions.
import { httpsCallable } from "firebase/functions";

// Importações do Firebase SDK
// ATUALIZADO: Adicionado fluxo com redirect para login Google e reset de senha
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithRedirect, signInWithPopup, getRedirectResult, sendPasswordResetEmail, setPersistence, browserLocalPersistence } from "firebase/auth";
// CORRIGIDO: Adicionado 'getDocs' à importação
import { collection, onSnapshot, query, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc, where, getDocs, limit, orderBy, Timestamp, serverTimestamp, arrayUnion, writeBatch } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// --- CORREÇÃO: Importa o novo AudioManager ---
import { audioManager } from './utils/AudioManager.js';
import { registerDeviceForPush, listenForForegroundMessages, subscribeToServiceWorkerMessages } from './utils/notifications.js';
import { updateStock as updateStockService } from './services/stockService.js';
import { StoreOperatingHoursPanel } from './components/StoreOperatingHoursPanel.jsx';
import { HomePage } from './components/home/HomePage.jsx';
import {
  isPopupFallbackError,
  logAuthRuntimeDiagnostics,
  mapAuthErrorToUserMessage,
  warnIfHostOutsideAllowlist,
} from './utils/authDiagnostics.js';

// --- importação para Android
import { NativeAudio } from '@capacitor-community/native-audio';
import { Capacitor } from '@capacitor/core';

// ✅ CORREÇÃO: URL local para evitar erro de pré-condição no Firebase Storage
const ALARM_SOUND_URL = '/audio/alarm.mp3';
const API_BASE_URL = 'https://us-central1-apfood-e9627.cloudfunctions.net/api';

const ROLE_OWNER = 'dono';
const ROLE_MANAGER = 'gerente';
const ROLE_ATTENDANT = 'atendente';
const ROLE_DEFAULT = ROLE_ATTENDANT;
const STORE_ALL_KEY = '__all__';
const DEFAULT_ALARM_SNOOZE_MINUTES = 5;
const MIN_ALARM_SNOOZE_MINUTES = 1;
const MAX_ALARM_SNOOZE_MINUTES = 120;
const MAX_LOGO_FILE_SIZE_BYTES = 1 * 1024 * 1024;
const ACCEPTED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
const DEFAULT_FORNECEDOR_CATEGORIES = ['Insumos', 'Embalagens', 'Bebidas', 'Decoração', 'Serviços'];
const CONFIG_DOC_ID = 'config';
const GOOGLE_AUTH_FLOW_KEY = 'google-auth-flow-in-progress';
const AUTH_PROGRESS_KEY = 'auth-in-progress';
const AUTH_RETURN_URL_KEY = 'auth-return-url';
const AUTH_TENANT_SLUG_KEY = 'auth-tenant-slug';
const AUTH_RETURN_URL_QUERY_PARAM = 'authReturnUrl';
const AUTH_TENANT_SLUG_QUERY_PARAM = 'tenantSlug';
const AUTH_CENTER_ORIGIN = process.env.REACT_APP_AUTH_CENTER_ORIGIN || 'https://apfood.com.br';
const ROOT_DOMAIN = 'apfood.com.br';
const CONFIG_COLLECTIONS = new Set(['cupons', 'logs']);
const MENU_PERMISSION_KEYS = [
  'pagina-inicial',
  'dashboard',
  'clientes',
  'pedidos',
  'produtos',
  'agenda',
  'fornecedores',
  'relatorios',
  'meu-espaco',
  'financeiro',
  'configuracoes'
];

const buildStoreCollectionPath = (storeId, collectionName, useLegacyPath = false) => {
  const shouldUseConfigPath = CONFIG_COLLECTIONS.has(collectionName) && !useLegacyPath;
  return shouldUseConfigPath
    ? ['lojas', storeId, 'configuracoes', CONFIG_DOC_ID, collectionName]
    : ['lojas', storeId, collectionName];
};

const getStoreCollectionRef = (storeId, collectionName, useLegacyPath = false) => {
  if (collectionName === 'clientes') {
    return collection(db, 'clientes');
  }

  return collection(db, ...buildStoreCollectionPath(storeId, collectionName, useLegacyPath));
};

const getStoreDocRef = (storeId, collectionName, docId, useLegacyPath = false) => {
  if (collectionName === 'clientes') {
    return doc(db, 'clientes', docId);
  }

  return doc(db, ...buildStoreCollectionPath(storeId, collectionName, useLegacyPath), docId);
};

const getStoreConfigDocRef = (storeId) => doc(db, 'lojas', storeId, 'configuracoes', CONFIG_DOC_ID);

const sanitizeAlarmSnoozeMinutes = (value, fallback = DEFAULT_ALARM_SNOOZE_MINUTES) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const rounded = Math.round(parsed);
  if (rounded < MIN_ALARM_SNOOZE_MINUTES || rounded > MAX_ALARM_SNOOZE_MINUTES) return fallback;
  return rounded;
};

const getStoreAlarmSnoozeMinutes = async (storeId) => {
  if (!storeId || storeId === STORE_ALL_KEY) {
    return DEFAULT_ALARM_SNOOZE_MINUTES;
  }

  const configSnap = await getDoc(getStoreConfigDocRef(storeId));
  if (!configSnap.exists()) {
    return DEFAULT_ALARM_SNOOZE_MINUTES;
  }

  const data = configSnap.data() || {};
  const resolvedValue = data.alarmSnoozeMinutes ?? data?.alarm?.snoozeMinutes;
  return sanitizeAlarmSnoozeMinutes(resolvedValue);
};

const saveStoreAlarmSnoozeMinutes = async (storeId, minutes) => {
  if (!storeId || storeId === STORE_ALL_KEY) {
    throw new Error('Selecione uma loja válida para salvar a configuração do alarme.');
  }

  const normalizedMinutes = sanitizeAlarmSnoozeMinutes(minutes);
  await setDoc(getStoreConfigDocRef(storeId), { alarmSnoozeMinutes: normalizedMinutes }, { merge: true });
  return normalizedMinutes;
};

const resolveStoreAlarmSnoozeMinutes = (selectedStoreId, storeSettingsMap, defaultValue = DEFAULT_ALARM_SNOOZE_MINUTES) => {
  if (!selectedStoreId || selectedStoreId === STORE_ALL_KEY) {
    return defaultValue;
  }

  const configuredValue = storeSettingsMap?.[selectedStoreId];
  return sanitizeAlarmSnoozeMinutes(configuredValue, defaultValue);
};

const getTenantSlugFromHostname = (hostname = '') => {
  const normalizedHost = (hostname || '').toLowerCase();
  const localHosts = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

  if (!normalizedHost || localHosts.has(normalizedHost)) {
    return null;
  }

  const suffix = `.${ROOT_DOMAIN}`;
  if (!normalizedHost.endsWith(suffix)) {
    return null;
  }

  const subdomain = normalizedHost.slice(0, -suffix.length);
  if (!subdomain || subdomain.includes('.')) {
    return null;
  }

  return /^[a-z0-9-]{2,50}$/.test(subdomain) ? subdomain : null;
};

const COLLECTIONS_TO_SYNC = [
  'produtos',
  'subcategorias',
  'categoriasFornecedores',
  'contas_a_pagar',
  'contas_a_receber',
  'fornecedores',
  'pedidosCompra',
  'estoque',
  'kardex',
  'perdasDescarte',
  'logs',
  'cupons',
  'pedidos'
];

const getInitialDataState = () => ({
  clientes: [],
  ...COLLECTIONS_TO_SYNC.reduce((acc, collection) => ({
    ...acc,
    [collection]: []
  }), {}),
  users: []
});

const normalizeRole = (role) => {
  if (!role || typeof role !== 'string') return ROLE_DEFAULT;

  const value = role.trim().toLowerCase();
  if (!value) return ROLE_DEFAULT;

  const normalizedValue = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const ownerAliases = new Set([
    ROLE_OWNER,
    'owner',
    'proprietario',
    'proprietaria',
    'admin',
    'adm',
    'administrador',
    'administradora',
    'adminstrador',
    'adminstradora',
    'superadmin',
    'superadministrador',
    'superadministradora'
  ]);

  const managerAliases = new Set([
    ROLE_MANAGER,
    'manager',
    'gerencia',
    'gerente',
    'gestor',
    'gestora'
  ]);

  const attendantAliases = new Set([
    ROLE_ATTENDANT,
    'atendente',
    'colaborador',
    'colaboradora',
    'funcionario',
    'funcionaria',
    'vendedor',
    'vendedora'
  ]);

  if (ownerAliases.has(normalizedValue)) {
    return ROLE_OWNER;
  }

  if (managerAliases.has(normalizedValue)) {
    return ROLE_MANAGER;
  }

  if (attendantAliases.has(normalizedValue)) {
    return ROLE_ATTENDANT;
  }

  if ([ROLE_OWNER, ROLE_MANAGER, ROLE_ATTENDANT].includes(value)) {
    return value;
  }

  return ROLE_DEFAULT;
};

const getDefaultPermissionsForRole = (role) => {
  const base = MENU_PERMISSION_KEYS.reduce((acc, key) => ({ ...acc, [key]: false }), {});
  const normalizedRole = normalizeRole(role);

  if (normalizedRole === ROLE_OWNER) {
    return MENU_PERMISSION_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {});
  }

  if (normalizedRole === ROLE_MANAGER) {
    return {
      ...base,
      'pagina-inicial': true,
      dashboard: true,
      clientes: true,
      pedidos: true,
      produtos: true,
      agenda: true,
      fornecedores: true,
      relatorios: true,
      'meu-espaco': true,
      financeiro: true,
      configuracoes: true,
    };
  }

  return {
    ...base,
    'pagina-inicial': true,
    clientes: true,
    pedidos: true,
    agenda: true,
    'meu-espaco': true,
  };
};

const sanitizePermissions = (permissions, role) => {
  const defaults = getDefaultPermissionsForRole(role);
  if (!permissions || typeof permissions !== 'object') return defaults;

  return MENU_PERMISSION_KEYS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(permissions, key)) {
      acc[key] = Boolean(permissions[key]);
    } else {
      acc[key] = defaults[key];
    }
    return acc;
  }, {});
};

const extractStoreIdsFromProfile = (profile) => {
  if (!profile) return [];
  const { lojaId, lojaIds, lojas } = profile;
  if (Array.isArray(lojaIds) && lojaIds.length) return lojaIds;
  if (Array.isArray(lojas) && lojas.length) return lojas;
  if (Array.isArray(lojaId) && lojaId.length) return lojaId;
  if (typeof lojaId === 'string' && lojaId.trim().length) return [lojaId.trim()];
  return [];
};

const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('55')) {
    return digits.length >= 12 ? digits : digits;
  }
  if (digits.length >= 11) {
    return `55${digits}`;
  }
  if (digits.length === 10) {
    return `55${digits}`;
  }
  return '';
};

const getOrderAddressDetails = (order, clientes = []) => {
  if (!order) return { cliente: null, enderecoTexto: '', locationLink: '' };

  const cliente = clientes.find((c) => c.id === order.clienteId) || null;
  const normalizeEnderecoObj = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return { enderecoCompleto: value };
    if (typeof value === 'object') return value;
    return null;
  };

  let enderecoTexto = '';
  if (typeof order.clienteEndereco === 'string') {
    enderecoTexto = order.clienteEndereco;
  } else if (typeof order.clienteEndereco === 'object' && order.clienteEndereco !== null) {
    enderecoTexto = order.clienteEndereco.enderecoCompleto || order.clienteEndereco.texto || '';
  }

  if (!enderecoTexto && cliente) {
    if (Array.isArray(cliente.enderecos) && cliente.enderecos.length > 0) {
      const primeiroEndereco = normalizeEnderecoObj(cliente.enderecos[0]);
      enderecoTexto = primeiroEndereco?.enderecoCompleto || '';
    } else if (cliente.endereco) {
      enderecoTexto = cliente.endereco;
    }
  }

  if (!enderecoTexto) {
    enderecoTexto = 'Não informado';
  }

  let enderecoSelecionado = null;
  if (cliente && Array.isArray(cliente.enderecos)) {
    enderecoSelecionado = cliente.enderecos.find((item) => {
      const normalizado = normalizeEnderecoObj(item);
      if (!normalizado) return false;
      if (!enderecoTexto || enderecoTexto === 'Não informado') return false;
      return normalizado.enderecoCompleto === enderecoTexto;
    }) || null;
  }

  let lat = null;
  let lng = null;
  if (enderecoSelecionado && typeof enderecoSelecionado === 'object') {
    const latNumber = parseFloat(enderecoSelecionado.lat);
    const lngNumber = parseFloat(enderecoSelecionado.lng);
    lat = Number.isNaN(latNumber) ? null : latNumber;
    lng = Number.isNaN(lngNumber) ? null : lngNumber;
  }

  const locationLink = lat !== null && lng !== null
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : enderecoTexto && enderecoTexto !== 'Não informado'
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(enderecoTexto)}`
      : '';

  return { cliente, enderecoTexto, locationLink };
};

// Hook customizado para estado persistente na sessão
const usePersistentState = (key, defaultValue) => {
  // Inicializa o estado apenas uma vez com o valor do sessionStorage
  const [state, setState] = useState(() => {
    try {
      const storedValue = sessionStorage.getItem(key);
      return storedValue ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
      console.error('Erro ao ler do sessionStorage', error);
      return defaultValue;
    }
  });

  // Referência para evitar salvar no primeiro render
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Pula o primeiro render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error('Erro ao salvar no sessionStorage', error);
    }
  }, [key, state]);

  return [state, setState];
};


// Componentes de UI
const Modal = ({ isOpen, onClose, title, children, size = "md" }) => {
  if (!isOpen) return null;
  const sizeClasses = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" };
  return ( <div className="fixed inset-0 z-50 flex items-center justify-center p-4"> <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} /> <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col`}> <div className="flex items-center justify-between p-6 border-b border-gray-100"> <h2 className="text-xl font-semibold text-gray-800">{title}</h2> <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors"> <X className="w-5 h-5" /> </button> </div> <div className="p-6 overflow-y-auto"> {children} </div> </div> </div> );
};
const Button = ({ children, variant = "primary", size = "md", onClick, className = "", disabled = false, type = "button" }) => {
  const baseClasses = "font-medium rounded-xl transition-all flex items-center gap-2 justify-center";
  const variants = {
    primary: "bg-gradient-to-r from-pink-500 to-rose-600 text-white hover:from-pink-600 hover:to-rose-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5",
    secondary: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-md hover:shadow-lg",
    outline: "bg-white text-pink-600 border border-pink-200 hover:bg-pink-50 shadow-sm",
    danger: "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl"
  };
  const sizes = { sm: "px-4 py-2 text-sm", md: "px-6 py-3", lg: "px-8 py-4 text-lg" };
  return (<button type={type} onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>{children}</button>);
};
const Input = ({ label, error, className = "", ...props }) => (<div className="space-y-1 w-full">{label && <label className="block text-sm font-medium text-gray-700">{label}</label>}<input {...props} className={`w-full px-4 py-3 border rounded-xl transition-all focus:ring-2 focus:ring-pink-500 focus:border-transparent ${error ? 'border-red-300' : 'border-gray-300'} ${className}`} />{error && <p className="text-sm text-red-600">{error}</p>}</div>);
const Textarea = ({ label, error, className = "", ...props }) => (<div className="space-y-1">{label && <label className="block text-sm font-medium text-gray-700">{label}</label>}<textarea {...props} className={`w-full px-4 py-3 border rounded-xl transition-all focus:ring-2 focus:ring-pink-500 focus:border-transparent ${error ? 'border-red-300' : 'border-gray-300'} ${className}`} />{error && <p className="text-sm text-red-600">{error}</p>}</div>);
const Select = ({ label, error, className = "", children, ...props }) => (<div className="space-y-1 w-full">{label && <label className="block text-sm font-medium text-gray-700">{label}</label>}<select {...props} className={`w-full px-4 py-3 border rounded-xl transition-all focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white ${error ? 'border-red-300' : 'border-gray-300'} ${className}`}>{children}</select>{error && <p className="text-sm text-red-600">{error}</p>}</div>);

// Componente de Tabela Responsiva
const Table = ({ columns, data, actions = [] }) => (
    <>
        {/* Visualização de Tabela para Desktop */}
        <div className="hidden md:block bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr>
                            {columns.map((col, index) => (<th key={index} className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{col.header}</th>))}
                            {actions.length > 0 && <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Ações</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {(data || []).map((row, rowIndex) => (
                            <tr key={row.id || row.uid || rowIndex} className="hover:bg-gradient-to-r hover:from-pink-50/50 hover:to-rose-50/50 transition-all">
                                {columns.map((col, colIndex) => (
                                    <td key={colIndex} className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{col.render ? col.render(row) : row[col.key]}</td>
                                ))}
                                {actions.length > 0 && (
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            {actions.map((action, actionIndex) => (
                                                <button key={actionIndex} onClick={() => action.onClick(row)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title={action.label}>
                                                    <action.icon className="w-4 h-4 text-gray-600" />
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>

        {/* Visualização de Cards para Celular */}
        <div className="block md:hidden space-y-4">
            {(data || []).map((row, rowIndex) => (
                <div key={row.id || row.uid || rowIndex} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 space-y-2">
                    {columns.map((col, colIndex) => {
                        const content = col.render ? col.render(row) : row[col.key];
                        if (content === '-' || content === null || content === undefined) return null;

                        return (
                             <div key={colIndex} className={`text-sm ${colIndex === 0 ? 'font-bold text-lg text-pink-600' : ''}`}>
                                {colIndex > 0 && <p className="text-xs text-gray-500">{col.header}</p>}
                                <div className={colIndex === 0 ? 'mt-0' : 'mt-1'}>{content}</div>
                             </div>
                        )
                    })}
                    {actions.length > 0 && (
                        <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-gray-100">
                            {actions.map((action, actionIndex) => (
                                <button key={actionIndex} onClick={() => action.onClick(row)} className="flex items-center gap-2 p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm text-gray-700" title={action.label}>
                                    <action.icon className="w-4 h-4" />
                                    <span>{action.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    </>
);

const DeliveryModal = ({ isOpen, onClose, order, clientes = [], fornecedores = [] }) => {
  const [selectedDeliverer, setSelectedDeliverer] = useState('');
  const [error, setError] = useState('');
  const availableDeliverers = useMemo(
    () => (fornecedores || []).filter((f) => (f.status || 'Ativo') !== 'Inativo'),
    [fornecedores]
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedDeliverer('');
      setError('');
    }
  }, [isOpen, order?.id]);

  if (!isOpen || !order) return null;

  const { enderecoTexto, locationLink } = getOrderAddressDetails(order, clientes);
  const canSend =
    availableDeliverers.length > 0 &&
    enderecoTexto &&
    enderecoTexto !== 'Não informado' &&
    enderecoTexto !== 'Retirar na Loja';

  const handleSend = () => {
    if (!selectedDeliverer) {
      setError('Selecione um entregador.');
      return;
    }
    const deliverer = availableDeliverers.find((f) => f.id === selectedDeliverer);
    const whatsappNumber = formatPhoneForWhatsApp(deliverer?.contato_whatsapp || deliverer?.contato_telefone);
    if (!deliverer || !whatsappNumber) {
      setError('O entregador selecionado não possui um telefone/WhatsApp válido.');
      return;
    }
    if (!canSend) {
      setError('Este pedido não possui um endereço válido para entrega.');
      return;
    }

    let message = `Olá ${deliverer.nome?.split(' ')[0] || 'entregador'}, segue o endereço para entrega.\n\n`;
    message += `Pedido: ${order.id?.substring(0, 8) || '-'}\n`;
    message += `Cliente: ${order.clienteNome || 'Cliente'}\n`;
    message += `Endereço: ${enderecoTexto}\n`;
    if (locationLink) {
      message += `Localização: ${locationLink}\n`;
    }
    if (order.telefone) {
      message += `Telefone do cliente: ${order.telefone}\n`;
    }
    if (order.observacao) {
      message += `Observações: ${order.observacao}\n`;
    }
    if (order.formaPagamento) {
      message += `Pagamento: ${order.formaPagamento}\n`;
    }
    if (order.total) {
      message += `Total: R$ ${(order.total || 0).toFixed(2)}\n`;
    }

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enviar endereço para entregador" size="md">
      <div className="space-y-4 text-sm text-gray-700">
        <div className="p-3 bg-gray-50 rounded-xl space-y-1">
          <p><strong>Cliente:</strong> {order.clienteNome || 'Cliente'}</p>
          <p><strong>Endereço:</strong> {enderecoTexto}</p>
          {locationLink && (
            <p className="truncate">
              <strong>Localização:</strong>{' '}
              <a href={locationLink} target="_blank" rel="noreferrer" className="text-pink-600 underline">
                Abrir no mapa
              </a>
            </p>
          )}
        </div>

        {availableDeliverers.length === 0 ? (
          <p className="text-sm text-red-500">Nenhum entregador cadastrado nos fornecedores.</p>
        ) : (
          <Select
            label="Selecione o entregador"
            value={selectedDeliverer}
            onChange={(e) => {
              setSelectedDeliverer(e.target.value);
              setError('');
            }}
          >
            <option value="">Escolha um entregador</option>
            {availableDeliverers.map((deliverer) => (
              <option key={deliverer.id} value={deliverer.id}>
                {deliverer.nome} {deliverer.categoria ? `(${deliverer.categoria})` : ''}
              </option>
            ))}
          </Select>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSend}
            disabled={!canSend || availableDeliverers.length === 0}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
          >
            <Truck className="w-4 h-4" /> Enviar via WhatsApp
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const generateStoreId = (value) => {
  if (!value) return '';
  const cleaned = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  return cleaned || `loja-${Date.now()}`;
};

const normalizeTenantSlug = (tenantValue) => {
  const base = (tenantValue || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  return base.replace(/-/g, '') || 'tenant';
};

const slugifyStoreName = (value) => {
  return generateStoreId(value).replace(/[^a-z0-9_-]/g, '-').slice(0, 40);
};

const normalizeStoreIdentifier = (value) => {
  const normalized = (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '')
    .slice(0, 40);

  return normalized;
};

const buildStorePublicPath = (tenantSlug, lojaSlug) => `${tenantSlug}_${lojaSlug}`;

const StoreManagerModal = ({
  isOpen,
  onClose,
  availableStores,
  storeInfoMap,
  onCreateStore,
  onSelectStore,
  canCreate,
  allowAllOption,
  currentStoreId,
  isCreatingStore
}) => {
  const [storeName, setStoreName] = useState('');
  const [storeIdInput, setStoreIdInput] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStoreName('');
      setStoreIdInput('');
      setError('');
      setSuccessMessage('');
	  setShowForm(false);

    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && canCreate && availableStores.length === 0) {
      setShowForm(true);
    }
  }, [isOpen, canCreate, availableStores.length]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canCreate) return;

    setError('');
    setSuccessMessage('');

    const name = storeName.trim();
    if (!name) {
      setError('Informe o nome da loja.');
      return;
    }

    let finalId = storeIdInput.trim();
    if (finalId) {
      finalId = generateStoreId(finalId);
    } else {
      finalId = generateStoreId(name);
    }

    if (finalId === STORE_ALL_KEY) {
      setError('Identificador inválido.');
      return;
    }

    if (!finalId) {
      setError('Não foi possível gerar um identificador para a loja.');
      return;
    }

    if (availableStores.includes(finalId)) {
      setError('Já existe uma loja com esse identificador.');
      return;
    }

    try {
      await onCreateStore({ storeId: finalId, nome: name });
      setSuccessMessage(`Loja "${name}" criada com sucesso!`);
      setStoreName('');
      setStoreIdInput('');
	  setShowForm(false);

    } catch (createError) {
      setError(createError.message || 'Não foi possível criar a loja.');
    }
  };

  const handleSelect = (value) => {
    onSelectStore(value);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gerenciar lojas" size="lg">
      <div className="space-y-6">
        {canCreate && (
          <div className="space-y-4">
            {showForm ? (
              <form onSubmit={handleSubmit} className="space-y-4 p-4 border border-gray-200 rounded-xl bg-white shadow-sm">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Adicionar nova loja</h3>
                  <p className="text-sm text-gray-500">Informe um nome para identificar a loja. Você pode ajustar o identificador se necessário.</p>
                </div>
                <Input
                  label="Nome da loja"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  placeholder="Ex: Loja Centro"
                  required
                />
                <Input
                  label="Identificador (opcional)"
                  value={storeIdInput}
                  onChange={(e) => setStoreIdInput(e.target.value)}
                  placeholder="Ex: loja-centro"
                />
                {error && <p className="text-sm text-red-500">{error}</p>}
                <div className="flex justify-end gap-3">
                  {availableStores.length > 0 && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowForm(false);
                        setStoreName('');
                        setStoreIdInput('');
                        setError('');
                      }}
                    >
                      Cancelar
                    </Button>
                  )}
                  <Button type="submit" disabled={isCreatingStore}>
                    <Plus className="w-4 h-4" />
                    {isCreatingStore ? 'Salvando...' : 'Criar loja'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="p-4 border border-gray-200 rounded-xl flex items-center justify-between bg-white shadow-sm">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Adicionar nova loja</h3>
                  <p className="text-sm text-gray-500">Crie lojas para organizar suas unidades. Você pode adicionar quantas quiser.</p>
                </div>
                <Button
                  onClick={() => {
                    setShowForm(true);
                    setError('');
                    setSuccessMessage('');
                  }}
                >
                  <Plus className="w-4 h-4" /> Nova loja
                </Button>
              </div>
            )}
            {successMessage && !showForm && (
              <p className="text-sm text-green-600">{successMessage}</p>
            )}
          </div>
        )}

        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800">Lojas disponíveis</h3>

          {allowAllOption && availableStores.length > 0 && (
            <div className="p-4 border border-gray-200 rounded-xl flex items-center justify-between bg-gray-50">
              <div>
                <p className="font-semibold text-gray-800">Visão Geral</p>
                <p className="text-xs text-gray-500">Exibe dados combinados de todas as lojas.</p>
              </div>
              <Button
                size="sm"
                variant={currentStoreId === STORE_ALL_KEY ? 'secondary' : 'primary'}
                onClick={() => handleSelect(STORE_ALL_KEY)}
                disabled={currentStoreId === STORE_ALL_KEY}
              >
                {currentStoreId === STORE_ALL_KEY ? 'Selecionada' : 'Selecionar'}
              </Button>
            </div>
          )}

          {availableStores.length === 0 ? (
            <div className="p-6 border border-dashed border-gray-300 rounded-xl text-center space-y-3 bg-gray-50">
              <Store className="w-10 h-10 mx-auto text-gray-400" />
              <p className="text-base font-semibold text-gray-700">Nenhuma loja cadastrada</p>
              <p className="text-sm text-gray-500">Crie sua primeira loja para começar a organizar sua operação.</p>
              {canCreate && (
                <Button
                  onClick={() => {
                    setShowForm(true);
                    setError('');
                    setSuccessMessage('');
                  }}
                >
                  <Plus className="w-4 h-4" /> Criar primeira loja
                </Button>
              )}
            </div>
          ) : (
            availableStores.map((storeId) => {
              const info = storeInfoMap[storeId] || {};
              return (
                <div key={storeId} className="p-4 border border-gray-200 rounded-xl flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">{info.nome || storeId}</p>
                    <p className="text-xs text-gray-500">ID: {storeId}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={currentStoreId === storeId ? 'secondary' : 'primary'}
                    onClick={() => handleSelect(storeId)}
                    disabled={currentStoreId === storeId}
                  >
                    {currentStoreId === storeId ? 'Selecionada' : 'Selecionar'}
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};


// Helper function
const getJSDate = (firestoreTimestamp) => {
  if (!firestoreTimestamp) return null;
  if (typeof firestoreTimestamp.toDate === 'function') {
    return firestoreTimestamp.toDate();
  }
  const date = new Date(firestoreTimestamp);
  return isNaN(date.getTime()) ? null : date;
};

// --- NOVOS COMPONENTES ---

const Fornecedores = ({ data, addItem, updateItem, deleteItem, setConfirmDelete, effectiveStoreId, updateStock, currentUser }) => {
    const [activeTab, setActiveTab] = usePersistentState('fornecedores_activeTab', 'fornecedores');
    
    // States
    const [searchTerm, setSearchTerm] = usePersistentState('fornecedores_searchTerm', '');
    
    const [showFornecedorModal, setShowFornecedorModal] = useState(false);
    const [editingFornecedor, setEditingFornecedor] = useState(null);
    const [fornecedorFormData, setFornecedorFormData] = useState({});
    
    const [showPedidoModal, setShowPedidoModal] = useState(false);
    const [editingPedido, setEditingPedido] = useState(null);
    const [pedidoFormData, setPedidoFormData] = useState({ fornecedorId: '', itens: [], valorTotal: 0, dataPedido: new Date().toISOString().split('T')[0], dataPrevistaEntrega: '', status: 'Pendente' });

    const [showEstoqueModal, setShowEstoqueModal] = useState(false);
    const [editingEstoque, setEditingEstoque] = useState(null);
    const [estoqueFormData, setEstoqueFormData] = useState({});

    const [showPerdaModal, setShowPerdaModal] = useState(false);
    const [editingPerda, setEditingPerda] = useState(null);
    const [perdaFormData, setPerdaFormData] = useState({ produtoId: '', produtoNome: '', custoUnitario: '', quantidade: '', dataDescarte: '', motivo: 'Vencimento', outroMotivo: '' });

    const [stockMovementModal, setStockMovementModal] = useState({ isOpen: false, type: 'entrada', item: null });
    const [stockMovementQuantity, setStockMovementQuantity] = useState('');
    
    const [isAddingFornecedorCategoria, setIsAddingFornecedorCategoria] = useState(false);
    const [newFornecedorCategoria, setNewFornecedorCategoria] = useState('');
    const [isSavingFornecedorCategoria, setIsSavingFornecedorCategoria] = useState(false);
    const [previousFornecedorCategoria, setPreviousFornecedorCategoria] = useState('');

    const resetFornecedorForm = () => {
        setFornecedorFormData({ nome: '', cnpj_cpf: '', contato_telefone: '', contato_email: '', contato_whatsapp: '', endereco_completo: '', endereco_cep: '', categoria: DEFAULT_FORNECEDOR_CATEGORIES[0], dados_bancarios: '', observacoes: '', status: 'Ativo' });
        setIsAddingFornecedorCategoria(false);
        setNewFornecedorCategoria('');
        setIsSavingFornecedorCategoria(false);
        setPreviousFornecedorCategoria('');
    };
    const resetPedidoForm = () => setPedidoFormData({ fornecedorId: '', itens: [], valorTotal: 0, dataPedido: new Date().toISOString().split('T')[0], dataPrevistaEntrega: '', status: 'Pendente' });
    const resetEstoqueForm = () => setEstoqueFormData({ nome: '', categoria: DEFAULT_FORNECEDOR_CATEGORIES[0], fornecedorId: '', quantidade: '', unidade: 'un', custoUnitario: '', nivelMinimo: '' });
    const resetPerdaForm = () => setPerdaFormData({ produtoId: '', produtoNome: '', custoUnitario: '', quantidade: '', dataDescarte: new Date().toISOString().split('T')[0], motivo: 'Vencimento', outroMotivo: '' });

    const openStockMovementModal = (item, type) => {
        setStockMovementModal({ isOpen: true, type, item });
        setStockMovementQuantity('');
    };

    const closeStockMovementModal = () => {
        setStockMovementModal({ isOpen: false, type: 'entrada', item: null });
        setStockMovementQuantity('');
    };

    const handleStockMovementSubmit = async (event) => {
        event.preventDefault();
        const quantity = parseFloat(stockMovementQuantity);

        if (!quantity || quantity <= 0) {
            alert('Informe uma quantidade válida para a movimentação.');
            return;
        }

        try {
            await updateStock(
                stockMovementModal.item.id,
                stockMovementModal.type,
                quantity,
                `Movimentação rápida - ${stockMovementModal.type}`,
                currentUser
            );
            closeStockMovementModal();
        } catch (error) {
            console.error('Erro ao movimentar estoque', error);
            alert(error.message || 'Erro ao atualizar estoque.');
        }
    };

    const fornecedorCategories = useMemo(() => {
        const customCategories = (data.categoriasFornecedores || [])
            .map(item => {
                if (!item) return null;
                if (typeof item === 'string') return item;
                return item.nome;
            })
            .filter(Boolean);

        const combined = [...DEFAULT_FORNECEDOR_CATEGORIES, ...customCategories];
        if (fornecedorFormData.categoria && fornecedorFormData.categoria.trim()) {
            combined.push(fornecedorFormData.categoria.trim());
        }
        if (estoqueFormData.categoria && estoqueFormData.categoria.trim()) {
            combined.push(estoqueFormData.categoria.trim());
        }

        const seen = new Set();
        const unique = [];
        combined.forEach(cat => {
            const normalized = typeof cat === 'string' ? cat.trim() : '';
            if (!normalized) return;
            const key = normalized.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                unique.push(normalized);
            }
        });

        return unique;
    }, [data.categoriasFornecedores, fornecedorFormData.categoria, estoqueFormData.categoria]);
    
	useEffect(() => {
		const total = (pedidoFormData.itens || []).reduce((sum, item) => 
			sum + ((item.quantidade || 0) * (item.custoUnitario || 0)), 0
		);
		
		// Só atualiza se mudou para evitar loop
		if (total !== pedidoFormData.valorTotal) {
			setPedidoFormData(prev => ({ ...prev, valorTotal: total }));
        }
    }, [pedidoFormData.itens, pedidoFormData.valorTotal]);

    const handlePerdaProdutoChange = async (e) => {
        const produtoId = e.target.value;
        setPerdaFormData(prev => ({ ...prev, produtoId, produtoNome: '', custoUnitario: '' }));

        if (!produtoId || !effectiveStoreId) return;

        try {
            const produtoDoc = await getDoc(doc(db, 'lojas', effectiveStoreId, 'produtos', produtoId));
            if (!produtoDoc.exists()) return;

            const produtoData = produtoDoc.data() || {};
            const custo = produtoData.custo ?? produtoData.custoUnitario ?? '';
            setPerdaFormData(prev => ({
                ...prev,
                produtoId,
                produtoNome: produtoData.nome || '',
                custoUnitario: custo
            }));
        } catch (error) {
            console.error('Erro ao buscar produto para perda', error);
        }
    };

    // Memoized Filters
    const filteredFornecedores = useMemo(() => (data.fornecedores || []).filter(f => (f.nome && f.nome.toLowerCase().includes(searchTerm.toLowerCase())) || (f.categoria && f.categoria.toLowerCase().includes(searchTerm.toLowerCase()))), [data.fornecedores, searchTerm]);
    const pedidosComNomes = useMemo(() => (data.pedidosCompra || []).map(pedido => ({ ...pedido, fornecedorNome: data.fornecedores.find(f => f.id === pedido.fornecedorId)?.nome || 'N/A' })), [data.pedidosCompra, data.fornecedores]);
    const estoqueComNomes = useMemo(() => (data.estoque || []).map(item => ({ ...item, fornecedorNome: data.fornecedores.find(f => f.id === item.fornecedorId)?.nome || 'N/A' })), [data.estoque, data.fornecedores]);
    const perdasOrdenadas = useMemo(() => {
        const perdas = data.perdasDescarte || [];
        const mapped = perdas.map(perda => {
            const produto = (data.produtos || []).find(p => p.id === perda.produtoId);
            const custo = perda.custoUnitario ?? produto?.custo ?? produto?.custoUnitario ?? 0;
            return {
                ...perda,
                produtoNome: produto?.nome || perda.produtoNome || 'Produto',
                custoUnitario: custo,
                valorTotal: perda.valorTotal ?? ((perda.quantidade || 0) * custo)
            };
        });

        return mapped.sort((a, b) => {
            const dateA = getJSDate(a.dataDescarte) || new Date(0);
            const dateB = getJSDate(b.dataDescarte) || new Date(0);
            return dateB - dateA;
        });
    }, [data.perdasDescarte, data.produtos]);

    const perdaValorTotal = useMemo(() => {
        const quantidade = parseFloat(perdaFormData.quantidade || 0) || 0;
        const custo = parseFloat(perdaFormData.custoUnitario || 0) || 0;
        return quantidade * custo;
    }, [perdaFormData.quantidade, perdaFormData.custoUnitario]);


    // Handlers Fornecedores
    const handleNewFornecedor = () => { setEditingFornecedor(null); resetFornecedorForm(); setShowFornecedorModal(true); };
    const handleEditFornecedor = (fornecedor) => { setEditingFornecedor(fornecedor); setFornecedorFormData(fornecedor); setIsAddingFornecedorCategoria(false); setNewFornecedorCategoria(''); setIsSavingFornecedorCategoria(false); setPreviousFornecedorCategoria(''); setShowFornecedorModal(true); };
    const handleFornecedorSubmit = async (e) => { e.preventDefault(); if (editingFornecedor) { await updateItem('fornecedores', editingFornecedor.id, fornecedorFormData); } else { await addItem('fornecedores', fornecedorFormData); } setShowFornecedorModal(false); };

    const handleFornecedorCategoriaChange = (e) => {
        const value = e.target.value;
        if (value === '__add_new__') {
            setIsAddingFornecedorCategoria(true);
            setNewFornecedorCategoria('');
            setPreviousFornecedorCategoria(fornecedorFormData.categoria || DEFAULT_FORNECEDOR_CATEGORIES[0]);
            setFornecedorFormData(prev => ({ ...prev, categoria: '' }));
            return;
        }
        setIsAddingFornecedorCategoria(false);
        setNewFornecedorCategoria('');
        setPreviousFornecedorCategoria('');
        setFornecedorFormData(prev => ({ ...prev, categoria: value || DEFAULT_FORNECEDOR_CATEGORIES[0] }));
    };

    const handleCancelFornecedorCategoria = () => {
        setIsAddingFornecedorCategoria(false);
        setNewFornecedorCategoria('');
        setIsSavingFornecedorCategoria(false);
        setFornecedorFormData(prev => ({ ...prev, categoria: previousFornecedorCategoria || DEFAULT_FORNECEDOR_CATEGORIES[0] }));
        setPreviousFornecedorCategoria('');
    };

    const handleCreateFornecedorCategoria = async () => {
        const trimmed = newFornecedorCategoria.trim();
        if (!trimmed) {
            alert('Informe o nome da nova categoria.');
            return;
        }

        const existing = fornecedorCategories.find(cat => cat.toLowerCase() === trimmed.toLowerCase());
        if (existing) {
            alert('Esta categoria já existe.');
            setFornecedorFormData(prev => ({ ...prev, categoria: existing }));
            setIsAddingFornecedorCategoria(false);
            setNewFornecedorCategoria('');
            return;
        }

        try {
            setIsSavingFornecedorCategoria(true);
            await addItem('categoriasFornecedores', { nome: trimmed });
            setFornecedorFormData(prev => ({ ...prev, categoria: trimmed }));
            setIsAddingFornecedorCategoria(false);
            setNewFornecedorCategoria('');
            setPreviousFornecedorCategoria('');
        } catch (error) {
            console.error('Erro ao criar categoria de fornecedor:', error);
            alert('Não foi possível salvar a nova categoria. Tente novamente.');
        } finally {
            setIsSavingFornecedorCategoria(false);
        }
    };

    // Handlers Pedidos de Compra
    const handleNewPedido = () => { setEditingPedido(null); resetPedidoForm(); setShowPedidoModal(true); };
    const handleEditPedido = (pedido) => { setEditingPedido(pedido); setPedidoFormData({ ...pedido, dataPedido: pedido.dataPedido?.split('T')[0] || '', dataPrevistaEntrega: pedido.dataPrevistaEntrega?.split('T')[0] || '' }); setShowPedidoModal(true); };
    const handlePedidoSubmit = async (e) => { e.preventDefault(); if (editingPedido) { await updateItem('pedidosCompra', editingPedido.id, pedidoFormData); } else { await addItem('pedidosCompra', pedidoFormData); } setShowPedidoModal(false); };
    const handleUpdatePedidoStatus = async (pedido, status) => { await updateItem('pedidosCompra', pedido.id, { ...pedido, status }); if (status === 'Recebido') { const conta = { descricao: `Compra de ${pedido.fornecedorNome}`, valor: pedido.valorTotal, dataVencimento: new Date().toISOString().split('T')[0], status: 'Pendente', categoria: 'Fornecedores', pedidoCompraId: pedido.id }; await addItem('contas_a_pagar', conta); alert('Conta a pagar gerada no financeiro!'); } };
    const handleAddItemToPedido = (item) => { setPedidoFormData(prev => ({...prev, itens: [...(prev.itens || []), {...item, quantidade: 1, custoUnitario: item.custoUnitario || 0}]}))};
    const handleUpdateItemInPedido = (index, field, value) => { const newItens = [...pedidoFormData.itens]; newItens[index][field] = value; setPedidoFormData(prev => ({...prev, itens: newItens})) };
    const handleRemoveItemFromPedido = (index) => { const newItens = pedidoFormData.itens.filter((_, i) => i !== index); setPedidoFormData(prev => ({...prev, itens: newItens}));};

    // Handlers Estoque
    const handleNewEstoque = () => { setEditingEstoque(null); resetEstoqueForm(); setShowEstoqueModal(true); };
    const handleEditEstoque = (item) => { setEditingEstoque(item); setEstoqueFormData(item); setShowEstoqueModal(true); };
    const handleEstoqueSubmit = async (e) => { e.preventDefault(); const dataToSave = { ...estoqueFormData, quantidade: parseFloat(estoqueFormData.quantidade || 0), custoUnitario: parseFloat(estoqueFormData.custoUnitario || 0), nivelMinimo: parseFloat(estoqueFormData.nivelMinimo || 0) }; if (editingEstoque) { await updateItem('estoque', editingEstoque.id, dataToSave); } else { await addItem('estoque', dataToSave); } setShowEstoqueModal(false); };

    const handleNewPerda = () => { setEditingPerda(null); resetPerdaForm(); setShowPerdaModal(true); };
    const handleEditPerda = (perda) => { setEditingPerda(perda); setPerdaFormData({ produtoId: perda.produtoId || '', produtoNome: perda.produtoNome || '', custoUnitario: perda.custoUnitario ?? '', quantidade: perda.quantidade ?? '', dataDescarte: perda.dataDescarte?.split('T')[0] || perda.dataDescarte || '', motivo: perda.motivo || 'Vencimento', outroMotivo: perda.outroMotivo || '' }); setShowPerdaModal(true); };
    const handlePerdaSubmit = async (e) => {
        e.preventDefault();
        if (!perdaFormData.produtoId) { alert('Selecione um produto.'); return; }
        const quantidade = parseFloat(perdaFormData.quantidade || 0);
        if (!quantidade || quantidade <= 0) { alert('A quantidade deve ser maior que zero.'); return; }
        const custoUnitario = parseFloat(perdaFormData.custoUnitario || 0);
        const valorTotal = quantidade * custoUnitario;
        const dataToSave = {
            ...perdaFormData,
            quantidade,
            custoUnitario,
            valorTotal,
            motivo: perdaFormData.motivo === 'Outro' ? (perdaFormData.outroMotivo || 'Outro') : perdaFormData.motivo
        };
        if (editingPerda) { await updateItem('perdasDescarte', editingPerda.id, dataToSave); } else { await addItem('perdasDescarte', dataToSave); }
        setShowPerdaModal(false);
    };
    const handleDeletePerda = (perda) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('perdasDescarte', perda.id) });

    // UI Rendering
    return (
        <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
            <div><h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Gestão de Fornecedores/Estoque</h1><p className="text-gray-600 mt-1">Organize seus parceiros, compras e insumos</p></div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2"><div className="flex space-x-2">
                {['fornecedores', 'pedidos', 'estoque', 'perdas'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === tab ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        {tab === 'fornecedores' && 'Fornecedores'}{tab === 'pedidos' && 'Pedidos de Compra'}{tab === 'estoque' && 'Estoque'}{tab === 'perdas' && 'Perdas/Descarte'}
                    </button>
                ))}
            </div></div>
            
            {activeTab === 'fornecedores' && (
                <div>
                    <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
                        <div className="relative max-w-md w-full"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" /><input type="text" placeholder="Buscar por nome ou categoria..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500" /></div>
                        <Button onClick={handleNewFornecedor} className="w-full md:w-auto"><Plus className="w-4 h-4" /> Novo Fornecedor</Button>
                    </div>
                    <Table columns={[{ header: 'Fornecedor', key: 'nome' }, { header: 'Telefone', key: 'contato_telefone' }, { header: 'Categoria', key: 'categoria' }, { header: "Status", render: (row) => (<span className={`px-3 py-1 rounded-full text-xs font-medium ${row.status === 'Ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{row.status}</span>) }]} data={filteredFornecedores} actions={[{ icon: Edit, label: "Editar", onClick: handleEditFornecedor }, { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('fornecedores', row.id) }) }]} />
                </div>
            )}
            {activeTab === 'pedidos' && (
                 <div>
                    <div className="flex justify-end mb-6"><Button onClick={handleNewPedido}><Plus className="w-4 h-4" /> Novo Pedido de Compra</Button></div>
                    <Table columns={[{ header: 'Fornecedor', key: 'fornecedorNome' }, { header: 'Data do Pedido', render: (row) => getJSDate(row.dataPedido)?.toLocaleDateString('pt-BR') || '-' }, { header: 'Previsão de Entrega', render: (row) => getJSDate(row.dataPrevistaEntrega)?.toLocaleDateString('pt-BR') || '-' }, { header: 'Valor Total', render: (row) => `R$ ${(row.valorTotal || 0).toFixed(2)}`}, { header: 'Status', render: (row) => <span className={`px-3 py-1 rounded-full text-xs font-medium ${row.status === 'Recebido' ? 'bg-green-100 text-green-800' : row.status === 'Pendente' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{row.status}</span> }]} data={pedidosComNomes} actions={[{ icon: Edit, label: "Editar", onClick: handleEditPedido }, { icon: Truck, label: "Receber", onClick: (row) => handleUpdatePedidoStatus(row, 'Recebido') }, { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('pedidosCompra', row.id) }) }]} />
                </div>
            )}
             {activeTab === 'estoque' && (
                 <div>
                    <div className="flex justify-end mb-6"><Button onClick={handleNewEstoque}><PackagePlus className="w-4 h-4" /> Novo Item de Estoque</Button></div>
                    <Table
                        columns={[
                            { header: 'Item', key: 'nome' },
                            { header: 'Fornecedor', key: 'fornecedorNome' },
                            { header: 'Estoque Atual', render: (row) => `${row.quantidade || 0} ${row.unidade}` },
                            {
                                header: 'Movimentação Rápida',
                                render: (row) => (
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="!px-3 !py-2 text-xs"
                                            onClick={() => openStockMovementModal(row, 'entrada')}
                                        >
                                            + Entrada
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="!px-3 !py-2 text-xs text-red-600 border-red-200 hover:border-red-300 hover:text-red-700"
                                            onClick={() => openStockMovementModal(row, 'saida')}
                                        >
                                            - Saída
                                        </Button>
                                    </div>
                                )
                            },
                            { header: 'Custo Unitário', render: (row) => `R$ ${(row.custoUnitario || 0).toFixed(2)}` }
                        ]}
                        data={estoqueComNomes}
                        actions={[ { icon: Edit, label: "Editar", onClick: handleEditEstoque }, { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('estoque', row.id) }) } ]}
                    />
                </div>
            )}

            {activeTab === 'perdas' && (
                <div>
                    <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">Perdas e Descarte</h2>
                            <p className="text-sm text-gray-600">Registre perdas de estoque e motivos de descarte</p>
                        </div>
                        <Button onClick={handleNewPerda} className="w-full md:w-auto"><PackagePlus className="w-4 h-4" /> ➕ Nova Perda/Descarte</Button>
                    </div>
                    <Table
                        columns={[
                            { header: 'Produto', key: 'produtoNome' },
                            { header: 'Valor de Custo (R$)', render: (row) => `R$ ${(row.custoUnitario || 0).toFixed(2)}` },
                            { header: 'Quantidade', key: 'quantidade' },
                            { header: 'Valor Total da Perda', render: (row) => `R$ ${((row.valorTotal ?? ((row.quantidade || 0) * (row.custoUnitario || 0))).toFixed(2))}` },
                            { header: 'Data do Descarte', render: (row) => getJSDate(row.dataDescarte)?.toLocaleDateString('pt-BR') || '-' },
                            { header: 'Motivo da Perda', render: (row) => row.motivo || '-' }
                        ]}
                        data={perdasOrdenadas}
                        actions={[
                            { icon: Edit, label: "Editar", onClick: handleEditPerda },
                            { icon: Trash2, label: "Excluir", onClick: handleDeletePerda }
                        ]}
                    />
                </div>
            )}

            <Modal isOpen={showFornecedorModal} onClose={() => setShowFornecedorModal(false)} title={editingFornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'} size="lg">
                <form onSubmit={handleFornecedorSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Nome/Razão Social" value={fornecedorFormData.nome || ''} onChange={e => setFornecedorFormData({...fornecedorFormData, nome: e.target.value})} required/>
                        <Input label="CNPJ/CPF" value={fornecedorFormData.cnpj_cpf || ''} onChange={e => setFornecedorFormData({...fornecedorFormData, cnpj_cpf: e.target.value})}/>
                        <Input label="Telefone" value={fornecedorFormData.contato_telefone || ''} onChange={e => setFornecedorFormData({...fornecedorFormData, contato_telefone: e.target.value})}/>
                        <Input label="Email" type="email" value={fornecedorFormData.contato_email || ''} onChange={e => setFornecedorFormData({...fornecedorFormData, contato_email: e.target.value})}/>
                        <Input label="Endereço Completo" value={fornecedorFormData.endereco_completo || ''} onChange={e => setFornecedorFormData({...fornecedorFormData, endereco_completo: e.target.value})}/>
                        <Select label="Categoria" value={fornecedorFormData.categoria || ''} onChange={handleFornecedorCategoriaChange}>
                            <option value="">Selecione...</option>
                            {fornecedorCategories.map(categoria => (
                                <option key={categoria} value={categoria}>{categoria}</option>
                            ))}
                            <option value="__add_new__">+ Adicionar nova categoria</option>
                        </Select>
                        {isAddingFornecedorCategoria && (
                            <div className="md:col-span-2 bg-pink-50 border border-pink-100 rounded-2xl p-4 space-y-3">
                                <div className="flex flex-col md:flex-row gap-3">
                                    <div className="flex-1">
                                        <Input label="Nova Categoria" placeholder="Digite o nome da categoria" value={newFornecedorCategoria} onChange={(e) => setNewFornecedorCategoria(e.target.value)} />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button type="button" variant="secondary" onClick={handleCancelFornecedorCategoria} className="whitespace-nowrap">Cancelar</Button>
                                        <Button type="button" onClick={handleCreateFornecedorCategoria} disabled={isSavingFornecedorCategoria} className="whitespace-nowrap">
                                            {isSavingFornecedorCategoria ? 'Salvando...' : 'Salvar Categoria'}
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-sm text-pink-700">A nova categoria ficará disponível automaticamente para todos os cadastros desta loja.</p>
                            </div>
                        )}
                    </div>
                    <Textarea label="Dados Bancários" rows="2" value={fornecedorFormData.dados_bancarios || ''} onChange={e => setFornecedorFormData({...fornecedorFormData, dados_bancarios: e.target.value})}/>
                    <Textarea label="Observações" rows="2" value={fornecedorFormData.observacoes || ''} onChange={e => setFornecedorFormData({...fornecedorFormData, observacoes: e.target.value})}/>
                    <div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => setShowFornecedorModal(false)}>Cancelar</Button><Button type="submit"><Save className="w-4 h-4"/> Salvar</Button></div>
                </form>
            </Modal>
            <Modal isOpen={showPedidoModal} onClose={() => setShowPedidoModal(false)} title={editingPedido ? 'Editar Pedido de Compra' : 'Novo Pedido de Compra'} size="xl">
                <form onSubmit={handlePedidoSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select label="Fornecedor" value={pedidoFormData.fornecedorId || ''} onChange={e => setPedidoFormData({...pedidoFormData, fornecedorId: e.target.value, fornecedorNome: e.target.selectedOptions[0].text })} required><option value="">Selecione...</option>{data.fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}</Select>
                        <Input label="Data do Pedido" type="date" value={pedidoFormData.dataPedido || ''} onChange={e => setPedidoFormData({...pedidoFormData, dataPedido: e.target.value})} required/>
                        <Input label="Previsão de Entrega" type="date" value={pedidoFormData.dataPrevistaEntrega || ''} onChange={e => setPedidoFormData({...pedidoFormData, dataPrevistaEntrega: e.target.value})} />
                    </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-2">
                            <h3 className="font-semibold">Adicionar Itens do Estoque</h3>
                            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                                {data.estoque.map(item => (<div key={item.id} className="flex justify-between items-center p-2 rounded hover:bg-gray-50"><span>{item.nome}</span><Button size="sm" variant="secondary" onClick={() => handleAddItemToPedido(item)}>+</Button></div>))}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h3 className="font-semibold">Itens no Pedido</h3>
                            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                                {(pedidoFormData.itens || []).length === 0 ? <p className="text-sm text-gray-500 text-center p-4">Nenhum item</p> : 
                                (pedidoFormData.itens || []).map((item, index) => (
                                    <div key={index} className="grid grid-cols-4 gap-2 items-center p-1">
                                        <span className="col-span-2 text-sm">{item.nome}</span>
                                        <Input type="number" placeholder="Qtd" value={item.quantidade} onChange={e => handleUpdateItemInPedido(index, 'quantidade', parseFloat(e.target.value || 0))} className="py-1"/>
                                        <div className="flex items-center gap-1">
                                        <Input type="number" step="0.01" placeholder="Custo" value={item.custoUnitario} onChange={e => handleUpdateItemInPedido(index, 'custoUnitario', parseFloat(e.target.value || 0))} className="py-1"/>
                                        <button type="button" onClick={() => handleRemoveItemFromPedido(index)} className="text-red-500"><Trash2 size={14}/></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-right font-bold text-lg mt-2">Total: R$ {(pedidoFormData.valorTotal || 0).toFixed(2)}</div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => setShowPedidoModal(false)}>Cancelar</Button><Button type="submit"><Save className="w-4 h-4"/> Salvar Pedido</Button></div>
                </form>
            </Modal>
            <Modal isOpen={showEstoqueModal} onClose={() => setShowEstoqueModal(false)} title={editingEstoque ? 'Editar Item de Estoque' : 'Novo Item de Estoque'} size="lg">
                <form onSubmit={handleEstoqueSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Nome do Item" value={estoqueFormData.nome || ''} onChange={e => setEstoqueFormData({...estoqueFormData, nome: e.target.value})} required/>
                        <Select label="Categoria" value={estoqueFormData.categoria || ''} onChange={e => setEstoqueFormData({...estoqueFormData, categoria: e.target.value})}>
                            {fornecedorCategories.map(categoria => (
                                <option key={categoria} value={categoria}>{categoria}</option>
                            ))}
                        </Select>
                        <Select label="Fornecedor Principal" value={estoqueFormData.fornecedorId || ''} onChange={e => setEstoqueFormData({...estoqueFormData, fornecedorId: e.target.value})}><option value="">Nenhum</option>{data.fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}</Select>
                        <Input label="Custo por Unidade (R$)" type="number" step="0.01" value={estoqueFormData.custoUnitario || ''} onChange={e => setEstoqueFormData({...estoqueFormData, custoUnitario: e.target.value})} />
                        <Input label="Quantidade Atual" type="number" value={estoqueFormData.quantidade || ''} onChange={e => setEstoqueFormData({...estoqueFormData, quantidade: e.target.value})} required/>
                        <Select label="Unidade de Medida" value={estoqueFormData.unidade || ''} onChange={e => setEstoqueFormData({...estoqueFormData, unidade: e.target.value})}><option>un</option><option>kg</option><option>g</option><option>L</option><option>ml</option></Select>
                        <Input label="Nível Mínimo de Estoque" type="number" value={estoqueFormData.nivelMinimo || ''} onChange={e => setEstoqueFormData({...estoqueFormData, nivelMinimo: e.target.value})} />
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => setShowEstoqueModal(false)}>Cancelar</Button><Button type="submit"><Save className="w-4 h-4"/> Salvar Item</Button></div>
                </form>
            </Modal>
            <Modal
                isOpen={stockMovementModal.isOpen}
                onClose={closeStockMovementModal}
                title={`Registrar ${stockMovementModal.type === 'entrada' ? 'Entrada' : 'Saída'} de Estoque`}
                size="sm"
            >
                <form onSubmit={handleStockMovementSubmit} className="space-y-4">
                    <div className="text-sm text-gray-700">
                        <p className="font-semibold">Item</p>
                        <p>{stockMovementModal.item?.nome || '-'}</p>
                    </div>
                    <Input
                        label="Quantidade"
                        type="number"
                        min="0"
                        step="0.01"
                        value={stockMovementQuantity}
                        onChange={(e) => setStockMovementQuantity(e.target.value)}
                        required
                    />
                    <div className="flex justify-end gap-3 pt-2">
                        <Button variant="secondary" type="button" onClick={closeStockMovementModal}>Cancelar</Button>
                        <Button type="submit">Confirmar</Button>
                    </div>
                </form>
            </Modal>
            <Modal isOpen={showPerdaModal} onClose={() => setShowPerdaModal(false)} title={editingPerda ? 'Editar Perda/Descarte' : 'Nova Perda/Descarte'} size="lg">
                <form onSubmit={handlePerdaSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select label="Produto" value={perdaFormData.produtoId} onChange={handlePerdaProdutoChange} required>
                            <option value="">Selecione um produto</option>
                            {(data.produtos || []).map(produto => (
                                <option key={produto.id} value={produto.id}>{produto.nome}</option>
                            ))}
                        </Select>
                        <Input label="Valor de Custo (R$)" type="number" step="0.01" value={perdaFormData.custoUnitario || ''} onChange={e => setPerdaFormData({...perdaFormData, custoUnitario: e.target.value})} />
                        <Input label="Quantidade" type="number" min="0" value={perdaFormData.quantidade || ''} onChange={e => setPerdaFormData({...perdaFormData, quantidade: e.target.value})} required/>
                        <Input label="Data do Descarte" type="date" value={perdaFormData.dataDescarte || ''} onChange={e => setPerdaFormData({...perdaFormData, dataDescarte: e.target.value})} required/>
                        <Select label="Motivo da Perda" value={perdaFormData.motivo} onChange={e => setPerdaFormData({...perdaFormData, motivo: e.target.value})}>
                            <option value="Vencimento">Vencimento</option>
                            <option value="Dano no transporte">Dano no transporte</option>
                            <option value="Erro de produção">Erro de produção</option>
                            <option value="Produto danificado">Produto danificado</option>
                            <option value="Outro">Outro</option>
                        </Select>
                        {perdaFormData.motivo === 'Outro' && (
                            <Input label="Detalhe o motivo" value={perdaFormData.outroMotivo || ''} onChange={e => setPerdaFormData({...perdaFormData, outroMotivo: e.target.value})} />
                        )}
                    </div>
                    <div className="p-4 bg-pink-50 border border-pink-100 rounded-xl flex items-center justify-between text-sm text-pink-900">
                        <span>Valor total da perda</span>
                        <span className="text-lg font-bold text-rose-600">R$ {perdaValorTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => setShowPerdaModal(false)}>Cancelar</Button><Button type="submit"><Save className="w-4 h-4"/> Salvar Perda</Button></div>
                </form>
            </Modal>
        </div>
    );
};


const Financeiro = ({ data, addItem, updateItem, deleteItem, setConfirmDelete }) => {
    const [activeTab, setActiveTab] = usePersistentState('financeiro_activeTab', 'dashboard');
    const [modalConfig, setModalConfig] = useState({ isOpen: false, type: null, item: null });
    const [formData, setFormData] = useState({});
    const chartsRef = useRef({});
    const [startDate, setStartDate] = usePersistentState('financeiro_startDate', '');
    const [endDate, setEndDate] = usePersistentState('financeiro_endDate', '');
    const [despesaFilter, setDespesaFilter] = usePersistentState('financeiro_despesaFilter', 'Todas');

    const monthlyChartRef = useRef(null);
    const categoryChartRef = useRef(null);
    
	useEffect(() => {
		if (activeTab !== 'dashboard' || !monthlyChartRef.current || !categoryChartRef.current || typeof window.Chart === 'undefined') {
			return;
		}

		// Destrói gráficos existentes
        const existingCharts = Object.values(chartsRef.current);
		existingCharts.forEach(chart => {
			if (chart && typeof chart.destroy === 'function') {
				chart.destroy();
			}
		});

		const monthlyCtx = monthlyChartRef.current.getContext('2d');
		const monthlyData = {
			labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
			datasets: [
				{ label: 'Receitas', data: Array(12).fill(0), backgroundColor: 'rgba(34, 197, 94, 0.6)' },
				{ label: 'Despesas', data: Array(12).fill(0), backgroundColor: 'rgba(239, 68, 68, 0.6)' }
			]
		};
		
		const allReceitas = [
			...(data.pedidos || []).filter(p => p.status === 'Finalizado'),
			...(data.contas_a_receber || []).filter(r => r.status === 'Recebido')
		];

		allReceitas.forEach(item => {
			const date = getJSDate(item.createdAt || item.dataRecebimento);
			if (date) monthlyData.datasets[0].data[date.getMonth()] += (item.total || item.valor || 0);
		});

		(data.contas_a_pagar || []).forEach(item => {
			if (item.status === 'Pago') {
				const date = getJSDate(item.dataVencimento);
				if (date) monthlyData.datasets[1].data[date.getMonth()] += item.valor;
			}
		});
		
		const monthlyChart = new window.Chart(monthlyCtx, { 
			type: 'bar', 
			data: monthlyData, 
			options: { 
				responsive: true, 
				plugins: { 
					title: { display: true, text: 'Fluxo de Caixa Mensal' } 
				} 
			} 
		});

		const categoryCtx = categoryChartRef.current.getContext('2d');
		const categoryData = (data.contas_a_pagar || [])
			.filter(i => i.status === 'Pago')
			.reduce((acc, item) => {
				acc[item.categoria] = (acc[item.categoria] || 0) + item.valor;
				return acc;
			}, {});
			
		const pieChart = new window.Chart(categoryCtx, { 
			type: 'pie', 
			data: { 
				labels: Object.keys(categoryData), 
				datasets: [{ 
					data: Object.values(categoryData), 
					backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'] 
				}] 
			}, 
			options: { 
				responsive: true, 
				plugins: { 
					title: { display: true, text: 'Despesas por Categoria' } 
				} 
			} 
		});

        chartsRef.current = { monthlyChart, pieChart };

		return () => {
			if (monthlyChart) monthlyChart.destroy();
			if (pieChart) pieChart.destroy();
		};
	}, [activeTab, data.pedidos, data.contas_a_receber, data.contas_a_pagar]);

    const financialSummary = useMemo(() => {
        const receitas = (data.contas_a_receber || []).filter(r => r.status === 'Recebido');
        const despesas = (data.contas_a_pagar || []).filter(p => p.status === 'Pago');

        const totalReceitas = receitas.reduce((sum, item) => sum + (item.valor || 0), 0);
        const totalDespesas = despesas.reduce((sum, item) => sum + (item.valor || 0), 0);
        const lucroLiquido = totalReceitas - totalDespesas;
        
        const aReceber = (data.contas_a_receber || []).filter(r => r.status === 'Pendente').reduce((sum, item) => sum + (item.valor || 0), 0);
        const aPagar = (data.contas_a_pagar || []).filter(p => p.status === 'Pendente').reduce((sum, item) => sum + (item.valor || 0), 0);

        return { totalReceitas, totalDespesas, lucroLiquido, aReceber, aPagar };
    }, [data.contas_a_receber, data.contas_a_pagar]);

    const handleNew = (type) => {
        const baseData = type === 'pagar' ? 
            { descricao: '', valor: '', dataVencimento: '', status: 'Pendente', categoria: 'Fornecedores' } :
            { descricao: '', valor: '', dataRecebimento: '', status: 'Pendente', metodo: 'Pix' };

        if (type === 'pagar' && despesaFilter !== 'Todas') {
            baseData.categoria = despesaFilter;
        }

        setFormData(baseData);
        setModalConfig({ isOpen: true, type, item: null });
    };

    const handleEdit = (type, item) => {
        const itemData = { ...item, valor: String(item.valor) };
        if(type === 'pagar' && item.dataVencimento) itemData.dataVencimento = getJSDate(item.dataVencimento)?.toISOString().split('T')[0];
        if(type === 'receber' && item.dataRecebimento) itemData.dataRecebimento = getJSDate(item.dataRecebimento)?.toISOString().split('T')[0];
        setFormData(itemData);
        setModalConfig({ isOpen: true, type, item });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const collection = modalConfig.type === 'pagar' ? 'contas_a_pagar' : 'contas_a_receber';
        const dataToSave = { ...formData, valor: parseFloat(formData.valor || 0) };
        
        if (modalConfig.item) {
            await updateItem(collection, modalConfig.item.id, dataToSave);
        } else {
            await addItem(collection, dataToSave);
        }
        setModalConfig({ isOpen: false, type: null, item: null });
    };

    const handleStatusChange = async (type, item, newStatus) => {
        const collection = type === 'pagar' ? 'contas_a_pagar' : 'contas_a_receber';
        await updateItem(collection, item.id, { status: newStatus });
    };

    const renderDashboard = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg"><ArrowUpCircle className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Receita Total (Pago)</p><h2 className="text-2xl font-bold text-gray-800">R$ {financialSummary.totalReceitas.toFixed(2)}</h2></div></div></div>
                <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg"><ArrowDownCircle className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Despesa Total (Pago)</p><h2 className="text-2xl font-bold text-gray-800">R$ {financialSummary.totalDespesas.toFixed(2)}</h2></div></div></div>
                <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg"><DollarSign className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Lucro Líquido</p><h2 className="text-2xl font-bold text-gray-800">R$ {financialSummary.lucroLiquido.toFixed(2)}</h2></div></div></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 bg-white p-6 rounded-2xl shadow-lg">
                    <canvas ref={monthlyChartRef}></canvas>
                </div>
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-lg">
                     <canvas ref={categoryChartRef}></canvas>
                </div>
            </div>
        </div>
    );
    
    const getStatusClass = (status) => {
        switch (status) {
            case 'Pendente': return 'bg-yellow-100 text-yellow-800';
            case 'Pago':
            case 'Recebido': return 'bg-green-100 text-green-800';
            case 'Atrasado': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const renderContas = (type) => {
        const collection = type === 'pagar' ? 'contas_a_pagar' : 'contas_a_receber';
        let title = type === 'pagar' ? 'Despesas' : 'Contas a Receber';
        let items = data[collection] || [];

        if (type === 'pagar' && despesaFilter !== 'Todas') {
            items = items.filter(item => item.categoria === despesaFilter);
        }

        const columns = [
            { header: 'Descrição', key: 'descricao' },
            { header: 'Valor', render: (row) => <span className="font-semibold text-gray-800">R$ {(row.valor || 0).toFixed(2)}</span> },
            { header: type === 'pagar' ? 'Vencimento' : 'Data', render: (row) => { const date = getJSDate(type === 'pagar' ? row.dataVencimento : row.dataRecebimento); return date ? date.toLocaleDateString('pt-BR') : '-'; } },
            { header: 'Categoria', key: 'categoria', visible: type === 'pagar' },
            { header: 'Método', key: 'metodo', visible: type === 'receber' },
            { header: 'Status', render: (row) => <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(row.status)}`}>{row.status}</span> }
        ].filter(c => c.visible !== false);
        
        const actions = [
            { icon: Edit, label: "Editar", onClick: (row) => handleEdit(type, row) },
            { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem(collection, row.id) }) }
        ];

        if (type === 'pagar') {
            actions.unshift({ icon: Banknote, label: "Marcar como Pago", onClick: (row) => handleStatusChange(type, row, 'Pago') });
        } else {
            actions.unshift({ icon: Banknote, label: "Marcar como Recebido", onClick: (row) => handleStatusChange(type, row, 'Recebido') });
        }

        return (
            <div>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-700">{title}</h2>
                    <Button onClick={() => handleNew(type)}><Plus className="w-4 h-4"/> Novo Lançamento</Button>
                </div>
                {type === 'pagar' && (
                    <div className="mb-4 flex space-x-2 border-b">
                        {['Todas', 'Despesa Fixa', 'Despesa Variável', 'Fornecedores'].map(filter => (
                            <button
                                key={filter}
                                onClick={() => setDespesaFilter(filter)}
                                className={`px-3 py-2 text-sm font-medium ${despesaFilter === filter ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                {filter.replace('Despesa ', '')}
                            </button>
                        ))}
                    </div>
                )}
                <Table columns={columns} data={items} actions={actions} />
            </div>
        );
    };
    
    const renderFluxoCaixa = () => {
        const filteredPedidos = (data.pedidos || [])
            .filter(p => p.status === 'Finalizado')
            .filter(p => {
                if(!p.createdAt) return false;
                const itemDate = getJSDate(p.createdAt);
                if (!itemDate) return false;
                const start = startDate ? new Date(startDate) : null;
                const end = endDate ? new Date(endDate) : null;
                if(start) start.setHours(0,0,0,0);
                if(end) end.setHours(23,59,59,999);
                if (start && itemDate < start) return false;
                if (end && itemDate > end) return false;
                return true;
            });

        const outrasEntradasFiltradas = (data.contas_a_receber || [])
            .filter(i => i.status === 'Recebido')
            .filter(item => {
                if(!item.dataRecebimento) return false;
                const itemDate = getJSDate(item.dataRecebimento);
                if (!itemDate) return false;
                const start = startDate ? new Date(startDate) : null;
                const end = endDate ? new Date(endDate) : null;
                if(start) start.setHours(0,0,0,0);
                if(end) end.setHours(23,59,59,999);
                if (start && itemDate < start) return false;
                if (end && itemDate > end) return false;
                return true;
            });
        
        const saidasFiltradas = (data.contas_a_pagar || [])
            .filter(i => i.status === 'Pago')
            .filter(item => {
                if(!item.dataVencimento) return false;
                const itemDate = getJSDate(item.dataVencimento);
                if (!itemDate) return false;
                const start = startDate ? new Date(startDate) : null;
                const end = endDate ? new Date(endDate) : null;
                if(start) start.setHours(0,0,0,0);
                if(end) end.setHours(23,59,59,999);
                if (start && itemDate < start) return false;
                if (end && itemDate > end) return false;
                return true;
            });
            
        // Breakdown by sales channel from Pedidos
        const totalVendasPresencial = filteredPedidos.filter(p => p.origem === 'Manual' && p.categoria !== 'Festa').reduce((sum, p) => sum + p.total, 0);
        const totalVendasOnline = filteredPedidos.filter(p => ['Cardapio Online', 'Plataforma'].includes(p.origem)).reduce((sum, p) => sum + p.total, 0);
        const totalVendasFesta = filteredPedidos.filter(p => p.categoria === 'Festa').reduce((sum, p) => sum + p.total, 0);
            
        // Breakdown by payment method from Pedidos
        const totaisPorPagamento = filteredPedidos.reduce((acc, pedido) => {
            const metodo = pedido.formaPagamento || 'Não informado';
            acc[metodo] = (acc[metodo] || 0) + pedido.total;
            return acc;
        }, {});
        
        const totalOutrasEntradas = outrasEntradasFiltradas.reduce((sum, t) => sum + t.valor, 0);
        const totalSaidas = saidasFiltradas.reduce((sum, t) => sum + t.valor, 0);
        
        const totalEntradas = totalVendasPresencial + totalVendasOnline + totalVendasFesta + totalOutrasEntradas;
        const saldo = totalEntradas - totalSaidas;
        
        return (
            <div>
                 <div className="p-4 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Data Inicial" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        <Input label="Data Final" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                 </div>
                 
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                     <div className="bg-green-100 p-4 rounded-xl"><p className="text-sm text-green-800">Total de Entradas</p><p className="text-xl font-bold text-green-900">R$ {totalEntradas.toFixed(2)}</p></div>
                     <div className="bg-red-100 p-4 rounded-xl"><p className="text-sm text-red-800">Total de Saídas</p><p className="text-xl font-bold text-red-900">R$ {totalSaidas.toFixed(2)}</p></div>
                     <div className="bg-blue-100 p-4 rounded-xl"><p className="text-sm text-blue-800">Saldo do Período</p><p className="text-xl font-bold text-blue-900">R$ {saldo.toFixed(2)}</p></div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <h3 className="font-bold text-lg mb-4">Entradas por Canal de Venda</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between border-b pb-1"><span className="text-gray-600">Vendas Presenciais:</span> <span className="font-semibold">R$ {totalVendasPresencial.toFixed(2)}</span></div>
                            <div className="flex justify-between border-b pb-1"><span className="text-gray-600">Delivery (Online):</span> <span className="font-semibold">R$ {totalVendasOnline.toFixed(2)}</span></div>
                            <div className="flex justify-between border-b pb-1"><span className="text-gray-600">Festas:</span> <span className="font-semibold">R$ {totalVendasFesta.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Outras Entradas:</span> <span className="font-semibold">R$ {totalOutrasEntradas.toFixed(2)}</span></div>
                        </div>
                    </div>
                     <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <h3 className="font-bold text-lg mb-4">Entradas por Forma de Pagamento</h3>
                        <div className="space-y-2 text-sm">
                            {Object.entries(totaisPorPagamento).map(([metodo, total]) => (
                                <div key={metodo} className="flex justify-between border-b pb-1"><span className="text-gray-600">{metodo}:</span> <span className="font-semibold">R$ {total.toFixed(2)}</span></div>
                            ))}
                        </div>
                    </div>
                 </div>
            </div>
        )
    };

    return (
        <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
            <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Financeiro</h1>
                <p className="text-gray-600 mt-1">Gerencie as finanças da sua loja</p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2">
                <div className="flex space-x-2">
                    {['dashboard', 'pagar', 'receber', 'fluxo'].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === tab ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                            {tab === 'dashboard' && 'Dashboard'}
                            {tab === 'pagar' && 'Despesas'}
                            {tab === 'receber' && 'Contas a Receber'}
                            {tab === 'fluxo' && 'Fluxo de Caixa'}
                        </button>
                    ))}
                </div>
            </div>
            
            <div className="mt-6">
                {activeTab === 'dashboard' && renderDashboard()}
                {activeTab === 'pagar' && renderContas('pagar')}
                {activeTab === 'receber' && renderContas('receber')}
                {activeTab === 'fluxo' && renderFluxoCaixa()}
            </div>
            
            <Modal isOpen={modalConfig.isOpen} onClose={() => setModalConfig({isOpen: false, type: null, item: null})} title={modalConfig.item ? 'Editar Lançamento' : 'Novo Lançamento'}>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input label="Descrição" value={formData.descricao || ''} onChange={(e) => setFormData({...formData, descricao: e.target.value})} required/>
                    <Input label="Valor (R$)" type="number" step="0.01" value={formData.valor || ''} onChange={(e) => setFormData({...formData, valor: e.target.value})} required/>
                    {modalConfig.type === 'pagar' && (
                        <>
                            <Input label="Data de Vencimento" type="date" value={formData.dataVencimento || ''} onChange={(e) => setFormData({...formData, dataVencimento: e.target.value})} required/>
                            <Select label="Categoria" value={formData.categoria || ''} onChange={(e) => setFormData({...formData, categoria: e.target.value})} required>
                                <option>Fornecedores</option>
                                <option>Despesa Fixa</option>
                                <option>Despesa Variável</option>
                            </Select>
                        </>
                    )}
                     {modalConfig.type === 'receber' && (
                        <>
                            <Input label="Data de Recebimento" type="date" value={formData.dataRecebimento || ''} onChange={(e) => setFormData({...formData, dataRecebimento: e.target.value})} required/>
                             <Select label="Método de Pagamento" value={formData.metodo || ''} onChange={(e) => setFormData({...formData, metodo: e.target.value})} required>
                                <option>Pix</option>
                                <option>Cartão</option>
                                <option>Dinheiro</option>
                                <option>Outro</option>
                            </Select>
                        </>
                    )}
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="secondary" onClick={() => setModalConfig({isOpen: false, type: null, item: null})}>Cancelar</Button>
                        <Button type="submit"><Save className="w-4 h-4"/> Salvar</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};


// --- FIM DOS NOVOS COMPONENTES ---

// Componente Relatorios adicionado no mesmo arquivo App.js para correção do erro
const Relatorios = ({ data }) => {
  const getInitialDateRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    return {
        start: formatDate(firstDay),
        end: formatDate(today)
    };
  };

  const [reportType, setReportType] = usePersistentState('relatorios_reportType', 'vendasPorPeriodo');
  const [startDate, setStartDate] = usePersistentState('relatorios_startDate', getInitialDateRange().start);
  const [endDate, setEndDate] = usePersistentState('relatorios_endDate', getInitialDateRange().end);
  const [reportData, setReportData] = useState([]);
  const [reportColumns, setReportColumns] = useState([]);
  const [reportTotals, setReportTotals] = useState(null);
  const [perdasColumns, setPerdasColumns] = useState([]);
  const [perdasData, setPerdasData] = useState([]);

  const formatCurrency = (value) =>
    (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (value) => {
    const date = getJSDate(value);
    return date ? date.toLocaleDateString('pt-BR') : '-';
  };

  const isCustoProducao = reportType === 'custoProducao';

  const handleGenerateReport = () => {
    let columns = [];
    let processedData = [];
    let totals = null;
    let perdasColumnsLocal = [];
    let perdasDataLocal = [];
    
    const filterByDate = (items, dateField) => {
        let filtered = items;
        if (startDate) filtered = filtered.filter(p => {
            const itemDate = getJSDate(p[dateField]);
            return itemDate && itemDate >= new Date(startDate + 'T00:00:00');
        });
        if (endDate) filtered = filtered.filter(p => {
            const itemDate = getJSDate(p[dateField]);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            return itemDate && itemDate <= end;
        });
        return filtered;
    }


    switch (reportType) {
        case 'vendasPorPeriodo': {
            const filtered = filterByDate(data.pedidos.filter(p => p.status === 'Finalizado'), 'createdAt');
            columns = [{ header: 'Data', key: 'date' }, { header: 'Nº de Vendas', key: 'count' }, { header: 'Total (R$)', key: 'total' }];
            const salesByDay = filtered.reduce((acc, pedido) => {
                const date = getJSDate(pedido.createdAt).toLocaleDateString('pt-BR');
                if (!acc[date]) acc[date] = { date, count: 0, total: 0 };
                acc[date].count++;
                acc[date].total += pedido.total;
                return acc;
            }, {});
            processedData = Object.values(salesByDay).map(d => ({...d, total: `R$ ${d.total.toFixed(2)}`}));
            break;
        }
        case 'produtosMaisVendidos': {
            const filtered = filterByDate(data.pedidos.filter(p => p.status === 'Finalizado'), 'createdAt');
            columns = [
                { header: 'Produto', key: 'nome' },
                { header: 'Quantidade Vendida', key: 'quantidade' },
                { header: 'Valor de Venda (R$)', key: 'valor' },
                { header: 'Custo Total (R$)', key: 'custoTotal' },
                { header: 'Lucro (R$)', key: 'lucro' }
            ];

            const productCostMap = (data.produtos || []).reduce((acc, produto) => {
                const custo = parseFloat(produto.custo || 0);
                if (produto.id) acc[produto.id] = custo;
                if (produto.nome) acc[produto.nome] = custo;
                return acc;
            }, {});

            const productSales = filtered.reduce((acc, pedido) => {
                (pedido.itens || []).forEach((item) => {
                    const id = item?.id || item?.nome;
                    if (!id) return;

                    const quantidade = Number(item.quantity) || 0;
                    const preco = Number(item.preco) || 0;
                    const custoUnitario = Number(
                        item.custo ?? productCostMap[item.id] ?? productCostMap[item.nome] ?? 0
                    );

                    if (!acc[id]) {
                        acc[id] = { nome: item.nome || 'Produto sem nome', quantidade: 0, valor: 0, custoTotal: 0 };
                    }

                    acc[id].quantidade += quantidade;
                    acc[id].valor += preco * quantidade;
                    acc[id].custoTotal += custoUnitario * quantidade;
                });

                return acc;
            }, {});

            totals = Object.values(productSales).reduce(
                (acc, item) => {
                    const custo = item.custoTotal || 0;
                    const valor = item.valor || 0;
                    return {
                        totalCost: acc.totalCost + custo,
                        totalSales: acc.totalSales + valor,
                        totalProfit: acc.totalProfit + (valor - custo)
                    };
                },
                { totalCost: 0, totalSales: 0, totalProfit: 0 }
            );

            processedData = Object.values(productSales)
                .filter(item => item.quantidade > 0)
                .map(item => ({
                    ...item,
                    valor: formatCurrency(item.valor),
                    custoTotal: formatCurrency(item.custoTotal),
                    lucro: formatCurrency(item.valor - item.custoTotal)
                }))
                .sort((a, b) => b.quantidade - a.quantidade);
            break;
        }
        case 'clientesMaisCompram': {
             const filtered = filterByDate(data.pedidos.filter(p => p.status === 'Finalizado'), 'createdAt');
             columns = [{ header: 'Cliente', key: 'nome' }, {header: 'Total Gasto (R$)', key: 'total'}, {header: 'Nº de Pedidos', key: 'pedidos'}];
             const customerSales = filtered.reduce((acc, pedido) => {
                if(!acc[pedido.clienteId]) acc[pedido.clienteId] = { nome: pedido.clienteNome, total: 0, pedidos: 0};
                acc[pedido.clienteId].total += pedido.total;
                acc[pedido.clienteId].pedidos += 1;
                return acc;
             }, {});
             processedData = Object.values(customerSales).sort((a,b) => b.total - a.total).map(c => ({...c, total: `R$ ${c.total.toFixed(2)}`}));
             break;
        }
        case 'usoCupons': {
            const filtered = filterByDate(data.pedidos.filter(p => p.cupom), 'createdAt');
            columns = [{ header: 'Cupom', key: 'codigo' }, { header: 'Usos', key: 'usos' }, { header: 'Total Descontado (R$)', key: 'totalDesconto' }];
            const couponUsage = filtered.reduce((acc, pedido) => {
                const codigo = pedido.cupom.codigo;
                if (!acc[codigo]) acc[codigo] = { codigo, usos: 0, totalDesconto: 0 };
                acc[codigo].usos++;
                acc[codigo].totalDesconto += pedido.cupom.valorDesconto || 0;
                return acc;
            }, {});
            processedData = Object.values(couponUsage).map(c => ({ ...c, totalDesconto: `R$ ${c.totalDesconto.toFixed(2)}` })).sort((a,b) => b.usos - a.usos);
            break;
        }
        case 'estoqueBaixo': {
             columns = [{ header: 'Produto', key: 'nome' }, { header: 'Estoque Atual', key: 'estoque' }];
             processedData = data.produtos.filter(p => p.estoque < 10).sort((a,b) => a.estoque - b.estoque);
             break;
        }
        case 'comprasInsumos': {
            const filtered = filterByDate(data.pedidosCompra.filter(p => p.status === 'Recebido'), 'dataPedido');
            columns = [{ header: 'Insumo', key: 'nome' }, { header: 'Quantidade Comprada', key: 'quantidade' }];
            const insumoSales = filtered.flatMap(p => p.itens || []).reduce((acc, item) => {
                if (!acc[item.id]) acc[item.id] = { nome: item.nome, quantidade: 0 };
                acc[item.id].quantidade += item.quantidade;
                return acc;
            }, {});
            processedData = Object.values(insumoSales).sort((a, b) => b.quantidade - a.quantidade);
            break;
        }
                case 'receitaPorPagamento': {
            const filtered = filterByDate(data.pedidos.filter(p => p.status === 'Finalizado'), 'createdAt');
            columns = [{ header: 'Forma de Pagamento', key: 'metodo' }, { header: 'Total Recebido (R$)', key: 'total' }];
            const paymentMethodSales = filtered.reduce((acc, pedido) => {
                const metodo = pedido.formaPagamento || 'Não informado';
                if (!acc[metodo]) acc[metodo] = { metodo, total: 0 };
                acc[metodo].total += pedido.total;
                return acc;
            }, {});
            processedData = Object.values(paymentMethodSales).map(d => ({...d, total: `R$ ${d.total.toFixed(2)}`})).sort((a,b) => b.total - a.total);
            break;
        }
        case 'custoProducao': {
            const filtered = filterByDate(
                data.pedidos.filter((p) => p.status === 'Finalizado'),
                'createdAt'
            );

            const perdasProcessadas = (data.perdasDescarte || [])
                .map((perda) => {
                    const produto = (data.produtos || []).find((p) => p.id === perda.produtoId);
                    const custoUnitario = Number(
                        perda.custoUnitario ?? produto?.custo ?? produto?.custoUnitario ?? 0
                    );
                    const quantidade = Number(perda.quantidade) || 0;
                    const dataPerda = perda.dataDescarte || perda.data;
                    const nomeProduto =
                        perda.nome || perda.produto || produto?.nome || 'Produto não informado';

                    return {
                        ...perda,
                        custoUnitario,
                        quantidade,
                        dataPerda,
                        nomeProduto,
                        valorTotal: quantidade * custoUnitario,
                    };
                })
                .filter((perda) => perda.quantidade > 0);

            const perdasFiltradas = filterByDate(perdasProcessadas, 'dataPerda').sort((a, b) => {
                const dataA = getJSDate(a.dataPerda) || new Date(0);
                const dataB = getJSDate(b.dataPerda) || new Date(0);
                return dataB - dataA;
            });

            const totalPerdas = perdasFiltradas.reduce(
                (total, perda) => total + (Number(perda.valorTotal) || 0),
                0
            );

            perdasColumnsLocal = [
                { header: 'Produto', key: 'produto' },
                { header: 'Quantidade', key: 'quantidade' },
                { header: 'Custo Unitário', key: 'custoUnitario' },
                { header: 'Valor Total', key: 'valorTotal' },
                { header: 'Data', key: 'data' },
                { header: 'Motivo', key: 'motivo' },
            ];

            perdasDataLocal = perdasFiltradas.map((perda) => ({
                produto: perda.nomeProduto,
                quantidade: perda.quantidade,
                custoUnitario: formatCurrency(perda.custoUnitario),
                valorTotal: formatCurrency(perda.valorTotal),
                data: formatDate(perda.dataPerda),
                motivo: perda.motivo || 'Não informado',
            }));

            columns = [
                { header: 'Produto', key: 'nome' },
                { header: 'Categoria', key: 'categoria' },
                { header: 'Custo Unitário (R$)', key: 'custo' },
                { header: 'Preço de Venda (R$)', key: 'preco' },
                { header: 'Lucro Unitário (R$)', key: 'lucroUnitario' },
                { header: 'Margem (%)', key: 'margemPercentual' }
            ];

            const productMap = (data.produtos || []).reduce((acc, produto) => {
                const produtoInfo = {
                    nome: produto.nome || 'Produto sem nome',
                    categoria: produto.subcategoria || produto.categoria || 'Não informado',
                    custo: Number(produto.custo) || 0,
                    preco: Number(produto.preco) || 0,
                };

                if (produto.id) acc[produto.id] = produtoInfo;
                if (produto.nome) acc[produto.nome] = produtoInfo;

                return acc;
            }, {});

            const productAggregates = filtered.reduce((acc, pedido) => {
                (pedido.itens || []).forEach((item) => {
                    const id = item?.id || item?.nome;
                    if (!id) return;

                    const quantidade = Number(item.quantity) || 0;
                    if (quantidade <= 0) return;

                    const produtoInfo = productMap[id] || {};
                    const custoUnitario = Number(item.custo ?? produtoInfo.custo ?? 0);
                    const precoUnitario = Number(item.preco ?? produtoInfo.preco ?? 0);
                    const nome = item.nome || produtoInfo.nome || 'Produto sem nome';
                    const categoria =
                        item.categoria || produtoInfo.categoria || 'Não informado';

                    if (!acc[id]) {
                        acc[id] = {
                            nome,
                            categoria,
                            quantidade: 0,
                            valorVenda: 0,
                            custoTotal: 0,
                        };
                    }

                    acc[id].quantidade += quantidade;
                    acc[id].valorVenda += precoUnitario * quantidade;
                    acc[id].custoTotal += custoUnitario * quantidade;
                });

                return acc;
            }, {});

            const { custoTotalGeral, valorTotalDeVendas, lucroTotalGeral } = Object.values(productAggregates).reduce(
                (acc, item) => {
                    const custoTotal = item.custoTotal || 0;
                    const valorTotal = item.valorVenda || 0;
                    return {
                        custoTotalGeral: acc.custoTotalGeral + custoTotal,
                        valorTotalDeVendas: acc.valorTotalDeVendas + valorTotal,
                        lucroTotalGeral: acc.lucroTotalGeral + (valorTotal - custoTotal),
                    };
                },
                { custoTotalGeral: 0, valorTotalDeVendas: 0, lucroTotalGeral: 0 }
            );

            processedData = Object.values(productAggregates)
                .filter((item) => item.quantidade > 0)
                .map((item) => {
                    const custoUnitario = item.quantidade
                        ? item.custoTotal / item.quantidade
                        : 0;
                    const precoUnitario = item.quantidade
                        ? item.valorVenda / item.quantidade
                        : 0;
                    const lucro = precoUnitario - custoUnitario;
                    const margem = precoUnitario ? (lucro / precoUnitario) * 100 : 0;

                    return {
                        nome: item.nome,
                        categoria: item.categoria,
                        custo: formatCurrency(custoUnitario),
                        preco: formatCurrency(precoUnitario),
                        lucroUnitario: formatCurrency(lucro),
                        margemPercentual: `${margem.toFixed(1)}%`,
                        custoValor: item.custoTotal,
                    };
                })
                .sort((a, b) => (b.custoValor || 0) - (a.custoValor || 0));

            totals = {
                totalCost: custoTotalGeral,
                totalSales: valorTotalDeVendas,
                totalProfit: lucroTotalGeral,
                totalPerdas,
                custoTotalProducao: custoTotalGeral + totalPerdas,
            };
            break;
        }
        default:
            break;
    }

    setReportColumns(columns);
    setReportData(processedData);
    setReportTotals(totals);
    setPerdasColumns(perdasColumnsLocal);
    setPerdasData(perdasDataLocal);
  };

  const exportPDF = () => {
    if (typeof window.jspdf === 'undefined') return;
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    doc.text(document.getElementById('report-select').selectedOptions[0].text, 14, 15);
    doc.autoTable({
        head: [reportColumns.map(c => c.header)],
        body: reportData.map(row => reportColumns.map(col => row[col.key])),
    });
    doc.save('relatorio.pdf');
  };

  const exportExcel = () => {
    if (typeof window.XLSX === 'undefined') return;
    const ws = window.XLSX.utils.json_to_sheet(reportData);
    const wb = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(wb, ws, "Relatorio");
    window.XLSX.writeFile(wb, "relatorio.xlsx");
  };

  return (
    <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
        <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Relatórios</h1>
            <p className="text-gray-600 mt-1">Analise o desempenho da sua loja</p>
        </div>

        <div className="p-4 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Input label="Data Inicial" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                <Input label="Data Final" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                <Select id="report-select" label="Tipo de Relatório" value={reportType} onChange={e => setReportType(e.target.value)}>
                    <option value="vendasPorPeriodo">Vendas por Período</option>
                    <option value="produtosMaisVendidos">Produtos Mais Vendidos</option>
                    <option value="clientesMaisCompram">Clientes que Mais Compram</option>
                    <option value="usoCupons">Uso de Cupons</option>
                    <option value="estoqueBaixo">Estoque Baixo (Produtos Finais)</option>
                    <option value="comprasInsumos">Compras de Insumos</option>
                    <option value="receitaPorPagamento">Receita por Forma de Pagamento</option>
                    <option value="custoProducao">Custo de Produção</option>
                </Select>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={handleGenerateReport} className="w-full sm:w-auto">Gerar Relatório</Button>
                <Button variant="secondary" onClick={() => { setStartDate(getInitialDateRange().start); setEndDate(getInitialDateRange().end); }} className="w-full sm:w-auto">Limpar Datas</Button>
                <Button onClick={exportPDF} variant="secondary" className="w-full sm:w-auto" disabled={reportData.length === 0}>Exportar PDF</Button>
                <Button onClick={exportExcel} variant="secondary" className="w-full sm:w-auto" disabled={reportData.length === 0}>Exportar Excel</Button>
            </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <Table columns={reportColumns} data={reportData} />
            {reportTotals && (
                <div className="p-6 border-t border-gray-100 bg-gray-50">
                    {isCustoProducao ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                <p className="text-sm text-gray-500">Valor Total de Vendas</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalSales)}</p>
                            </div>
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                <p className="text-sm text-gray-500">Custos Vendidos</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalCost)}</p>
                            </div>
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                <p className="text-sm text-gray-500">Total de Perdas</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalPerdas)}</p>
                            </div>
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 lg:col-span-2">
                                <p className="text-sm text-gray-500">Custo Total de Produção = Custos Vendidos + Custos Descartados</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.custoTotalProducao)}</p>
                            </div>
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 lg:col-span-2">
                                <p className="text-sm text-gray-500">Lucro Total</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalProfit)}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                <p className="text-sm text-gray-500">Custo Total</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalCost)}</p>
                            </div>
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                <p className="text-sm text-gray-500">Valor Total de Vendas</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalSales)}</p>
                            </div>
                            {reportTotals.totalPerdas !== undefined && (
                                <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                    <p className="text-sm text-gray-500">Total de Perdas</p>
                                    <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalPerdas)}</p>
                                </div>
                            )}
                            {reportTotals.custoTotalProducao !== undefined && (
                                <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                                    <p className="text-sm text-gray-500">Custo Total de Produção</p>
                                    <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.custoTotalProducao)}</p>
                                </div>
                            )}
                            <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100 lg:col-span-2">
                                <p className="text-sm text-gray-500">Lucro Total</p>
                                <p className="text-xl font-semibold text-gray-800">{formatCurrency(reportTotals.totalProfit)}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>

        {isCustoProducao && perdasData.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="px-6 pt-6 pb-2">
                    <h3 className="text-lg font-semibold text-gray-800">Custos de Descarte (Perdas)</h3>
                    <p className="text-sm text-gray-500">Itens descartados, do mais recente para o mais antigo.</p>
                </div>
                <Table columns={perdasColumns} data={perdasData} />
            </div>
        )}
    </div>
  );
};


// Componente principal
function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  const [currentPage, setCurrentPage] = usePersistentState('currentPage', 'pagina-inicial');
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [passwordResetEmail, setPasswordResetEmail] = useState("");
  const [passwordResetMessage, setPasswordResetMessage] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);
  const [stopAlarmFn, setStopAlarmFn] = useState(null);
  const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);
  const [hasNewPendingOrders, setHasNewPendingOrders] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const isiOS = useMemo(() => {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return true;

    if (platform === 'web' && typeof navigator !== 'undefined') {
      const ua = navigator.userAgent || '';
      return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    }

    return false;
  }, []);

  const [soundUnlocked, setSoundUnlocked] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('iosSoundUnlocked') === 'true';
  });

  const [isAlarmSnoozed, setIsAlarmSnoozed] = useState(false);
  const [snoozeEndTime, setSnoozeEndTime] = useState(null);
  const [audioAllowed, setAudioAllowed] = useState(audioManager.unlocked);
  // --- Estado audioUnlocked agora é derivado do AudioManager ---
  // const [audioUnlocked, setAudioUnlocked] = useState(...);


  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, onConfirm: () => {} });
  // ... (outros estados: showLogin, email, password, etc.) ...
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  // ... (outros estados: passwordResetEmail, passwordResetMessage) ...
  
  const [availableStores, setAvailableStores] = useState([]);
  const [storeInfoMap, setStoreInfoMap] = useState({});
  const [storeAlarmSnoozeMap, setStoreAlarmSnoozeMap] = useState({});
  const [selectedStoreId, setSelectedStoreId] = usePersistentState('selectedStoreId', null);
  const [showStoreManager, setShowStoreManager] = useState(false);
  const [isCreatingStore, setIsCreatingStore] = useState(false);

  const isTenantSubdomain = useMemo(
    () => !!getTenantSlugFromHostname(window.location.hostname),
    []
  );

  const prepareCentralizedLogin = useCallback(() => {
    const tenantSlug = getTenantSlugFromHostname(window.location.hostname);
    const returnUrl = window.location.href;
    const authUrl = new URL('/admin', AUTH_CENTER_ORIGIN);

    authUrl.searchParams.set(AUTH_RETURN_URL_QUERY_PARAM, returnUrl);
    if (tenantSlug) {
      authUrl.searchParams.set(AUTH_TENANT_SLUG_QUERY_PARAM, tenantSlug);
    }

    window.location.assign(authUrl.toString());
  }, []);

  const finalizeCentralizedLoginReturn = useCallback(() => {
    const returnUrl = sessionStorage.getItem(AUTH_RETURN_URL_KEY);
    if (!returnUrl) {
      return;
    }

    sessionStorage.removeItem(AUTH_RETURN_URL_KEY);
    sessionStorage.removeItem(AUTH_TENANT_SLUG_KEY);
    window.location.assign(returnUrl);
  }, []);

  useEffect(() => {
    if (!isiOS || soundUnlocked) return undefined;

    const unlockWithGesture = async () => {
      try {
        await audioManager.userUnlock({ userGesture: true });
        setAudioAllowed(audioManager.unlocked);

        const htmlAudio = new Audio(ALARM_SOUND_URL);
        const playPromise = htmlAudio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        htmlAudio.pause();
        htmlAudio.currentTime = 0;
      } catch (error) {
        console.warn('[App.js] Não foi possível iniciar o áudio no gesto de desbloqueio:', error);
      }

      localStorage.setItem('iosSoundUnlocked', 'true');
      setSoundUnlocked(true);
      setAudioAllowed(audioManager.unlocked);
      window.removeEventListener('touchstart', unlockWithGesture);
      window.removeEventListener('click', unlockWithGesture);
    };

    window.addEventListener('touchstart', unlockWithGesture, { passive: true });
    window.addEventListener('click', unlockWithGesture, { passive: true });

    return () => {
      window.removeEventListener('touchstart', unlockWithGesture);
      window.removeEventListener('click', unlockWithGesture);
    };
  }, [isiOS, soundUnlocked]);


  // --- REVISADO: Refs de Áudio ---
  const stopAlarmRef = useRef(null); // Guarda a função de parar o som
  const stopAlarmFnRef = useRef(null);
  const snoozeTimerRef = useRef(null);
  const isSnoozedRef = useRef(false);
  const initialDataLoaded = useRef(false);
  const storeCollectionsDataRef = useRef({});
  const clientesDataRef = useRef([]);
  const pushTokenRef = useRef(null);
  const configMigrationStatusRef = useRef(new Set());
  // --- REMOVIDO: audioRef e alarmIntervalRef ---

  
  const [data, setData] = useState(getInitialDataState());
  const [loading, setLoading] = useState(true);
  const userId = user?.auth?.uid || null;

  
   const resolveStoreIdsForView = useCallback(() => {
    if (!user) return [];

    if (user.role === ROLE_OWNER) {
        if (selectedStoreId === STORE_ALL_KEY) {
            return availableStores;
        }
        return selectedStoreId ? [selectedStoreId] : (availableStores.length ? [availableStores[0]] : []);
    }

    if (selectedStoreId) return [selectedStoreId];
    if (availableStores.length) return [availableStores[0]];
    if (user.lojaIds && user.lojaIds.length) return [user.lojaIds[0]];
    if (user.lojaId) return [user.lojaId];
    return [];
  }, [user, selectedStoreId, availableStores]);

  const recomputeDataForView = useCallback(() => {
    const base = getInitialDataState();

    if (!user) {
      return base;
    }

    const storeIds = resolveStoreIdsForView();
    if (!storeIds.length) {
      return base;
    }

    storeIds.forEach((storeId) => {
      const storeData = storeCollectionsDataRef.current[storeId];
      if (!storeData) return;

      COLLECTIONS_TO_SYNC.forEach((collectionName) => {
        const items = storeData[collectionName] || [];
        if (user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
          base[collectionName] = [
            ...(base[collectionName] || []),
            ...items.map((item) => ({ ...item, lojaId: storeId }))
          ];
        } else {
          base[collectionName] = items.map((item) => ({ ...item, lojaId: storeId }));
        }
      });
    });

    const allClientes = clientesDataRef.current || [];
    const storeIdSet = new Set(storeIds);

    const matchesStore = (cliente) => {
      const visitedStores = Array.isArray(cliente.lojasVisitadas) ? cliente.lojasVisitadas : [];
      if (visitedStores.some((id) => storeIdSet.has(id))) return true;
      if (cliente.lojaId && storeIdSet.has(cliente.lojaId)) return true;
      if (cliente.lojaAtual && storeIdSet.has(cliente.lojaAtual)) return true;
      return false;
    };

    const addResolvedStoreToClient = (cliente) => {
      const visitedStores = Array.isArray(cliente.lojasVisitadas) ? cliente.lojasVisitadas : [];
      const resolvedLojaId = visitedStores.length === 1
        ? visitedStores[0]
        : (cliente.lojaAtual || cliente.lojaId || null);

      if (!resolvedLojaId) return cliente;
      return { ...cliente, lojaId: resolvedLojaId };
    };

    if (user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
      base.clientes = allClientes.map(addResolvedStoreToClient);
    } else {
      base.clientes = allClientes.filter(matchesStore).map(addResolvedStoreToClient);
    }

    return base;
  }, [user, resolveStoreIdsForView, selectedStoreId]);

  const migrateLegacyConfigCollection = useCallback(async (storeId, collectionName, legacyDocs) => {
    const migrationKey = `${storeId}:${collectionName}`;
    if (configMigrationStatusRef.current.has(migrationKey)) return;

    configMigrationStatusRef.current.add(migrationKey);

    try {
      const batch = writeBatch(db);
      legacyDocs.forEach((docSnap) => {
        batch.set(
          doc(db, ...buildStoreCollectionPath(storeId, collectionName), docSnap.id),
          docSnap.data()
        );
      });
      await batch.commit();
      console.log(`[App.js] Migração de ${collectionName} concluída para a loja ${storeId}.`);
    } catch (error) {
      console.error(`[App.js] Falha ao migrar ${collectionName} da loja ${storeId}:`, error);
    }
  }, []);

  const resolveActiveStoreForWrite = useCallback(() => {
        if (!user) {
          throw new Error('Usuário não autenticado.');
        }

	if (user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
	  throw new Error('Selecione uma loja específica para executar esta ação.');
	}

	const storeId = selectedStoreId || (availableStores.length ? availableStores[0] : (user.lojaIds && user.lojaIds.length ? user.lojaIds[0] : user.lojaId));

	if (!storeId) {
	  throw new Error('Nenhuma loja associada ao usuário.');
	}

        return storeId;
  }, [user, selectedStoreId, availableStores]);

  const callClientApi = useCallback(async (path, { method = 'GET', body = null } = {}) => {
    const storeId = resolveActiveStoreForWrite();
    const url = `${API_BASE_URL}${path}${path.includes('?') ? '&' : '?'}lojaId=${encodeURIComponent(storeId)}`;

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || 'Erro ao comunicar com a API de clientes.');
    }

    if (response.status === 204) return null;
    return response.json();
  }, [resolveActiveStoreForWrite]);
  
  const selectStoreById = useCallback((value) => {
        if (value === STORE_ALL_KEY) {
          setSelectedStoreId(STORE_ALL_KEY);
        } else if (value) {
          setSelectedStoreId(value);
        } else {
          setSelectedStoreId(null);
        }
  }, [setSelectedStoreId]);

  const handleStoreChange = useCallback((event) => {
        selectStoreById(event.target.value);
  }, [selectStoreById]);

  const handleCreateStore = useCallback(async ({ storeId, nome, slug }) => {
        const trimmedId = typeof storeId === 'string' ? storeId.trim() : '';
        const trimmedName = typeof nome === 'string' ? nome.trim() : '';

        if (!trimmedName) {
          throw new Error('Nome e identificador são obrigatórios.');
        }

        setIsCreatingStore(true);

        try {
          const createStoreFn = httpsCallable(functions, 'createStore');
          const result = await createStoreFn({ storeId: trimmedId, nome: trimmedName, slug: typeof slug === 'string' ? slug.trim() : '' });
          const response = (result && result.data) || {};
          const createdStoreId = response.storeId || generateStoreId(trimmedId || trimmedName);

          if (!createdStoreId || createdStoreId === STORE_ALL_KEY) {
            throw new Error('Identificador inválido para a loja.');
          }

          const storeData = response.storeData || { nome: trimmedName };

          setAvailableStores((prev) => (prev.includes(createdStoreId) ? prev : [...prev, createdStoreId]));
          setStoreInfoMap((prev) => ({
            ...prev,
            [createdStoreId]: { ...(prev[createdStoreId] || {}), ...storeData }
          }));

          selectStoreById(createdStoreId);

          if (Array.isArray(response.assignedStoreIds)) {
            setUser((prev) => {
              if (!prev) return prev;
              const uniqueIds = Array.from(new Set(response.assignedStoreIds));
              const nextPrimary = response.primaryStoreId || prev.lojaId || uniqueIds[0] || null;

              return {
                ...prev,
                lojaIds: uniqueIds,
                lojaId: nextPrimary,
                canAccessAllStores: typeof response.canAccessAllStores === 'boolean'
                  ? response.canAccessAllStores
                  : prev.canAccessAllStores
              };
            });
          }
        } catch (error) {
          console.error('Erro ao criar loja:', error);
          if (error && typeof error.message === 'string') {
            const cleanedMessage = error.message.replace(/^FirebaseError:\s*/i, '').trim();
            throw new Error(cleanedMessage || 'Não foi possível criar a loja.');
          }
          throw new Error('Não foi possível criar a loja.');
		  } finally {
          setIsCreatingStore(false);
        }
  }, [selectStoreById, setUser]);

  // --- SUBSTITUÍDO: Nova função stopAlarm ---
        const stopAlarm = useCallback(() => {
                console.log("[App.js] Parando alarme...");
                if (stopAlarmRef.current) {
		  stopAlarmRef.current(); // Chama a função de parada
		  stopAlarmRef.current = null; // Limpa a referência
		}
		if (stopAlarmFn) {
		  stopAlarmFn(); // Também chama a função do estado se existir
		  setStopAlarmFn(null); // Limpa o estado
		}
		setIsAlarmPlaying(false); // Atualiza o estado da UI
	}, [stopAlarmFn]);

  // --- REMOVIDO: Antiga função unlockAudio ---

  // --- SUBSTITUÍDO: Nova função playAlarm ---
  const playAlarm = useCallback(async () => {
                if (isiOS && !soundUnlocked) {
                        console.warn("[App.js] Áudio bloqueado no iOS aguardando interação do usuário.");
                        return;
                }
                // Só toca se não estiver em modo soneca
                if (isSnoozedRef.current) {
                        console.log("[App.js] Alarme em soneca, não tocando.");
                        return;
                }
		
		// Se já está tocando, não faz nada
		if (isAlarmPlaying) {
			console.log("[App.js] Alarme já está tocando, ignorando nova chamada.");
			return;
		}

		console.log("[App.js] Tentando tocar alarme...");

		// Chama o AudioManager para tocar o som
		// Verifica se o áudio está desbloqueado antes de tentar tocar
		if (!audioManager.unlocked) {
		  console.warn("[App.js] Áudio bloqueado — aguardando interação do usuário.");
		  try {
			await audioManager.userUnlock();
		  } catch (e) {
			console.warn("[App.js] Não foi possível desbloquear o áudio automaticamente:", e);
		  }
		}

		let started = false;
		try {
			started = await audioManager.playAlarmSound();
		} catch (error) {
			console.error("[App.js] Erro ao iniciar o alarme:", error);
		}

		if (started) {
			const stopFn = () => audioManager.stopAlarmSound();
			setIsAlarmPlaying(true);
			setStopAlarmFn(() => stopFn);
			stopAlarmRef.current = stopFn;
			console.log("[App.js] Alarme iniciado.");
		} else {
			console.log("[App.js] Alarme pendente aguardando desbloqueio do áudio.");
			setIsAlarmPlaying(false);
			setShowActivateSoundButton(true);
		}
        }, [isiOS, isAlarmPlaying, soundUnlocked]); // Adicione isAlarmPlaying como dependência
	
	  // --- PRÉ-CARREGAMENTO DO ÁUDIO NATIVO (Capacitor Android/iOS) ---
          useEffect(() => {
                const loadAudio = async () => {
                  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
                        try {
                          await NativeAudio.preload({
                                        assetId: 'pedido',
                                        assetPath: 'mixkit_vintage_warning_alarm_990.mp3',
                                        audioChannelNum: 1,
                                        isUrl: false,
                          });
                          console.log('🔊 Áudio pré-carregado com sucesso!');
                        } catch (err) {
                          console.error('Erro ao carregar áudio:', err);
                        }
                  }
                };

                loadAudio();
          }, []);

  // --- SUBSTITUÍDO: Novo useEffect de inicialização do AudioManager ---
  useEffect(() => {
    if (!user) return;
    setAudioAllowed(audioManager.unlocked);
  }, [user]); // Depende do 'user' para saber se deve mostrar o botão

  // --- NOVO: Estado para controlar a exibição do botão de ativar som ---
  const [showActivateSoundButton, setShowActivateSoundButton] = useState(false);
  
    useEffect(() => {
    if (!user) {
      return;
    }

    const requestCapabilities = async () => {
      if (typeof navigator === 'undefined') {
        return;
      }

      const requestGeolocation = async () => {
        if (!navigator.geolocation) {
          return;
        }

        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve(position),
            (error) => {
              console.warn('[App.js] Permissão de geolocalização negada ou indisponível:', error);
              resolve(null);
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
          );
        });
      };

      try {
        if (navigator.permissions?.query) {
          try {
            const geoStatus = await navigator.permissions.query({ name: 'geolocation' });
            if (geoStatus.state === 'granted') {
              await requestGeolocation();
            }
          } catch (error) {
            // Silencia falhas do Permissions API para não quebrar o fluxo principal.
          }
        }
      } catch (error) {
        console.warn('[App.js] Erro ao solicitar geolocalização:', error);
      }
	};

    requestCapabilities();
  }, [user]);

  // --- NOVO: Effect para verificar e mostrar o botão de ativar som ---
  useEffect(() => {
      // Define um pequeno delay para dar tempo ao audioManager.init() tentar o resume automático
      const timer = setTimeout(() => {
          if (user && !audioManager.unlocked) {
              setShowActivateSoundButton(true);
              console.log("[App.js] Áudio não desbloqueado, mostrando botão.");
          } else {
              setShowActivateSoundButton(false);
          }
      }, 500); // Meio segundo de espera

      return () => clearTimeout(timer);

  }, [user]);


  // EFFECT para sincronizar ref com estado isAlarmSnoozed
  useEffect(() => {
    isSnoozedRef.current = isAlarmSnoozed;
  }, [isAlarmSnoozed]);

  // --- Refs para estabilizar callbacks ---
  const playAlarmRef = useRef(playAlarm);
  useEffect(() => {
      playAlarmRef.current = playAlarm;
  }, [playAlarm]);
  
    useEffect(() => {
    stopAlarmFnRef.current = stopAlarmFn;
  }, [stopAlarmFn]);
  
   const handleIncomingPushNotification = useCallback((payload) => {
    console.log('[App.js] Notificação push recebida:', payload);
    setHasNewPendingOrders(true);

    if (isSnoozedRef.current) {
      console.log('[App.js] Push recebido durante soneca. Alarme permanecerá silenciado até o fim da soneca.');
      return;
    }

    if (typeof playAlarmRef.current === 'function') {
      playAlarmRef.current();
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      pushTokenRef.current = null;
      return;
    }

    let unsubscribeForeground = null;
    let unsubscribeServiceWorker = null;
    let cancelled = false;

    const setupPushNotifications = async () => {
      try {
        const token = await registerDeviceForPush(userId);
        if (token) {
          pushTokenRef.current = token;
          console.log('[App.js] Token de push registrado para o usuário:', userId, token);
        }
      } catch (error) {
        console.error('[App.js] Erro ao configurar notificações push:', error);
      }

      if (cancelled) {
        return;
      }

      try {
        unsubscribeForeground = await listenForForegroundMessages((payload) => {
          console.log('[App.js] Mensagem de push recebida em primeiro plano:', payload);
          handleIncomingPushNotification(payload);
        });
      } catch (error) {
        console.error('[App.js] Não foi possível escutar mensagens em primeiro plano:', error);
      }

      unsubscribeServiceWorker = subscribeToServiceWorkerMessages((event) => {
        const message = event.data;
        if (!message) {
          return;
        }

        if (message.type === 'NEW_ORDER_PUSH') {
          handleIncomingPushNotification(message.payload);
        }
		
        if (message.type === 'PLAY_ORDER_SOUND' && typeof playAlarmRef.current === 'function') {
          playAlarmRef.current();
        }		
		
      });
    };

    setupPushNotifications();

    return () => {
      cancelled = true;
      if (typeof unsubscribeForeground === 'function') {
        unsubscribeForeground();
      }
      if (typeof unsubscribeServiceWorker === 'function') {
        unsubscribeServiceWorker();
      }
    };
  }, [userId, handleIncomingPushNotification]);

  const dataRef = useRef(data);
  useEffect(() => {
      dataRef.current = data;
  }, [data]);
  
  // FUNÇÃO PARA PARAR E ATIVAR SONEÇA - Refatorada
  const handleStopAndSnoozeAlarm = useCallback(() => {
    const snoozeMinutes = resolveStoreAlarmSnoozeMinutes(selectedStoreId, storeAlarmSnoozeMap, DEFAULT_ALARM_SNOOZE_MINUTES);

    console.log(`[App.js] Ativando soneca por ${snoozeMinutes} minuto(s)...`);
    stopAlarm(); // Para o alarme atual
	setStopAlarmFn(null); // Limpa o estado da função de parada
    setIsAlarmSnoozed(true); // Ativa o estado de soneca
    
    const endTime = new Date().getTime() + (snoozeMinutes * 60 * 1000);
    setSnoozeEndTime(endTime); // Define o tempo final da soneca
    
    // Limpa timer anterior se existir
    if (snoozeTimerRef.current) clearInterval(snoozeTimerRef.current);
    
    // Inicia timer para reativar alarme
    snoozeTimerRef.current = setInterval(() => {
      const now = new Date().getTime();
      const remaining = endTime - now;
      
      if (remaining <= 0) {
        // Fim da soneca
        clearInterval(snoozeTimerRef.current);
        snoozeTimerRef.current = null;
        setIsAlarmSnoozed(false); // Desativa o estado de soneca
        setSnoozeEndTime(null);
        console.log('[App.js] Soneca terminada');
        
        // Verifica se ainda existem pedidos pendentes para tocar o alarme novamente
        const hasPending = dataRef.current.pedidos && dataRef.current.pedidos.some(p => p.status === 'Pendente');
        if (hasPending) {
          console.log('[App.js] Pedidos pendentes encontrados após soneca, reativando alarme.');
          setHasNewPendingOrders(true); // Garante que o banner apareça (se necessário)
          playAlarmRef.current(); // Tenta tocar o alarme usando a ref
        } else {
           setHasNewPendingOrders(false); // Esconde o banner se não há mais pendentes
        }
      } 
      // O display do timer é gerenciado localmente pelo Dashboard
    }, 1000); // Atualiza a cada segundo
  }, [selectedStoreId, stopAlarm, storeAlarmSnoozeMap]); // Removidas dependências instáveis (data, playAlarm, unlockAudio)

  // EFFECT PARA SINCRONIZAR DADOS DO FIREBASE
        useEffect(() => {
          const storeIds = resolveStoreIdsForView();

          if (!user || !storeIds.length) {
                setData(getInitialDataState());
                setPendingOrders([]);
                clientesDataRef.current = [];
                storeCollectionsDataRef.current = {};
                setLoading(false);
                initialDataLoaded.current = false;
                return;
          }

          let isMounted = true;
          let pendingInitial = (storeIds.length * COLLECTIONS_TO_SYNC.length) + 1;
          const unsubscribes = [];

          const markInitialLoaded = () => {
                if (pendingInitial > 0) {
                      pendingInitial -= 1;
                      if (pendingInitial === 0) {
                            initialDataLoaded.current = true;
                            setLoading(false);
                      }
                }
          };

          if (pendingInitial === 0) {
                setLoading(false);
                initialDataLoaded.current = true;
          } else {
                setLoading(true);
                initialDataLoaded.current = false;
          }

          const setupClientesListener = () => {
                const uniqueStoreIds = Array.from(new Set(storeIds)).filter(Boolean);
                const clientesRef = collection(db, 'clientes');

                let clientesQuery;
                if (user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
                      clientesQuery = query(clientesRef);
                } else if (uniqueStoreIds.length === 1) {
                      clientesQuery = query(clientesRef, where('lojasVisitadas', 'array-contains', uniqueStoreIds[0]));
                } else if (uniqueStoreIds.length > 1) {
                      clientesQuery = query(clientesRef, where('lojasVisitadas', 'array-contains-any', uniqueStoreIds.slice(0, 10)));
                } else {
                      clientesQuery = query(clientesRef);
                }

                let initialResolved = false;
                const unsubscribe = onSnapshot(
                      clientesQuery,
                      (snapshot) => {
                            if (!isMounted) return;

                            clientesDataRef.current = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
                            const computedData = recomputeDataForView();
                            setData(computedData);

                            if (!initialResolved) {
                                  markInitialLoaded();
                                  initialResolved = true;
                            }
                      },
                      (error) => {
                            console.error('[App.js] Erro ao sincronizar clientes:', error);
                            if (!initialResolved) {
                                  markInitialLoaded();
                                  initialResolved = true;
                            }
                      }
                );

                unsubscribes.push(() => unsubscribe());
          };

          setupClientesListener();

          storeIds.forEach((storeId) => {
                COLLECTIONS_TO_SYNC.forEach((collectionName) => {
                        const isConfigCollection = CONFIG_COLLECTIONS.has(collectionName);
                        const primaryQuery = query(getStoreCollectionRef(storeId, collectionName));
                        const legacyQuery = isConfigCollection ? query(getStoreCollectionRef(storeId, collectionName, true)) : null;

                        let primaryItems = [];
                        let legacyItems = [];
                        let legacyUnsubscribe = null;
                        let initialResolved = false;

                        const applyItems = (changes = []) => {
                              if (!isMounted) return;
                              const itemsToUse = primaryItems.length ? primaryItems : legacyItems;

                              storeCollectionsDataRef.current = {
                                    ...storeCollectionsDataRef.current,
                                    [storeId]: {
                                          ...(storeCollectionsDataRef.current[storeId] || {}),
                                          [collectionName]: itemsToUse
                                    }
                              };

                              const computedData = recomputeDataForView();
                              setData(computedData);

                              if (collectionName === 'pedidos') {
                                    const activeOrders = (computedData.pedidos || []).filter(p => p.status !== 'Finalizado' && p.status !== 'Cancelado');
                                    setPendingOrders(activeOrders);

                                    if (initialDataLoaded.current) {
                                          const isOverviewSelected = user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY;
                                          const newPendingOrdersDetected = changes.some(change => change.type === 'added' && change.doc.data().status === 'Pendente');

                                          if (newPendingOrdersDetected && !isOverviewSelected && !isAlarmPlaying && !isSnoozedRef.current) {
                                                console.log('[App.js] Novo pedido pendente detectado pelo listener!');
                                                setHasNewPendingOrders(true);

                                                console.log('[App.js] Tentando tocar alarme...');
                                                (async () => {
                                                  try {
                                                        if (!audioManager.unlocked) {
                                                          console.warn("[App.js] Áudio bloqueado — aguardando interação do usuário.");
                                                          try {
                                                                await audioManager.userUnlock();
                                                          } catch (e) {
                                                                console.warn("[App.js] Não foi possível desbloquear o áudio automaticamente:", e);
                                                          }
                                                        }

                                                        if (audioManager.unlocked) {
                                                          playAlarmRef.current();
                                                        } else {
                                                          console.log("[App.js] Áudio ainda bloqueado, não tocando alarme.");
                                                        }

                                                  } catch (error) {
                                                        console.error("[App.js] Erro ao tentar tocar alarme:", error);
                                                  }
                                                })();
                                          } else if (newPendingOrdersDetected && isOverviewSelected) {
                                                // Sem alarme na Visão Geral.
                                                stopAlarm();
                                          } else if (newPendingOrdersDetected && isSnoozedRef.current) {
                                                console.log('[App.js] Alarme em modo soneca, não tocando agora.');
                                          } else if (newPendingOrdersDetected && isAlarmPlaying) {
                                                console.log('[App.js] Alarme já está tocando, não iniciando novo.');
                                          }
                                    }
                              }

                              if (!initialResolved) {
                                    markInitialLoaded();
                                    initialResolved = true;
                              }
                        };

                        const handleSnapshotError = (error) => {
                              console.error(`[App.js] Erro ao sincronizar ${collectionName} da loja ${storeId}:`, error);
                              if (!initialResolved) {
                                    markInitialLoaded();
                                    initialResolved = true;
                              }
                        };

                        const primaryUnsubscribe = onSnapshot(
                              primaryQuery,
                              (snapshot) => {
                                    primaryItems = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
                                    applyItems(snapshot.docChanges());

                                    if (!primaryItems.length && isConfigCollection && legacyQuery && !legacyUnsubscribe) {
                                          legacyUnsubscribe = onSnapshot(
                                                legacyQuery,
                                                (legacySnap) => {
                                                      if (primaryItems.length) return;
                                                      legacyItems = legacySnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
                                                      if (!primaryItems.length && isConfigCollection && legacySnap.docs.length) {
                                                            migrateLegacyConfigCollection(storeId, collectionName, legacySnap.docs);
                                                      }
                                                      applyItems(legacySnap.docChanges());
                                                },
                                                handleSnapshotError
                                          );
                                    } else if (primaryItems.length && legacyUnsubscribe) {
                                          legacyUnsubscribe();
                                          legacyUnsubscribe = null;
                                          legacyItems = [];
                                    }
                              },
                              handleSnapshotError
                        );

                        unsubscribes.push(() => {
                              primaryUnsubscribe();
                              if (legacyUnsubscribe) legacyUnsubscribe();
                        });
                });
          });

          return () => {
                isMounted = false;
                unsubscribes.forEach(unsubscribe => unsubscribe());
                initialDataLoaded.current = false;
          };
        }, [user, isAlarmPlaying, resolveStoreIdsForView, recomputeDataForView, selectedStoreId, availableStores, migrateLegacyConfigCollection]);
    // EFFECT PARA PARAR ALARME QUANDO NÃO HÁ MAIS PEDIDOS PENDENTES
    useEffect(() => {
        const isOverviewSelected = user?.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY;
        const hasPendingOrdersForSelectedStore = data.pedidos && data.pedidos.some(p => p.status === 'Pendente');

        if (isOverviewSelected) {
          stopAlarm();
          return;
        }

        if (!hasPendingOrdersForSelectedStore && !isAlarmSnoozed) {
          console.log('[App.js] Nenhum pedido pendente e não está em soneca. Parando alarme e escondendo banner.');
          setHasNewPendingOrders(false);
          stopAlarm();
        }
    }, [data.pedidos, isAlarmSnoozed, selectedStoreId, stopAlarm, user]);

    // Garante que o alarme continue tocando enquanto houver pedidos pendentes
    useEffect(() => {
        const isOverviewSelected = user?.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY;
        const hasPendingOrdersForSelectedStore = pendingOrders.some(order => order.status === 'Pendente');
        const shouldPlayAlarm = !isOverviewSelected && hasPendingOrdersForSelectedStore && !isAlarmSnoozed;

        if (!shouldPlayAlarm) {
          stopAlarm();
          return;
        }

        if (audioAllowed && !isAlarmPlaying) {
          console.log('[App.js] Pedidos pendentes encontrados enquanto o alarme estava parado. Reativando alarme.');
          setHasNewPendingOrders(true);
          playAlarmRef.current();
        }
    }, [audioAllowed, pendingOrders, isAlarmSnoozed, isAlarmPlaying, selectedStoreId, stopAlarm, user]);

  // --- REMOVIDO: Antigo useEffect de desbloqueio ---
  // useEffect(() => { if (audioUnlocked && ...) ... });


  const updateStock = useCallback(async (productId, type, quantity, reason = 'Movimentação de estoque', userInfo = null, targetStoreId = null) => {
    const storeId = targetStoreId || resolveActiveStoreForWrite();
    await updateStockService(productId, type, quantity, reason, userInfo, storeId);
  }, [resolveActiveStoreForWrite]);


  const addItem = async (section, item, targetStoreId = null) => {
    try {
        const storeId = targetStoreId || resolveActiveStoreForWrite();

        if (section === 'clientes') {
            await callClientApi('/clientes', { method: 'POST', body: item });
            return;
        }

        const payload = {
            ...item,
                        ...(item?.lojaId ? {} : { lojaId: storeId }),
            createdAt: new Date()
         };

        const docRef = await addDoc(getStoreCollectionRef(storeId, section), payload);
        if (user && section !== 'logs') {
            await addDoc(getStoreCollectionRef(storeId, 'logs'), {
                action: `Novo item adicionado em ${section}`,
                details: `ID: ${docRef.id}`,
                userEmail: user?.auth?.email || 'N/A',
                timestamp: new Date()
            });
        }
    } catch (e) {
        console.error("Erro ao adicionar documento: ", e);
                if (e && e.message) {
            alert(e.message);
        }
    }
  };

  const updateItem = async (section, id, updatedItem, targetStoreId = null) => {
    try {
        const storeId = targetStoreId || resolveActiveStoreForWrite();

        if (section === 'clientes') {
            await callClientApi(`/clientes/${id}`, { method: 'PUT', body: updatedItem });
            return;
        }

        const itemDoc = getStoreDocRef(storeId, section, id);
        if (user && section !== 'logs') {
             const docSnap = await getDoc(itemDoc);
             if (docSnap.exists()) {
                const oldData = docSnap.data();
                const changes = {};
                for (const key in updatedItem) {

                    if (Object.prototype.hasOwnProperty.call(updatedItem, key) &&
                        JSON.stringify(oldData[key]) !== JSON.stringify(updatedItem[key])) {
                        changes[key] = { old: oldData[key], new: updatedItem[key] };
                    }
                }
                if (Object.keys(changes).length > 0) {
                     await addDoc(getStoreCollectionRef(storeId, 'logs'), {
                        action: `Item atualizado em ${section}`,
                        details: `ID ${id} com alterações: ${JSON.stringify(changes)}`,
                        userEmail: user?.auth?.email || 'N/A',
                        timestamp: new Date()
                    });
                }
             }
        }
        await updateDoc(itemDoc, updatedItem);
    } catch (e) {
        console.error("Erro ao atualizar documento: ", e);
		if (e && e.message) {
            alert(e.message);
        }
    }
  };

  const deleteItem = async (section, id, targetStoreId = null) => {
    try {
        const storeId = targetStoreId || resolveActiveStoreForWrite();
        await deleteDoc(getStoreDocRef(storeId, section, id));
        if (user && section !== 'logs') {
            await addDoc(getStoreCollectionRef(storeId, 'logs'), {
                action: `Item deletado de ${section}`,
                details: `ID: ${id}`,
                userEmail: user?.auth?.email || 'N/A',
                timestamp: new Date()
            });
        }
    } catch (e) {
        console.error("Erro ao deletar documento: ", e);

		    if (e && e.message) {
            alert(e.message);
        }
    }
  };
  
  useEffect(() => {
    const scripts = [
        { id: 'jspdf', src: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js' },
        { id: 'jspdf-autotable', src: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.23/jspdf.plugin.autotable.min.js' },
        { id: 'xlsx', src: 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js' },
        { id: 'chartjs', src: 'https://cdn.jsdelivr.net/npm/chart.js' }
    ];

    scripts.forEach(scriptInfo => {
        if (!document.getElementById(scriptInfo.id)) {
            const script = document.createElement('script');
            script.id = scriptInfo.id;
            script.src = scriptInfo.src;
            script.async = true;
            document.body.appendChild(script);
        }
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
        const desktop = window.innerWidth >= 768;
        setIsDesktop(desktop);
        setSidebarOpen(desktop);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
	useEffect(() => {
	  const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
                        if (authUser) {
                          try {
                                        const userDocRef = doc(db, "users", authUser.uid);
                                        const userDoc = await getDoc(userDocRef);

                                        let profile;

                                        if (userDoc.exists()) {
                                          profile = userDoc.data() || {};
                                        } else {
                                          let initialRole = ROLE_DEFAULT;

                                          try {
                                            const anyUserSnap = await getDocs(query(collection(db, "users"), limit(1)));
                                            if (anyUserSnap.empty) {
                                              initialRole = ROLE_OWNER;
                                            }
                                          } catch (roleCheckError) {
                                            console.error("Erro ao verificar usuários existentes:", roleCheckError);
                                            initialRole = ROLE_OWNER;
                                          }

                                          profile = {
                                            email: authUser.email || "",
                                            nome: authUser.displayName || authUser.email || "Usuário",
                                            role: initialRole,
                                            lojaId: null,
                                            lojaIds: [],
                                          };

                                          await setDoc(userDocRef, profile, { merge: true });
                                        }

                                        const role = normalizeRole(profile.role);
                                        const lojaIds = extractStoreIdsFromProfile(profile);
                                        const permissionsDefaults = getDefaultPermissionsForRole(role);
                                        const customProfileRef = doc(db, "customProfiles", authUser.uid);
                                        const customProfileSnap = await getDoc(customProfileRef);
                                        const customProfileData = customProfileSnap.exists() ? customProfileSnap.data() : null;
                                        const customPermissions = customProfileData?.permissions
                                          ? sanitizePermissions(customProfileData.permissions, role)
                                          : null;
                                        const permissions = customPermissions || permissionsDefaults;

                                        if (!customProfileSnap.exists()) {
                                          await setDoc(customProfileRef, {
                                            uid: authUser.uid,
                                            role,
                                            permissions: permissionsDefaults,
                                          }, { merge: true });
                                        }
                                        const userData = {
                                          auth: authUser,
                                          role,
                                          lojaIds,
                                          lojaId: lojaIds[0] || null,
                                          canAccessAllStores: role === ROLE_OWNER && lojaIds.length === 0,
                                          permissions,
                                          customPermissions,
                                          hasCustomProfile: Boolean(customProfileData),
                                        };
                                        setUser(userData)
			if (sessionStorage.getItem(GOOGLE_AUTH_FLOW_KEY) === 'true') {
			  setShowLogin(false);
			  setCurrentPage('dashboard');
			  sessionStorage.removeItem(GOOGLE_AUTH_FLOW_KEY);
			}
			setAudioAllowed(audioManager.unlocked);

		  } catch (error) {
			console.error("Erro ao carregar dados do usuário:", error);
		  }
                } else {
                  setUser(null);
                  storeCollectionsDataRef.current = {};
                  clientesDataRef.current = [];
                  setAvailableStores([]);
                  setStoreInfoMap({});
                  setStoreAlarmSnoozeMap({});
                  setSelectedStoreId(null);
				  setShowStoreManager(false);
                  setIsCreatingStore(false);
                  setCurrentPage('pagina-inicial');
           // Não remove mais 'audioUnlocked' do localStorage no logout
           stopAlarm(); // Garante que o alarme pare no logout
                }
                setAuthLoading(false);
          });

	  return () => unsubscribe();
        }, [stopAlarm, setCurrentPage]);

    useEffect(() => {
        let isMounted = true;

        const loadStoresForUser = async () => {
            if (!user) {
                if (isMounted) {
                    setAvailableStores([]);
                    setStoreInfoMap({});
                    setStoreAlarmSnoozeMap({});
                    storeCollectionsDataRef.current = {};
                    setSelectedStoreId(null);
                }
                return;
            }

            try {
                let storeIds = [];

                if (user.role === ROLE_OWNER && user.canAccessAllStores) {
                    const snapshot = await getDocs(collection(db, 'lojas'));
                    storeIds = snapshot.docs
                        .filter((docSnap) => !(docSnap.data() || {}).isDeleted)
                        .map((docSnap) => docSnap.id);
                } else {
                    storeIds = user.lojaIds && user.lojaIds.length ? user.lojaIds : (user.lojaId ? [user.lojaId] : []);
                }

                if (!isMounted) return;

                setAvailableStores(storeIds);

                setSelectedStoreId((prevSelected) => {
                    if (user.role === ROLE_OWNER) {
                        if (prevSelected === STORE_ALL_KEY) return STORE_ALL_KEY;
                        if (prevSelected && storeIds.includes(prevSelected)) return prevSelected;
                        return STORE_ALL_KEY;
                    }

                    const preferredStoreId = user.lojaId && storeIds.includes(user.lojaId)
                        ? user.lojaId
                        : (storeIds.length ? storeIds[0] : null);

                    if (prevSelected && storeIds.includes(prevSelected)) return prevSelected;
                    return preferredStoreId;
                });
            } catch (error) {
                console.error('Erro ao carregar lojas do usuário:', error);
                if (isMounted) {
                    setAvailableStores([]);
                }
            }
        };

        loadStoresForUser();

        return () => {
            isMounted = false;
        };
    }, [user, setSelectedStoreId]);

    useEffect(() => {
        let active = true;

        const loadStoreInfos = async () => {
            if (!availableStores.length) {
                if (active) {
                    setStoreInfoMap({});
                }
                return;
            }

            try {
                const entries = await Promise.all(availableStores.map(async (storeId) => {
                    try {
                        const empresaDocRef = doc(db, 'lojas', storeId, 'meuEspaco', 'empresa');
                        const empresaDocSnap = await getDoc(empresaDocRef);
                        if (empresaDocSnap.exists()) {
                            return [storeId, empresaDocSnap.data()];
                        }

                        const pontoDocSnap = await getDoc(doc(db, 'lojas', storeId, 'meuEspaco', 'ponto'));
                        if (pontoDocSnap.exists()) {
                            return [storeId, pontoDocSnap.data()];
                        }

                        const fallbackDocSnap = await getDoc(doc(db, 'lojas', storeId));
                        if (fallbackDocSnap.exists()) {
                            return [storeId, fallbackDocSnap.data()];
                        }
                    } catch (error) {
                        console.error(`Erro ao buscar info da loja ${storeId}:`, error);
                    }
                    return [storeId, {}];
                }));

                if (active) {
                    setStoreInfoMap(Object.fromEntries(entries));
                }
            } catch (error) {
                console.error('Erro geral ao buscar informações das lojas:', error);
                if (active) {
                    setStoreInfoMap({});
                }
            }
        };

        loadStoreInfos();

        return () => {
            active = false;
        };
    }, [availableStores]);

    useEffect(() => {
        let active = true;

        const loadStoreAlarmSnoozeSettings = async () => {
            if (!availableStores.length) {
                if (active) {
                    setStoreAlarmSnoozeMap({});
                }
                return;
            }

            try {
                const entries = await Promise.all(availableStores.map(async (storeId) => {
                    try {
                        const minutes = await getStoreAlarmSnoozeMinutes(storeId);
                        return [storeId, minutes];
                    } catch (error) {
                        console.error(`[App.js] Erro ao carregar soneca do alarme da loja ${storeId}:`, error);
                        return [storeId, DEFAULT_ALARM_SNOOZE_MINUTES];
                    }
                }));

                if (active) {
                    setStoreAlarmSnoozeMap(Object.fromEntries(entries));
                }
            } catch (error) {
                console.error('[App.js] Erro geral ao buscar configurações de soneca do alarme:', error);
                if (active) {
                    setStoreAlarmSnoozeMap({});
                }
            }
        };

        loadStoreAlarmSnoozeSettings();

        return () => {
            active = false;
        };
    }, [availableStores]);

    useEffect(() => {
        warnIfHostOutsideAllowlist();
        logAuthRuntimeDiagnostics({
            method: 'AUTH_BOOTSTRAP',
            firebasePublicConfig,
        });
    }, []);

    const handleLogin = async () => {
        if (isAuthInProgress) return;
        setLoginError('');

        if (isTenantSubdomain) {
            setLoginError('Você será redirecionado para o login seguro em apfood.com.br.');
            prepareCentralizedLogin();
            return;
        }

        setIsAuthInProgress(true);
        sessionStorage.setItem(AUTH_PROGRESS_KEY, 'true');
        logAuthRuntimeDiagnostics({ method: 'EMAIL_PASSWORD', firebasePublicConfig });

        try {
            await signInWithEmailAndPassword(auth, email, password);
            setShowLogin(false);
            setEmail('');
            setPassword('');
            setCurrentPage('dashboard');
            finalizeCentralizedLoginReturn();
        } catch (error) {
            const friendlyMessage = mapAuthErrorToUserMessage(error);
            logAuthRuntimeDiagnostics({ method: 'EMAIL_PASSWORD', firebasePublicConfig, error });
            setLoginError(friendlyMessage);
        } finally {
            sessionStorage.removeItem(AUTH_PROGRESS_KEY);
            setIsAuthInProgress(false);
        }
    };

    const handleGoogleSignIn = async () => {
        if (isAuthInProgress) return;
        setLoginError('');

        if (isTenantSubdomain) {
            setLoginError('Você será redirecionado para o login seguro em apfood.com.br.');
            prepareCentralizedLogin();
            return;
        }

        const provider = new GoogleAuthProvider();

        setIsAuthInProgress(true);
        sessionStorage.setItem(AUTH_PROGRESS_KEY, 'true');
        logAuthRuntimeDiagnostics({ method: 'GOOGLE_POPUP', firebasePublicConfig });

        try {
            await setPersistence(auth, browserLocalPersistence);
            await signInWithPopup(auth, provider);
            sessionStorage.removeItem(GOOGLE_AUTH_FLOW_KEY);
            setShowLogin(false);
            setCurrentPage('dashboard');
            finalizeCentralizedLoginReturn();
        } catch (error) {
            const code = error?.code || '';

            if (isPopupFallbackError(code)) {
                try {
                    sessionStorage.setItem(GOOGLE_AUTH_FLOW_KEY, 'true');
                    logAuthRuntimeDiagnostics({ method: 'GOOGLE_REDIRECT', firebasePublicConfig });
                    await signInWithRedirect(auth, provider);
                    return;
                } catch (redirectError) {
                    logAuthRuntimeDiagnostics({ method: 'GOOGLE_REDIRECT', firebasePublicConfig, error: redirectError });
                    setLoginError(mapAuthErrorToUserMessage(redirectError));
                }
            } else {
                logAuthRuntimeDiagnostics({ method: 'GOOGLE_POPUP', firebasePublicConfig, error });
                setLoginError(mapAuthErrorToUserMessage(error));
            }
        } finally {
            sessionStorage.removeItem(AUTH_PROGRESS_KEY);
            setIsAuthInProgress(false);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const returnUrl = params.get(AUTH_RETURN_URL_QUERY_PARAM);
        const tenantSlug = params.get(AUTH_TENANT_SLUG_QUERY_PARAM);

        if (returnUrl) {
            sessionStorage.setItem(AUTH_RETURN_URL_KEY, returnUrl);
        }

        if (tenantSlug) {
            sessionStorage.setItem(AUTH_TENANT_SLUG_KEY, tenantSlug);
        }
    }, []);

    useEffect(() => {
        let active = true;

        const validateGoogleRedirectResult = async () => {
            try {
                const result = await getRedirectResult(auth);
                const googleUser = result?.user;

                sessionStorage.removeItem(AUTH_PROGRESS_KEY);
                setIsAuthInProgress(false);

                if (!googleUser?.email) {
                    return;
                }

                sessionStorage.removeItem(GOOGLE_AUTH_FLOW_KEY);

                if (active) {
                    setShowLogin(false);
                    setCurrentPage('dashboard');
                }

                finalizeCentralizedLoginReturn();
            } catch (error) {
                sessionStorage.removeItem(AUTH_PROGRESS_KEY);
                setIsAuthInProgress(false);

                if (error?.code === 'auth/cancelled-popup-request') {
                    console.warn('[AUTH][GOOGLE_REDIRECT] Requisição cancelada por concorrência de popup:', error);
                    return;
                }

                logAuthRuntimeDiagnostics({ method: 'GOOGLE_REDIRECT', firebasePublicConfig, error });
                if (active) {
                    setLoginError(mapAuthErrorToUserMessage(error));
                }
            }
        };

        validateGoogleRedirectResult();

        return () => {
            active = false;
        };
    }, [finalizeCentralizedLoginReturn]);
    
    const handlePasswordReset = async () => {
        if (!passwordResetEmail) {
            setPasswordResetMessage({ text: 'Por favor, insira seu email.', type: 'error' });
            return;
        }
        setPasswordResetMessage({ text: 'Enviando email...', type: 'loading' });
        try {
            await sendPasswordResetEmail(auth, passwordResetEmail);
            setPasswordResetMessage({ text: 'Email de recuperação enviado! Verifique sua caixa de entrada e spam.', type: 'success' });
        } catch (error) {
            if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-email') {
                setPasswordResetMessage({ text: 'Email não encontrado. Verifique o email digitado.', type: 'error' });
            } else {
                 console.error("Erro ao resetar senha:", error);
                setPasswordResetMessage({ text: 'Ocorreu um erro. Tente novamente.', type: 'error' });
            }
        }
    };


  const handleLogout = async () => { 
      stopAlarm(); // Garante que o alarme pare
      await signOut(auth); 
      // O useEffect do onAuthStateChanged agora cuida de resetar a página
  };

  const allMenuItems = [
    { id: 'pagina-inicial', permission: 'pagina-inicial', label: 'Página Inicial', icon: Home, roles: [ROLE_OWNER, ROLE_MANAGER, ROLE_ATTENDANT, null] },
    { id: 'dashboard', permission: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: [ROLE_OWNER, ROLE_MANAGER] },
    { id: 'clientes', permission: 'clientes', label: 'Clientes', icon: Users, roles: [ROLE_OWNER, ROLE_MANAGER, ROLE_ATTENDANT] },
    { id: 'pedidos', permission: 'pedidos', label: 'Pedidos', icon: ShoppingCart, roles: [ROLE_OWNER, ROLE_MANAGER, ROLE_ATTENDANT] },
    { id: 'produtos', permission: 'produtos', label: 'Produtos', icon: Package, roles: [ROLE_OWNER, ROLE_MANAGER] },
    { id: 'agenda', permission: 'agenda', label: 'Agenda', icon: Calendar, roles: [ROLE_OWNER, ROLE_MANAGER, ROLE_ATTENDANT] },
    { id: 'fornecedores', permission: 'fornecedores', label: 'Fornecedores/Estoque', icon: Truck, roles: [ROLE_OWNER, ROLE_MANAGER] },
    { id: 'relatorios', permission: 'relatorios', label: 'Relatórios', icon: BarChart3, roles: [ROLE_OWNER, ROLE_MANAGER] },
    { id: 'meu-espaco', permission: 'meu-espaco', label: 'Meu Espaço', icon: Clock, roles: [ROLE_OWNER, ROLE_MANAGER, ROLE_ATTENDANT] },
    { id: 'financeiro', permission: 'financeiro', label: 'Financeiro', icon: DollarSign, roles: [ROLE_OWNER, ROLE_MANAGER] },
    { id: 'configuracoes', permission: 'configuracoes', label: 'Configurações', icon: Settings, roles: [ROLE_OWNER, ROLE_MANAGER] },
  ];
  const currentUserRole = user ? user.role : null;
  const menuItems = useMemo(() => {
    if (!user) {
      return allMenuItems.filter(item => item.roles.includes(null));
    }
    if (currentUserRole === ROLE_OWNER && !user?.hasCustomProfile) {
      return allMenuItems;
    }

    const permissionKeyFor = (item) => item.permission || item.id;
    const customPermissions = user.customPermissions;
    const normalizedPermissions = sanitizePermissions(user.permissions, currentUserRole);

    return allMenuItems.filter(item => {
      const permissionKey = permissionKeyFor(item);

      if (customPermissions) {
        return Boolean(customPermissions[permissionKey]);
      }

      if (item.roles?.includes(currentUserRole)) {
        return true;
      }

      return Boolean(normalizedPermissions[permissionKey]);
    });
  }, [allMenuItems, currentUserRole, user]);
  
  // Componentes de Páginas
  const PaginaInicial = () => <HomePage />;

  const MeuEspaco = ({ user, resolveActiveStoreForWrite, currentStoreIdForDisplay }) => {
    const now = new Date();
    const initialMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const [selectedMonth, setSelectedMonth] = useState(initialMonth);
    const [companyInfo, setCompanyInfo] = useState({
      nome: '',
      endereco: '',
      cnpj: '',
      atividade: '',
      horarioTrabalho: '',
      competencia: `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`,
      gestorResponsavel: ''
    });
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companySaving, setCompanySaving] = useState(false);
    const [records, setRecords] = useState([]);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerMessage, setRegisterMessage] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState('self');
    const [employees, setEmployees] = useState([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editForm, setEditForm] = useState({ horaEntrada: '', horaSaida: '', horaAlmocoSaida: '', horaAlmocoRetorno: '', irregularidade: '', qtde: '', justificativa: '' });
    const [savingEdit, setSavingEdit] = useState(false);
    const [todayRecordData, setTodayRecordData] = useState(null);

    const isManager = user ? [ROLE_OWNER, ROLE_MANAGER].includes(user.role) : false;
    const userId = user?.auth?.uid || '';
    const userName = user?.auth?.displayName || user?.auth?.email || 'Gestor';

    const competenciaLabel = useMemo(() => {
      const [year, month] = selectedMonth.split('-');
      return `${month}/${year}`;
    }, [selectedMonth]);

    useEffect(() => {
      if (isManager) {
        setSelectedEmployee('all');
      } else if (userId) {
        setSelectedEmployee(userId);
      }
    }, [isManager, userId]);

    useEffect(() => {
      setCompanyInfo((prev) => ({ ...prev, competencia: competenciaLabel }));
    }, [competenciaLabel]);

    useEffect(() => {
      if (!currentStoreIdForDisplay || currentStoreIdForDisplay === STORE_ALL_KEY) {
        setCompanyInfo((prev) => ({ ...prev, nome: '', endereco: '', cnpj: '', atividade: '', horarioTrabalho: '', gestorResponsavel: '' }));
        return;
      }

      setCompanyLoading(true);
      const companyDoc = doc(db, 'lojas', currentStoreIdForDisplay, 'meuEspaco', 'empresa');
      const unsubscribe = onSnapshot(companyDoc, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setCompanyInfo((prev) => ({
            ...prev,
            ...data,
            competencia: competenciaLabel,
          }));
        } else {
          setCompanyInfo((prev) => ({ ...prev, nome: '', endereco: '', cnpj: '', atividade: '', horarioTrabalho: '', gestorResponsavel: '' }));
        }
        setCompanyLoading(false);
      }, (error) => {
        console.error('Erro ao carregar dados da empresa', error);
        setCompanyLoading(false);
      });

      return () => unsubscribe();
    }, [currentStoreIdForDisplay, competenciaLabel]);

    useEffect(() => {
      if (!isManager || !currentStoreIdForDisplay || currentStoreIdForDisplay === STORE_ALL_KEY) {
        setEmployees([]);
        setEmployeesLoading(false);
        return;
      }

      setEmployeesLoading(true);
      const fetchEmployees = async () => {
        try {
          const snap = await getDocs(collection(db, 'users'));
          const list = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          const filtered = list.filter(item => {
            const lojas = Array.isArray(item.lojaIds) ? item.lojaIds : (item.lojaId ? [item.lojaId] : []);
            return lojas.includes(currentStoreIdForDisplay);
          });
          setEmployees(filtered);
        } catch (error) {
          console.error('Erro ao buscar colaboradores', error);
        } finally {
          setEmployeesLoading(false);
        }
      };

      fetchEmployees();
    }, [isManager, currentStoreIdForDisplay]);

    useEffect(() => {
      if (!currentStoreIdForDisplay || currentStoreIdForDisplay === STORE_ALL_KEY) {
        setRecords([]);
        return;
      }

      setRecordsLoading(true);
      const pontosRef = collection(db, 'lojas', currentStoreIdForDisplay, 'pontos');
      const pontosQuery = query(pontosRef, where('competencia', '==', selectedMonth));
      const unsubscribe = onSnapshot(pontosQuery, (snapshot) => {
        const data = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        setRecords(data);
        setRecordsLoading(false);
      }, (error) => {
        console.error('Erro ao carregar registros de ponto', error);
        setRecords([]);
        setRecordsLoading(false);
      });

      return () => unsubscribe();
    }, [currentStoreIdForDisplay, selectedMonth]);

    useEffect(() => {
      if (!currentStoreIdForDisplay || currentStoreIdForDisplay === STORE_ALL_KEY || !userId) {
        setTodayRecordData(null);
        return;
      }
      const today = new Date();
      const dayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const competenciaKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const pontosRef = collection(db, 'lojas', currentStoreIdForDisplay, 'pontos');
      const todayQuery = query(
        pontosRef,
        where('funcionarioId', '==', userId),
        where('dia', '==', dayKey),
        where('competencia', '==', competenciaKey),
        limit(1)
      );
      const unsubscribe = onSnapshot(todayQuery, (snapshot) => {
        if (!snapshot.empty) {
          setTodayRecordData({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
        } else {
          setTodayRecordData(null);
        }
      }, () => setTodayRecordData(null));
      return () => unsubscribe();
    }, [currentStoreIdForDisplay, userId]);

    const getDayInfo = (record) => {
      if (record.dia) {
        const [year, month, day] = record.dia.split('-').map(Number);
        if (year && month && day) {
          return new Date(year, month - 1, day);
        }
      }
      if (record.data && typeof record.data.toDate === 'function') {
        return record.data.toDate();
      }
      return null;
    };

    const formatTime = (value) => value || '--:--';

    const formatMinutesToLabel = (minutes) => {
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hrs}:${String(mins).padStart(2, '0')}`;
    };

    const calculateWorkSummary = (registro) => {
      if (!registro?.horaEntrada || !registro?.horaSaida) {
        return { workedLabel: '-', irregularidade: '-', workedMinutes: null };
      }

      const parseTime = (time) => {
        const [hours, minutes] = (time || '').split(':').map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        return hours * 60 + minutes;
      };

      const entrada = parseTime(registro.horaEntrada);
      const saida = parseTime(registro.horaSaida);

      if (entrada === null || saida === null) {
        return { workedLabel: '-', irregularidade: '-', workedMinutes: null };
      }

      let workedMinutes = saida - entrada;

      const almocoSaida = parseTime(registro.horaAlmocoSaida);
      const almocoRetorno = parseTime(registro.horaAlmocoRetorno);
      if (almocoSaida !== null && almocoRetorno !== null) {
        workedMinutes -= almocoRetorno - almocoSaida;
      }

      if (Number.isNaN(workedMinutes) || workedMinutes <= 0) {
        return { workedLabel: '-', irregularidade: '-', workedMinutes: null };
      }

      const workedLabel = formatMinutesToLabel(workedMinutes);

      const date = getDayInfo(registro);
      const dayOfWeek = date ? date.getDay() : null; // 0 = domingo
      const expectedMinutes = dayOfWeek !== null && dayOfWeek >= 1 && dayOfWeek <= 5 ? 8 * 60 : 0;

      const diff = workedMinutes - expectedMinutes;
      const irregularidade = dayOfWeek === null
        ? '-'
        : diff === 0
          ? '0:00'
          : `${diff > 0 ? '+' : '-'}${formatMinutesToLabel(Math.abs(diff))}`;

      return { workedLabel, irregularidade, workedMinutes };
    };

    const getWorkedTime = (registro) => calculateWorkSummary(registro).workedLabel;

    const getRecordDateTime = (record) => {
      if (record?.data && typeof record.data.toDate === 'function') {
        return record.data.toDate();
      }
      const baseDate = getDayInfo(record);
      if (!baseDate) return null;
      const timeString = record.horaEntrada || record.horaSaida || record.horaAlmocoRetorno || record.horaAlmocoSaida;
      if (timeString) {
        const [hours, minutes] = timeString.split(':').map(Number);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes);
        }
      }
      return baseDate;
    };

    const filteredRecords = useMemo(() => {
      const sorted = [...records].sort((a, b) => {
        const dateA = getRecordDateTime(a);
        const dateB = getRecordDateTime(b);
        if (dateA && dateB) {
          const diff = dateA - dateB;
          if (diff !== 0) return diff;
        }
        if (!dateA && dateB) return -1;
        if (dateA && !dateB) return 1;
        const createdA = a?.createdAt && typeof a.createdAt.toDate === 'function' ? a.createdAt.toDate() : null;
        const createdB = b?.createdAt && typeof b.createdAt.toDate === 'function' ? b.createdAt.toDate() : null;
        if (createdA && createdB) return createdA - createdB;
        if (!createdA && createdB) return -1;
        if (createdA && !createdB) return 1;
        return 0;
      });
      if (isManager) {
        if (selectedEmployee === 'all') return sorted;
        return sorted.filter(item => item.funcionarioId === selectedEmployee);
      }
      return sorted.filter(item => item.funcionarioId === userId);
    }, [records, isManager, selectedEmployee, userId]);

    const todayRecord = todayRecordData;

    const requestLocation = () => new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Seu navegador não suporta geolocalização.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
    });

    const getAddressFromCoordinates = async ({ latitude, longitude }) => {
      if (typeof latitude !== 'number' || typeof longitude !== 'number') return '';
      const params = new URLSearchParams({
        format: 'jsonv2',
        lat: String(latitude),
        lon: String(longitude),
        'accept-language': 'pt-BR'
      });

      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params.toString()}`, {
          headers: {
            'Accept-Language': 'pt-BR'
          }
        });
        if (!response.ok) {
          throw new Error('Falha ao buscar endereço.');
        }
        const data = await response.json();
        if (data?.display_name) return data.display_name;
        if (data?.address) {
          const { road, neighbourhood, suburb, city, town, state, postcode } = data.address;
          const parts = [road, neighbourhood || suburb, city || town, state, postcode];
          const filtered = parts.filter(Boolean);
          if (filtered.length) {
            return filtered.join(', ');
          }
        }
        return '';
      } catch (error) {
        console.error('Erro ao obter endereço da localização', error);
        return '';
      }
    };

    const formatTimeString = (dateObj) => dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    const handleRegisterPoint = async (type) => {
      try {
        setRegisterLoading(true);
        setRegisterMessage(null);
        const position = await requestLocation();
        const coords = {
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
          capturedAt: new Date().toISOString()
        };
        const capturedAddress = await getAddressFromCoordinates(coords);
        const storeId = resolveActiveStoreForWrite();
        const pontosRef = collection(db, 'lojas', storeId, 'pontos');
        const currentDate = new Date();
        const dayKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        const competenciaKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        if (selectedMonth !== competenciaKey) {
          setSelectedMonth(competenciaKey);
        }
        const existingQuery = query(
          pontosRef,
          where('funcionarioId', '==', userId),
          where('dia', '==', dayKey),
          where('competencia', '==', competenciaKey),
          limit(1)
        );
        const snapshot = await getDocs(existingQuery);
        const nowTime = formatTimeString(new Date());
        const payload = {
          entrada: {
            horaEntrada: nowTime,
            localizacaoEntrada: coords,
            localizacaoEntradaEndereco: capturedAddress || ''
          },
          almoco_inicio: {
            horaAlmocoSaida: nowTime
          },
          almoco_fim: {
            horaAlmocoRetorno: nowTime
          },
          saida: {
            horaSaida: nowTime,
            localizacaoSaida: coords,
            localizacaoSaidaEndereco: capturedAddress || ''
          }
        }[type];

        if (!payload) {
          throw new Error('Tipo de registro inválido.');
        }

        const baseData = {
          funcionarioId: userId,
          funcionarioNome: userName,
          dia: dayKey,
          data: Timestamp.now(),
          horaEntrada: '',
          horaSaida: '',
          horaAlmocoSaida: '',
          horaAlmocoRetorno: '',
          localizacaoEntrada: null,
          localizacaoEntradaEndereco: '',
          localizacaoSaida: null,
          localizacaoSaidaEndereco: '',
          irregularidade: '',
          qtde: '',
          justificativa: '',
          competencia: competenciaKey,
          empresaId: currentStoreIdForDisplay,
          historicoAlteracoes: [],
          createdAt: serverTimestamp()
        };

        if (snapshot.empty) {
          const recordToSave = { ...baseData, ...payload };
          const summary = calculateWorkSummary(recordToSave);
          await addDoc(pontosRef, {
            ...recordToSave,
            irregularidade: summary.irregularidade !== '-' ? summary.irregularidade : '',
            qtde: summary.workedLabel !== '-' ? summary.workedLabel : ''
          });
        } else {
          const existingData = snapshot.docs[0].data();
          const docRef = snapshot.docs[0].ref;
          const updatedRecord = { ...existingData, ...payload };
          const summary = calculateWorkSummary(updatedRecord);
          await updateDoc(docRef, {
            ...payload,
            irregularidade: summary.irregularidade !== '-' ? summary.irregularidade : '',
            qtde: summary.workedLabel !== '-' ? summary.workedLabel : '',
            updatedAt: serverTimestamp()
          });
        }
        const actionMap = {
          entrada: 'entrada',
          almoco_inicio: 'início do almoço',
          almoco_fim: 'retorno do almoço',
          saida: 'saída'
        };
        setRegisterMessage({ type: 'success', text: `Ponto de ${actionMap[type]} registrado com sucesso!` });
      } catch (error) {
        console.error('Erro ao registrar ponto', error);
        setRegisterMessage({ type: 'error', text: error.message || 'Não foi possível registrar o ponto.' });
      } finally {
        setRegisterLoading(false);
      }
    };

    const handleSaveCompanyInfo = async (event) => {
      event.preventDefault();
      if (!isManager) return;
      if (!currentStoreIdForDisplay || currentStoreIdForDisplay === STORE_ALL_KEY) {
        alert('Selecione uma loja para salvar as informações.');
        return;
      }

      try {
        setCompanySaving(true);
        const storeId = resolveActiveStoreForWrite();
        const companyDoc = doc(db, 'lojas', storeId, 'meuEspaco', 'empresa');
        await setDoc(companyDoc, {
          nome: companyInfo.nome || '',
          endereco: companyInfo.endereco || '',
          cnpj: companyInfo.cnpj || '',
          atividade: companyInfo.atividade || '',
          horarioTrabalho: companyInfo.horarioTrabalho || '',
          competencia: competenciaLabel,
          gestorResponsavel: companyInfo.gestorResponsavel || userName,
          gestorId: userId,
          atualizadoEm: serverTimestamp()
        }, { merge: true });
        setRegisterMessage({ type: 'success', text: 'Informações da empresa salvas com sucesso!' });
      } catch (error) {
        console.error('Erro ao salvar dados da empresa', error);
        setRegisterMessage({ type: 'error', text: 'Não foi possível salvar as informações da empresa.' });
      } finally {
        setCompanySaving(false);
      }
    };

    const openEditModal = (record) => {
      const summary = calculateWorkSummary(record);
      setEditingRecord(record);
      setEditForm({
        horaEntrada: record.horaEntrada || '',
        horaSaida: record.horaSaida || '',
        horaAlmocoSaida: record.horaAlmocoSaida || '',
        horaAlmocoRetorno: record.horaAlmocoRetorno || '',
        irregularidade: summary.irregularidade !== '-' ? summary.irregularidade : '',
        qtde: summary.workedLabel !== '-' ? summary.workedLabel : '',
        justificativa: record.justificativa || ''
      });
    };

    const handleSaveEdit = async () => {
      if (!editingRecord) return;
      try {
        setSavingEdit(true);
        const storeId = resolveActiveStoreForWrite();
        const recordRef = doc(db, 'lojas', storeId, 'pontos', editingRecord.id);
        const nowDate = new Date();
        const summary = calculateWorkSummary({ ...editingRecord, ...editForm });
        await updateDoc(recordRef, {
          horaEntrada: editForm.horaEntrada || '',
          horaSaida: editForm.horaSaida || '',
          horaAlmocoSaida: editForm.horaAlmocoSaida || '',
          horaAlmocoRetorno: editForm.horaAlmocoRetorno || '',
          irregularidade: summary.irregularidade !== '-' ? summary.irregularidade : '',
          qtde: summary.workedLabel !== '-' ? summary.workedLabel : '',
          justificativa: editForm.justificativa || '',
          gestorId: userId,
          dataAjuste: serverTimestamp(),
          historicoAlteracoes: arrayUnion({
            data: nowDate.toISOString(),
            gestor: userName,
            alteracoes: { ...editForm, irregularidade: summary.irregularidade, qtde: summary.workedLabel }
          })
        });
        setRegisterMessage({ type: 'success', text: 'Registro de ponto atualizado.' });
        setEditingRecord(null);
      } catch (error) {
        console.error('Erro ao atualizar registro', error);
        setRegisterMessage({ type: 'error', text: 'Não foi possível atualizar o registro.' });
      } finally {
        setSavingEdit(false);
      }
    };

    if (!currentStoreIdForDisplay || currentStoreIdForDisplay === STORE_ALL_KEY) {
      return (
        <div className="p-4 md:p-6">
          <div className="bg-white rounded-2xl shadow p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Selecione uma loja</h2>
            <p className="text-gray-600">Para acessar o Meu Espaço, escolha uma loja específica no topo do sistema.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/20 to-rose-50/20 min-h-screen">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-gray-800">Meu Espaço</h1>
          <p className="text-gray-600">Registre seu ponto e acompanhe os horários do mês atual.</p>
        </div>

        {registerMessage && (
          <div className={`p-4 rounded-2xl ${registerMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
            {registerMessage.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Registro de ponto</h2>
                <p className="text-gray-500 text-sm">A localização é capturada automaticamente.</p>
              </div>
              <Clock className="w-10 h-10 text-pink-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={() => handleRegisterPoint('entrada')}
                disabled={registerLoading}
              >
                Registrar entrada
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleRegisterPoint('saida')}
                disabled={registerLoading}
              >
                Registrar saída
              </Button>
              <Button
                variant="outline"
                onClick={() => handleRegisterPoint('almoco_inicio')}
                disabled={registerLoading || !todayRecord?.horaEntrada}
              >
                Registrar início do almoço
              </Button>
              <Button
                variant="outline"
                onClick={() => handleRegisterPoint('almoco_fim')}
                disabled={registerLoading || !todayRecord?.horaAlmocoSaida}
              >
                Registrar retorno do almoço
              </Button>
            </div>
            {todayRecord && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-gray-50 rounded-xl p-4">
                <div>
                  <p className="text-gray-500">Entrada</p>
                  <p className="font-semibold text-gray-800">{formatTime(todayRecord.horaEntrada)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Saída</p>
                  <p className="font-semibold text-gray-800">{formatTime(todayRecord.horaSaida)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Início do almoço</p>
                  <p className="font-semibold text-gray-800">{formatTime(todayRecord.horaAlmocoSaida)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Retorno do almoço</p>
                  <p className="font-semibold text-gray-800">{formatTime(todayRecord.horaAlmocoRetorno)}</p>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSaveCompanyInfo} className="bg-white rounded-2xl shadow p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Informações da empresa</h2>
                <p className="text-gray-500 text-sm">Dados exibidos no cabeçalho do relatório.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Nome da empresa" value={companyInfo.nome} onChange={(e) => setCompanyInfo({ ...companyInfo, nome: e.target.value })} disabled={!isManager} required={isManager} />
              <Input label="CNPJ" value={companyInfo.cnpj} onChange={(e) => setCompanyInfo({ ...companyInfo, cnpj: e.target.value })} disabled={!isManager} />
              <Input label="Endereço" value={companyInfo.endereco} onChange={(e) => setCompanyInfo({ ...companyInfo, endereco: e.target.value })} disabled={!isManager} />
              <Input label="Atividade econômica" value={companyInfo.atividade} onChange={(e) => setCompanyInfo({ ...companyInfo, atividade: e.target.value })} disabled={!isManager} />
              <Input label="Horário de trabalho" value={companyInfo.horarioTrabalho} onChange={(e) => setCompanyInfo({ ...companyInfo, horarioTrabalho: e.target.value })} disabled={!isManager} />
              <Input label="Competência" value={competenciaLabel} disabled readOnly />
              <Input label="Gestor responsável" value={companyInfo.gestorResponsavel || userName} onChange={(e) => setCompanyInfo({ ...companyInfo, gestorResponsavel: e.target.value })} disabled={!isManager} />
            </div>
            {isManager && (
              <div className="flex justify-end">
                <Button type="submit" disabled={companyLoading || companySaving}>{companySaving ? 'Salvando...' : 'Salvar informações'}</Button>
              </div>
            )}
          </form>
        </div>

        <div className="bg-white rounded-2xl shadow p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Registros do mês</h2>
                <p className="text-gray-500 text-sm">Visualização automática do mês selecionado.</p>
              </div>
              <span className="inline-flex items-center rounded-full bg-pink-100 text-pink-700 px-3 py-1 text-sm font-semibold">
                Registros ({filteredRecords.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <Input
                type="month"
                label="Mês"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="min-w-[160px]"
              />
              {isManager && (
                <Select label="Colaborador" value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} disabled={employeesLoading} className="min-w-[200px]">
                  {employeesLoading ? (
                    <option>Carregando...</option>
                  ) : (
                    <>
                      <option value="all">Todos</option>
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>{employee.nome || employee.email || employee.id}</option>
                      ))}
                    </>
                  )}
                </Select>
              )}
            </div>
          </div>

          {recordsLoading ? (
            <div className="py-10 text-center text-gray-500">Carregando registros...</div>
          ) : filteredRecords.length === 0 ? (
            <div className="py-10 text-center text-gray-500">Nenhum registro encontrado para o período selecionado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-3 px-4">Funcionário</th>
                    <th className="py-3 px-4">Dia da semana</th>
                    <th className="py-3 px-4">Dia do Mês</th>
                    <th className="py-3 px-4">Entrada</th>
                    <th className="py-3 px-4">Saída almoço</th>
                    <th className="py-3 px-4">Retorno almoço</th>
                    <th className="py-3 px-4">Saída</th>
                    <th className="py-3 px-4">Irregularidade</th>
                    <th className="py-3 px-4">Qtde</th>
                    <th className="py-3 px-4">Justificativa</th>
                    {isManager && <th className="py-3 px-4">Localização</th>}
                    {isManager && <th className="py-3 px-4">Ações</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRecords.map((registro) => {
                    const date = getDayInfo(registro);
                    const diaSemana = date ? date.toLocaleDateString('pt-BR', { weekday: 'long' }) : '-';
                    const diaMes = date ? String(date.getDate()).padStart(2, '0') : '-';
                    const workSummary = calculateWorkSummary(registro);
                    return (
                      <tr key={registro.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">{registro.funcionarioNome || '-'}</td>
                        <td className="py-3 px-4 capitalize">{diaSemana}</td>
                        <td className="py-3 px-4">{diaMes}</td>
                        <td className="py-3 px-4 font-semibold">{formatTime(registro.horaEntrada)}</td>
                        <td className="py-3 px-4 font-semibold">{formatTime(registro.horaAlmocoSaida)}</td>
                        <td className="py-3 px-4 font-semibold">{formatTime(registro.horaAlmocoRetorno)}</td>
                        <td className="py-3 px-4 font-semibold">{formatTime(registro.horaSaida)}</td>
                        <td className="py-3 px-4">{workSummary.irregularidade}</td>
                        <td className="py-3 px-4">{workSummary.workedLabel}</td>
                        <td className="py-3 px-4 max-w-xs">{registro.justificativa || '-'}</td>
                        {isManager && (
                          <td className="py-3 px-4">
                            <div className="space-y-3 text-xs">
                              {registro.localizacaoEntrada && (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1 font-semibold text-pink-600">
                                    <MapPin className="w-4 h-4" /> Entrada
                                  </div>
                                  <p className="text-gray-600">
                                    {registro.localizacaoEntradaEndereco || 'Endereço não disponível'}
                                  </p>
                                  <a
                                    href={`https://maps.google.com/?q=${registro.localizacaoEntrada.latitude},${registro.localizacaoEntrada.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-pink-600 hover:underline"
                                  >
                                    Ver no mapa
                                  </a>
                                </div>
                              )}
                              {registro.localizacaoSaida && (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1 font-semibold text-emerald-600">
                                    <MapPin className="w-4 h-4" /> Saída
                                  </div>
                                  <p className="text-gray-600">
                                    {registro.localizacaoSaidaEndereco || 'Endereço não disponível'}
                                  </p>
                                  <a
                                    href={`https://maps.google.com/?q=${registro.localizacaoSaida.latitude},${registro.localizacaoSaida.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
                                  >
                                    Ver no mapa
                                  </a>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        {isManager && (
                          <td className="py-3 px-4">
                            <Button size="sm" variant="secondary" onClick={() => openEditModal(registro)}>Editar</Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Modal isOpen={Boolean(editingRecord)} onClose={() => setEditingRecord(null)} title="Editar registro" size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Hora de entrada" type="time" value={editForm.horaEntrada} onChange={(e) => setEditForm({ ...editForm, horaEntrada: e.target.value })} />
              <Input label="Hora de saída" type="time" value={editForm.horaSaida} onChange={(e) => setEditForm({ ...editForm, horaSaida: e.target.value })} />
              <Input label="Saída para almoço" type="time" value={editForm.horaAlmocoSaida} onChange={(e) => setEditForm({ ...editForm, horaAlmocoSaida: e.target.value })} />
              <Input label="Retorno do almoço" type="time" value={editForm.horaAlmocoRetorno} onChange={(e) => setEditForm({ ...editForm, horaAlmocoRetorno: e.target.value })} />
              <Input label="Irregularidade" value={editForm.irregularidade} onChange={(e) => setEditForm({ ...editForm, irregularidade: e.target.value })} />
              <Input label="Quantidade (horas)" value={editForm.qtde} onChange={(e) => setEditForm({ ...editForm, qtde: e.target.value })} />
            </div>
            <Textarea label="Justificativa" value={editForm.justificativa} onChange={(e) => setEditForm({ ...editForm, justificativa: e.target.value })} />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setEditingRecord(null)}>Cancelar</Button>
              <Button onClick={handleSaveEdit} disabled={savingEdit}>{savingEdit ? 'Salvando...' : 'Salvar ajustes'}</Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  };

  // --- CORREÇÃO: Props do Dashboard atualizadas ---
  const Dashboard = ({handleStopAndSnoozeAlarm, isAlarmPlaying, isAlarmSnoozed, hasNewPendingOrders, snoozeEndTime}) => {
    const { pedidos, clientes } = data;
    
    // --- CORREÇÃO: Lógica de display da soneca movida para dentro do Dashboard ---
    const [snoozeDisplay, setSnoozeDisplay] = useState('');
    const snoozeDisplayTimerRef = useRef(null);
    // --- CORREÇÃO: State local para audioUnlocked (lido do manager) ---
    const [audioAllowed, setAudioAllowed] = useState(audioManager.unlocked);

    // Efeito para observar mudanças no estado 'unlocked' do AudioManager
    useEffect(() => {
        const checkAudioState = () => {
             // Força a atualização do estado local lendo a propriedade do manager
            setAudioAllowed(audioManager.unlocked);
        };
        // Checa o estado a cada segundo
        const interval = setInterval(checkAudioState, 1000);
        checkAudioState(); // Checa imediatamente na montagem
        return () => clearInterval(interval); // Limpa o intervalo ao desmontar
    }, []); // Roda apenas uma vez no mount


    useEffect(() => {
        if (isAlarmSnoozed && snoozeEndTime) {
            // Limpa timer anterior, se houver
            if (snoozeDisplayTimerRef.current) clearInterval(snoozeDisplayTimerRef.current);
            
            // Função para atualizar o display
            const updateDisplay = () => {
                const now = new Date().getTime();
                const remaining = snoozeEndTime - now;

                if (remaining > 0) {
                    const minutes = Math.floor(remaining / 60000);
                    const seconds = Math.floor((remaining % 60000) / 1000);
                    setSnoozeDisplay(`${minutes}:${seconds.toString().padStart(2, '0')}`);
                } else {
                    // Timer expirou, limpa o display e o timer
                    setSnoozeDisplay('');
                    if (snoozeDisplayTimerRef.current) clearInterval(snoozeDisplayTimerRef.current);
                }
            };
            
            updateDisplay(); // Atualiza imediatamente
            snoozeDisplayTimerRef.current = setInterval(updateDisplay, 1000); // Atualiza a cada segundo
        } else {
            // Garante que, se não estiver em soneca, o timer e o display sejam limpos
            if (snoozeDisplayTimerRef.current) clearInterval(snoozeDisplayTimerRef.current);
            setSnoozeDisplay('');
        }

        // Função de limpeza para o timer do display
        return () => {
            if (snoozeDisplayTimerRef.current) clearInterval(snoozeDisplayTimerRef.current);
        };
    }, [isAlarmSnoozed, snoozeEndTime]); // Roda sempre que o estado de soneca ou o tempo final mudarem


    const today = new Date(); 
    today.setHours(0, 0, 0, 0);
    const lastSunday = new Date(); 
    lastSunday.setDate(lastSunday.getDate() - lastSunday.getDay()); 
    lastSunday.setHours(0, 0, 0, 0);
    const vendasHoje = (pedidos || []).filter(pedido => { const pedidoDate = getJSDate(pedido.createdAt); if (!pedidoDate) return false; pedidoDate.setHours(0,0,0,0); return pedidoDate.getTime() === today.getTime() && pedido.status === 'Finalizado'; }).reduce((acc, pedido) => acc + (pedido.total || 0), 0);
    const numVendasHoje = (pedidos || []).filter(pedido => { const pedidoDate = getJSDate(pedido.createdAt); if (!pedidoDate) return false; pedidoDate.setHours(0,0,0,0); return pedidoDate.getTime() === today.getTime() && pedido.status === 'Finalizado'; }).length;
    const vendasSemana = (pedidos || []).filter(pedido => { const pedidoDate = getJSDate(pedido.createdAt); if (!pedidoDate) return false; return pedidoDate >= lastSunday && pedidoDate <= new Date() && pedido.status === 'Finalizado'; }).reduce((acc, pedido) => acc + (pedido.total || 0), 0);
    const numVendasSemana = (pedidos || []).filter(pedido => { const pedidoDate = getJSDate(pedido.createdAt); if (!pedidoDate) return false; return pedidoDate >= lastSunday && pedidoDate <= new Date() && pedido.status === 'Finalizado'; }).length;
    
    const activeStatuses = ['Pendente', 'Em Produção', 'Pronto para Entrega'];
    const pedidosPendentesCRM = (pedidos || []).filter(p => activeStatuses.includes(p.status) && p.origem !== 'Cardapio Online').length;
    const pedidosPendentesWhatsApp = (pedidos || []).filter(p => activeStatuses.includes(p.status) && p.origem === 'Cardapio Online').length;
    
    const clientesAtivos = (clientes || []).length;
    
    const upcomingBirthdays = useMemo(() => {
        if (!clientes) return [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const limitDate = new Date();
        limitDate.setDate(today.getDate() + 30);

        return clientes.filter(cliente => {
            if (!cliente.aniversario || !/^\d{4}-\d{2}-\d{2}$/.test(cliente.aniversario)) return false;

            const [, month, day] = cliente.aniversario.split('-');
            const birthMonth = parseInt(month, 10) - 1;
            const birthDay = parseInt(day, 10);

            const currentYearBirthday = new Date(today.getFullYear(), birthMonth, birthDay);
            currentYearBirthday.setHours(0, 0, 0, 0);

            const nextYearBirthday = new Date(today.getFullYear() + 1, birthMonth, birthDay);
            nextYearBirthday.setHours(0, 0, 0, 0);

            const upcomingBirthday = currentYearBirthday < today ? nextYearBirthday : currentYearBirthday;
            
            return upcomingBirthday >= today && upcomingBirthday <= limitDate;
        }).sort((a, b) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const getUpcomingBirthday = (aniversario) => {
                 const [, month, day] = aniversario.split('-');
                 const birthMonth = parseInt(month, 10) - 1;
                 const birthDay = parseInt(day, 10);
                 const currentYearBirthday = new Date(today.getFullYear(), birthMonth, birthDay);
                 currentYearBirthday.setHours(0, 0, 0, 0);
                 const nextYearBirthday = new Date(today.getFullYear() + 1, birthMonth, birthDay);
                 nextYearBirthday.setHours(0, 0, 0, 0);
                 return currentYearBirthday < today ? nextYearBirthday : currentYearBirthday;
            };

            const dateA = getUpcomingBirthday(a.aniversario);
            const dateB = getUpcomingBirthday(b.aniversario);
            
            return dateA - dateB;
        });
    }, [clientes]);

    const upcomingFestaOrders = useMemo(() => {
        if (!pedidos) return [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const limitDate = new Date();
        limitDate.setDate(today.getDate() + 7);
        limitDate.setHours(23, 59, 59, 999);
  
        return pedidos
            .filter(pedido => {
                if (pedido.categoria !== 'Festa' || !pedido.dataEntrega || ['Finalizado', 'Cancelado'].includes(pedido.status)) {
                    return false;
                }
                const entregaDate = new Date(pedido.dataEntrega + 'T00:00:00'); // Considera a data no início do dia
                // entregaDate.setHours(0, 0, 0, 0); // Ajuste já feito na criação
  
                return entregaDate >= today && entregaDate <= limitDate;
            })
            .sort((a, b) => new Date(a.dataEntrega) - new Date(b.dataEntrega));
    }, [pedidos]);

    return (
      <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
        
        <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
            <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Dashboard</h1>
                <p className="text-gray-600 mt-1">Visão geral da sua operação</p>
            </div>
            
            {/* Container para os banners de alarme e soneca */}
            <div className="w-full md:w-auto md:min-w-[300px] space-y-2"> 
                {hasNewPendingOrders && !isAlarmSnoozed && (
                  <div className={`p-3 rounded-lg transition-colors ${
                    isAlarmPlaying ? 'bg-red-100 border border-red-300 text-red-700' : 'bg-yellow-100 border border-yellow-300 text-yellow-700'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center">
                        <Bell className={`w-6 h-6 mr-3 ${isAlarmPlaying ? 'animate-bounce' : ''}`} />
                        <div>
                          <p className="font-bold">
                            {isAlarmPlaying ? "🔊 NOVO PEDIDO!" : "📱 Pedido Pendente!"}
                          </p>
                          {/* Mostra aviso apenas se o som estiver bloqueado E o alarme não estiver tocando */}
                          {!audioAllowed && !isAlarmPlaying && (
                            <p className="text-sm text-yellow-600">
                              🔒 Áudio bloqueado
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-shrink-0">
                         {/* Botão de Ativar Som removido daqui, é global agora */}
                        
                        <Button 
                          variant={isAlarmPlaying ? "danger" : "secondary"} 
                          size="sm" 
                          onClick={handleStopAndSnoozeAlarm}
                          className="text-xs" // Deixa o botão um pouco menor
                        >
                          <VolumeX className="w-4 h-4 mr-1" />
                          {isAlarmPlaying ? "Parar" : "Pausar (5min)"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {isAlarmSnoozed && (
                  <div className="bg-blue-100 border border-blue-300 text-blue-700 p-3 rounded-lg flex items-center">
                    <Clock className="w-5 h-5 mr-3 flex-shrink-0" />
                    <div>
                        <p className="font-bold">Alarme Pausado</p>
                        {/* Usa o snoozeDisplay local do Dashboard */}
                        <p className="text-sm">Reativando em <strong>{snoozeDisplay}</strong></p> 
                    </div>
                  </div>
                )}
            </div>
        </div>


        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg"><DollarSign className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Vendas Hoje</p><h2 className="text-2xl font-bold text-gray-800">R$ {vendasHoje.toFixed(2)}</h2></div></div></div>
            <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg"><ShoppingCart className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Nº Vendas Hoje</p><h2 className="text-2xl font-bold text-gray-800">{numVendasHoje}</h2></div></div></div>
            <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg"><BarChart3 className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Vendas Semana</p><h2 className="text-2xl font-bold text-gray-800">R$ {vendasSemana.toFixed(2)}</h2></div></div></div>
            <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg"><ShoppingCart className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Nº Vendas Semana</p><h2 className="text-2xl font-bold text-gray-800">{numVendasSemana}</h2></div></div></div>
            <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg"><Heart className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Clientes ativos</p><h2 className="text-2xl font-bold text-gray-800">{clientesAtivos}</h2></div></div></div>
            <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg"><Clock className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Pendentes (CRM)</p><h2 className="text-2xl font-bold text-gray-800">{pedidosPendentesCRM}</h2></div></div></div>
            <div className="bg-white p-6 rounded-2xl shadow-lg"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg"><MessageSquare className="w-6 h-6 text-white" /></div><div><p className="text-gray-500 text-sm font-medium">Pendentes (WhatsApp)</p><h2 className="text-2xl font-bold text-gray-800">{pedidosPendentesWhatsApp}</h2></div></div></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {upcomingBirthdays.length > 0 && (
                <div className="bg-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <Cake className="w-6 h-6 text-pink-500" />
                        <h3 className="text-xl font-bold text-gray-800">Aniversariantes Próximos</h3>
                    </div>
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                        {upcomingBirthdays.map(cliente => (
                            <div key={cliente.id} className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                                <p className="font-semibold text-gray-700">{cliente.nome}</p>
                                <p className="text-sm text-pink-600 font-medium">
                                    {new Date(cliente.aniversario + 'T03:00:00Z').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {upcomingFestaOrders.length > 0 && (
                <div className="bg-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-3 mb-4">
                        <Gift className="w-6 h-6 text-purple-500" />
                        <h3 className="text-xl font-bold text-gray-800">Próximas Entregas (Festa)</h3>
                    </div>
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                        {upcomingFestaOrders.map(pedido => (
                            <div key={pedido.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                                <div>
                                    <p className="font-semibold text-gray-700">{pedido.clienteNome}</p>
                                    <p className="text-sm text-gray-500">Pedido para festa</p>
                                </div>
                                <p className="text-sm text-purple-600 font-medium">
                                    {new Date(pedido.dataEntrega + 'T03:00:00Z').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
      </div>
    );
  };

  const Clientes = () => {
    const { clientes } = data;
    const [searchTerm, setSearchTerm] = usePersistentState("clientes_searchTerm", "");
    const [showModal, setShowModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [formData, setFormData] = useState({ nome: "", email: "", telefone: "", endereco: "", aniversario: "", status: "Ativo" });

    const filteredClients = useMemo(() => (clientes || []).filter(c => (c.nome && c.nome.toLowerCase().includes(searchTerm.toLowerCase())) || (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ), [clientes, searchTerm]);
    
    const resetForm = () => {
      setEditingClient(null);
      setFormData({ nome: "", email: "", telefone: "", endereco: "", aniversario: "", status: "Ativo" });
    };

    const handleNewClient = () => {
      resetForm();
      setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (editingClient) {
            const { id, ...updateData } = formData;
            await updateItem('clientes', editingClient.id, updateData);
        } else {
            await addItem('clientes', { ...formData, numeroDeCompras: 0, valorEmCompras: 0 });
        }
        setShowModal(false);
        resetForm();
    };
    const handleEdit = (client) => { setEditingClient(client); setFormData(client); setShowModal(true); };
    const columns = [
        { header: "Cliente", render: (row) => (<div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white font-bold shadow-md">{row.nome.charAt(0).toUpperCase()}</div><div><p className="font-semibold text-gray-800">{row.nome}</p><p className="text-sm text-gray-500">{row.email}</p></div></div>) },
        { header: "Telefone", key: 'telefone' },
        {
          header: "Aniversário",
          render: (row) => {
            if (!row.aniversario) return '-';
            const parts = row.aniversario.split('-');
            if (parts.length !== 3) return '-';
            const [, month, day] = parts;
            return `${day}/${month}`;
          }
        },
        { header: "Valor em Compras", render: (row) => (<span className="font-semibold text-green-600">R$ {(row.valorEmCompras || 0).toFixed(2)}</span>) },
        { header: "Última Compra", render: (row) => row.ultimaCompra ? getJSDate(row.ultimaCompra)?.toLocaleDateString('pt-BR') : '-' },
        { header: "Status", render: (row) => (<span className={`px-3 py-1 rounded-full text-xs font-medium ${row.status === 'VIP' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>{row.status}</span>) }
    ];
    const actions = [ { icon: Edit, label: "Editar", onClick: handleEdit }, { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('clientes', row.id) }) } ];
    return (
      <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4"><div><h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Gestão de Clientes</h1><p className="text-gray-600 mt-1">Gerencie seus clientes</p></div><Button onClick={handleNewClient} className="w-full md:w-auto"><Plus className="w-4 h-4" /> Novo Cliente</Button></div>
        <div className="relative max-w-md"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" /><input type="text" placeholder="Buscar clientes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500" /></div>
        <Table columns={columns} data={filteredClients} actions={actions} />
        <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={editingClient ? "Editar Cliente" : "Novo Cliente"} size="lg"><form onSubmit={handleSubmit} className="space-y-6"><div className="grid grid-cols-1 md:grid-cols-2 gap-6"><Input label="Nome Completo" type="text" value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})} required /><Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /><Input label="Telefone" type="tel" value={formData.telefone} onChange={(e) => setFormData({...formData, telefone: e.target.value})} /><Input label="Data de Aniversário" type="date" value={formData.aniversario} onChange={(e) => setFormData({...formData, aniversario: e.target.value})} /></div><Input label="Endereço" type="text" value={formData.endereco} onChange={(e) => setFormData({...formData, endereco: e.target.value})} /><div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</Button><Button type="submit"><Save className="w-4 h-4" />{editingClient ? "Salvar Alterações" : "Criar Cliente"}</Button></div></form></Modal>
      </div>
    );
  };
  
  const Produtos = () => {
    const [searchTerm, setSearchTerm] = usePersistentState("produtos_searchTerm", ""); 
    const [showModal, setShowModal] = useState(false); 
    const [editingProduct, setEditingProduct] = useState(null); 
    const [formData, setFormData] = useState({ nome: "", categoria: "Delivery", subcategoria: "", preco: "", custo: "", estoque: "", status: "Ativo", descricao: "", tempoPreparo: "", imageUrl: "" }); 
    const [imageFile, setImageFile] = useState(null); 
    const [imagePreview, setImagePreview] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isAddingSubcategory, setIsAddingSubcategory] = useState(false);
    const [newSubcategory, setNewSubcategory] = useState("");
    const [isSavingSubcategory, setIsSavingSubcategory] = useState(false);
    const [stockMovementModal, setStockMovementModal] = useState({ isOpen: false, type: 'entrada', product: null });
    const [stockMovementQuantity, setStockMovementQuantity] = useState('');
    const [stockMovementReason, setStockMovementReason] = useState('venda');
    const [statusLoading, setStatusLoading] = useState({});
    const [statusOverrides, setStatusOverrides] = useState({});

    const defaultSubcategorias = useMemo(() => ({
      Delivery: [ 'Queridinhos', 'Mousse', 'Palha Italiana', 'Bolo no pote', 'Copo da felicidade', 'Bombom aberto', 'Pipoca', 'Cone recheado', 'Bolo gelado', 'Bombom recheado' ],
      Festa: [ 'Bolo', 'Docinhos', 'Bombom', 'Doces finos', 'Bem casados', 'Cupcakes' ]
    }), []);
	
	  const subcategoriasPorCategoria = useMemo(() => {
      const map = Object.keys(defaultSubcategorias).reduce((acc, categoria) => {
        acc[categoria] = [...defaultSubcategorias[categoria]];
        return acc;
      }, {});

      (data.subcategorias || []).forEach((item) => {
        if (!item || !item.categoria || !item.nome) return;
        const categoria = item.categoria;
        if (!map[categoria]) {
          map[categoria] = [];
        }
        map[categoria].push(item.nome);
      });

      Object.keys(map).forEach((categoria) => {
        const uniqueValues = Array.from(new Set(map[categoria].filter(Boolean)));
        map[categoria] = uniqueValues.sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
      });

      return map;
    }, [data.subcategorias, defaultSubcategorias]);

    const availableSubcategories = useMemo(() => {
      const list = subcategoriasPorCategoria[formData.categoria] || [];
      if (formData.subcategoria && !list.includes(formData.subcategoria)) {
        return [...list, formData.subcategoria];
      }
      return list;
    }, [formData.categoria, formData.subcategoria, subcategoriasPorCategoria]);


    useEffect(() => {
      if (formData.categoria && formData.subcategoria && !availableSubcategories.includes(formData.subcategoria)) {
        setFormData(prev => ({ ...prev, subcategoria: '' }));
      }
    }, [formData.categoria, formData.subcategoria, availableSubcategories]);

    useEffect(() => {
      setIsAddingSubcategory(false);
      setNewSubcategory("");
    }, [formData.categoria]);

    const handleSubcategoriaChange = (e) => {
      const value = e.target.value;
      setIsAddingSubcategory(false);
      setNewSubcategory("");
      setFormData(prev => ({ ...prev, subcategoria: value }));
    };

    const handleCreateSubcategory = async () => {
      const trimmed = newSubcategory.trim();
      if (!trimmed) {
        alert('Informe o nome da nova subcategoria.');
        return;
      }

      const existing = availableSubcategories.find(sub => sub.toLowerCase() === trimmed.toLowerCase());
      if (existing) {
        alert('Esta subcategoria já existe para a categoria selecionada.');
        setFormData(prev => ({ ...prev, subcategoria: existing }));
        setIsAddingSubcategory(false);
        setNewSubcategory("");
        return;
      }

      try {
        setIsSavingSubcategory(true);
        await addItem('subcategorias', { nome: trimmed, categoria: formData.categoria });
        setFormData(prev => ({ ...prev, subcategoria: trimmed }));
        setIsAddingSubcategory(false);
        setNewSubcategory("");
      } catch (error) {
        console.error('Erro ao criar subcategoria:', error);
      } finally {
        setIsSavingSubcategory(false);
      }
    };

    const openStockMovementModal = (product, type) => {
      setStockMovementModal({ isOpen: true, type, product });
      setStockMovementQuantity('');
      setStockMovementReason('venda');
    };

    const closeStockMovementModal = () => {
      setStockMovementModal({ isOpen: false, type: 'entrada', product: null });
      setStockMovementQuantity('');
      setStockMovementReason('venda');
    };

    const buildReasonLabel = () => {
      if (stockMovementModal.type === 'saida') {
        const reasonLabels = {
          venda: 'Venda',
          doacao: 'Doação',
          perca: 'Perda',
        };
        return `Saída - ${reasonLabels[stockMovementReason] || 'Movimentação'}`;
      }
      return 'Entrada rápida de estoque';
    };

    const handleStockMovementSubmit = async (event) => {
      event.preventDefault();

      const quantity = parseFloat(stockMovementQuantity);

      if (!quantity || quantity <= 0) {
        alert('Informe uma quantidade válida para a movimentação.');
        return;
      }

      try {
        await updateStock(
          stockMovementModal.product.id,
          stockMovementModal.type,
          quantity,
          buildReasonLabel(),
          user,
        );
        closeStockMovementModal();
      } catch (error) {
        console.error('Erro ao movimentar estoque', error);
        alert(error.message || 'Erro ao atualizar estoque.');
      }
    };

    const handleToggleStatus = async (product) => {
      const currentStatus = statusOverrides[product.id] ?? product.status;
      const newStatus = currentStatus === 'Ativo' ? 'Inativo' : 'Ativo';

      setStatusOverrides(prev => ({ ...prev, [product.id]: newStatus }));
      setStatusLoading(prev => ({ ...prev, [product.id]: true }));

      try {
        await updateItem('produtos', product.id, { status: newStatus });
      } catch (error) {
        console.error('Erro ao atualizar status do produto', error);
        alert(error.message || 'Não foi possível atualizar o status.');
        setStatusOverrides(prev => ({ ...prev, [product.id]: currentStatus }));
      } finally {
        setStatusLoading(prev => ({ ...prev, [product.id]: false }));
      }
    };

    const filteredProducts = (data.produtos || []).filter(p => p.nome.toLowerCase().includes(searchTerm.toLowerCase()));

    useEffect(() => {
      setStatusOverrides(prev => {
        const updatedOverrides = { ...prev };

        (data.produtos || []).forEach((product) => {
          if (updatedOverrides[product.id] && updatedOverrides[product.id] === product.status) {
            delete updatedOverrides[product.id];
          }
        });

        return updatedOverrides;
      });
    }, [data.produtos]);

    const resetForm = () => {
      setShowModal(false);
      setEditingProduct(null);
      setFormData({ nome: "", categoria: "Delivery", subcategoria: "", preco: "", custo: "", estoque: "", status: "Ativo", descricao: "", tempoPreparo: "", imageUrl: "" });
      setImageFile(null);
      setImagePreview(null);
      setIsAddingSubcategory(false);
      setNewSubcategory("");
      setIsSavingSubcategory(false);
    };
    const handleImageChange = (e) => { if (e.target.files[0]) { const file = e.target.files[0]; setImageFile(file); setImagePreview(URL.createObjectURL(file)); } };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsUploading(true);
        try {
            let imageUrl = formData.imageUrl || "";
            if (imageFile) {
                const imageRef = ref(storage, `products/${Date.now()}_${imageFile.name}`);
                await uploadBytes(imageRef, imageFile);
                imageUrl = await getDownloadURL(imageRef);
            }
            const productData = { ...formData, preco: parseFloat(formData.preco || 0), custo: parseFloat(formData.custo || 0), estoque: parseInt(formData.estoque || 0), imageUrl: imageUrl };
            if (editingProduct) {
                const { id, ...updateData } = productData;
                await updateItem('produtos', editingProduct.id, updateData);
            } else {
                await addItem('produtos', productData);
            }
            resetForm();
        } catch (error) {
            console.error('Erro ao salvar produto', error);
            alert(error?.message || 'Não foi possível salvar o produto.');
        } finally {
            setIsUploading(false);
        }
    };
    const handleEdit = (product) => {
      setEditingProduct(product);
      setFormData({ ...product, preco: String(product.preco), custo: String(product.custo), estoque: String(product.estoque) });
      setImagePreview(product.imageUrl || null);
      setIsAddingSubcategory(false);
      setNewSubcategory("");
      setIsSavingSubcategory(false);
      setShowModal(true);
    };
    const columns = [
      {
        header: "Produto",
        render: (row) => (
          <div className="flex items-center gap-3">
            <img
              src={row.imageUrl || 'https://placehold.co/40x40/FFC0CB/FFFFFF?text=Doce'}
              alt={row.nome}
              className="w-10 h-10 rounded-xl object-cover shadow-md"
              onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/40x40/FFC0CB/FFFFFF?text=Erro'; }}
            />
            <div>
              <p className="font-semibold text-gray-800">{row.nome}</p>
              <p className="text-sm text-gray-500">{row.categoria} / {row.subcategoria}</p>
            </div>
          </div>
        )
      },
      { header: "Preço", render: (row) => <span className="font-semibold text-green-600">R$ {(row.preco || 0).toFixed(2)}</span> },
      { header: "Estoque", render: (row) => <span className={`font-medium ${row.estoque < 10 ? 'text-red-600' : 'text-gray-800'}`}>{row.estoque} un</span> },
      {
        header: 'Movimentação Rápida',
        render: (row) => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="!px-3 !py-2 text-xs"
              onClick={() => openStockMovementModal(row, 'entrada')}
            >
              + Entrada
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="!px-3 !py-2 text-xs text-red-600 border-red-200 hover:border-red-300 hover:text-red-700"
              onClick={() => openStockMovementModal(row, 'saida')}
            >
              - Saída
            </Button>
          </div>
        )
      },
      {
        header: "Status",
        render: (row) => {
          const effectiveStatus = statusOverrides[row.id] ?? row.status;
          const isActive = effectiveStatus === 'Ativo';

          return (
            <label className="inline-flex items-center gap-3 select-none">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isActive}
                onChange={() => handleToggleStatus(row)}
                disabled={!!statusLoading[row.id]}
              />
              <div
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  isActive ? 'bg-green-500' : 'bg-gray-200'
                } ${statusLoading[row.id] ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} peer-focus:outline-none`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
                    isActive ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </div>
              <span className={`text-sm font-medium ${isActive ? 'text-green-700' : 'text-gray-600'}`}>
                {isActive ? 'Ativo' : 'Inativo'}
              </span>
            </label>
          );
        }
      }
    ];
    const actions = [ { icon: Edit, label: "Editar", onClick: handleEdit }, { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('produtos', row.id) }) } ];
    
    return (
      <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4"><div><h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Gestão de Produtos</h1><p className="text-gray-600 mt-1">Gerencie seu cardápio e estoque</p></div><Button onClick={() => setShowModal(true)} className="w-full md:w-auto"><Plus className="w-4 h-4" /> Novo Produto</Button></div>
        <div className="relative max-w-md"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" /><input type="text" placeholder="Buscar produtos..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500" /></div>
        <Table columns={columns} data={filteredProducts} actions={actions} />
        <Modal
          isOpen={stockMovementModal.isOpen}
          onClose={closeStockMovementModal}
          title={`Registrar ${stockMovementModal.type === 'entrada' ? 'Entrada' : 'Saída'} de Estoque`}
          size="md"
        >
          <form onSubmit={handleStockMovementSubmit} className="space-y-5">
            <div className="bg-pink-50/80 border border-pink-100 rounded-2xl p-4">
              <p className="text-sm text-gray-500">Produto selecionado</p>
              <p className="font-semibold text-gray-900">{stockMovementModal.product?.nome || '-'}</p>
            </div>

            <Input
              label="Quantidade"
              type="number"
              min="0"
              step="1"
              value={stockMovementQuantity}
              onChange={(e) => setStockMovementQuantity(e.target.value)}
              required
            />

            {stockMovementModal.type === 'saida' && (
              <Select
                label="Motivo da Saída"
                value={stockMovementReason}
                onChange={(e) => setStockMovementReason(e.target.value)}
                required
              >
                <option value="venda">Venda</option>
                <option value="doacao">Doação</option>
                <option value="perca">Perda</option>
              </Select>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" type="button" onClick={closeStockMovementModal}>
                Cancelar
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4" /> Confirmar
              </Button>
            </div>
          </form>
        </Modal>
        <Modal isOpen={showModal} onClose={resetForm} title={editingProduct ? "Editar Produto" : "Novo Produto"} size="xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input label="Nome do Produto" value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})} required />
                  <Select label="Categoria" value={formData.categoria} onChange={(e) => setFormData({...formData, categoria: e.target.value})} required><option value="Delivery">Delivery</option><option value="Festa">Festa</option></Select>
                  <div className="space-y-1 w-full">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">Subcategoria</label>
                      <button
                        type="button"
                        className="text-xs font-semibold text-pink-600 hover:text-pink-700"
                        onClick={() => {
                          setIsAddingSubcategory(true);
                          setNewSubcategory("");
                          setFormData(prev => ({ ...prev, subcategoria: '' }));
                        }}
                      >
                        + Nova subcategoria
                      </button>
                    </div>
                    <select
                      value={formData.subcategoria}
                      onChange={handleSubcategoriaChange}
                      required
                      className="w-full px-4 py-3 border rounded-xl transition-all focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white border-gray-300"
                    >
                      <option value="">Selecione...</option>
                      {availableSubcategories.map(sub => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
					  {isAddingSubcategory && (
						<div className="md:col-span-2 bg-pink-50/80 border border-pink-100 rounded-2xl p-4 space-y-4">
						  <div className="flex items-center justify-between flex-wrap gap-2">
							<p className="text-sm font-semibold text-pink-700">Cadastrar nova subcategoria para "{formData.categoria}"</p>
							<button type="button" className="text-xs text-pink-600 hover:underline" onClick={() => { setIsAddingSubcategory(false); setNewSubcategory(""); }}>Cancelar</button>
						  </div>
						  <div className="md:grid md:grid-cols-3 md:gap-4 space-y-4 md:space-y-0">
							<div className="md:col-span-2">
							  <Input label="Nova Subcategoria" placeholder="Digite o nome da subcategoria" value={newSubcategory} onChange={(e) => setNewSubcategory(e.target.value)} />
							</div>
							<div className="flex gap-2 items-end justify-end">
							  <Button type="button" onClick={handleCreateSubcategory} disabled={isSavingSubcategory} className="w-full md:w-auto">
								<PackagePlus className="w-4 h-4" />
								{isSavingSubcategory ? 'Salvando...' : 'Salvar'}
							  </Button>
							</div>
						  </div>
						</div>
					  )}
                  <Input label="Preço (R$)" type="number" step="0.01" value={formData.preco} onChange={(e) => setFormData({...formData, preco: e.target.value})} />
                  <Input label="Custo (R$)" type="number" step="0.01" value={formData.custo} onChange={(e) => setFormData({...formData, custo: e.target.value})} />
                  <Input label="Estoque" type="number" value={formData.estoque} onChange={(e) => setFormData({...formData, estoque: e.target.value})} />
                  <Input label="Tempo de Preparo" value={formData.tempoPreparo} onChange={(e) => setFormData({...formData, tempoPreparo: e.target.value})} />
                  <Select label="Status" value={formData.status || 'Ativo'} onChange={(e) => setFormData({...formData, status: e.target.value})} required>
                    <option value="Ativo">Ativo</option>
                    <option value="Inativo">Inativo</option>
                  </Select>
                </div>
                <div className="relative">
                  <Textarea label="Descrição" rows="3" value={formData.descricao} onChange={(e) => setFormData({...formData, descricao: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Foto do Produto</label>
                <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center text-center p-4">{imagePreview ? (<img src={imagePreview} alt="Pré-visualização" className="max-h-full max-w-full object-contain rounded-lg"/>) : (<div className="text-gray-500"><ImageIcon className="mx-auto h-12 w-12" /><p className="mt-2 text-sm">Clique para selecionar</p></div>)}</div>
                <Input type="file" accept="image/*" onChange={handleImageChange} className="mt-2" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" type="button" onClick={resetForm}>Cancelar</Button>
              <Button type="submit" disabled={isUploading}><Save className="w-4 h-4" />{isUploading ? 'Salvando...' : (editingProduct ? "Salvar Alterações" : "Criar Produto")}</Button>
            </div>
          </form>
        </Modal>
      </div>
    );
  };
  
	const Configuracoes = ({ user, setConfirmDelete, data, addItem, updateItem, deleteItem, availableStores, storeInfoMap, resolveActiveStoreForWrite, selectedStoreId, storeAlarmSnoozeMap, onSaveStoreAlarmSnoozeMinutes, canManageStoreAlarmConfig, onCreateStore, onStoreBrandingChange }) => {
    const [activeTab, setActiveTab] = usePersistentState('configuracoes_activeTab', 'users');
    const [alarmSnoozeInput, setAlarmSnoozeInput] = useState(String(DEFAULT_ALARM_SNOOZE_MINUTES));
    const [isSavingLogo, setIsSavingLogo] = useState(false);
    const [logoMessage, setLogoMessage] = useState(null);
    const logoFileInputRef = useRef(null);

    useEffect(() => {
        if (activeTab === 'lojas' && user?.role !== ROLE_OWNER) {
            setActiveTab('users');
        }
    }, [activeTab, setActiveTab, user]);
    const [isSavingAlarmSnooze, setIsSavingAlarmSnooze] = useState(false);
    const [alarmSnoozeMessage, setAlarmSnoozeMessage] = useState(null);

    // States para Usuários
    const [usuarios, setUsuarios] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userExcludeTerm, setUserExcludeTerm] = useState('');
    const [userEmailFilter, setUserEmailFilter] = useState('any');
    const [userRoleFilter, setUserRoleFilter] = useState('all');
    const [showUserModal, setShowUserModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedExistingUserId, setSelectedExistingUserId] = useState('');
    const [userFormData, setUserFormData] = useState({
        email: "",
        senha: "",
        nome: "",
        role: ROLE_ATTENDANT,
        lojaId: "",
        lojaIds: [],
        permissions: getDefaultPermissionsForRole(ROLE_ATTENDANT),
        applyCustomProfile: true,
        uid: ''
    });
    const [newPassword, setNewPassword] = useState("");
	
	const effectiveStoreId = useMemo(() => {
	if (!user) return null;
	if (user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
		return null;
	}
	try {
		return resolveActiveStoreForWrite();
	} catch (error) {
		return null;
	}
}, [resolveActiveStoreForWrite, selectedStoreId, user]);

const effectiveStoreName = useMemo(() => {
	if (effectiveStoreId) {
		return storeInfoMap[effectiveStoreId]?.nome || effectiveStoreId;
	}
	if (user?.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
		return 'Visão Geral';
	}
	return 'Selecione uma loja para gerenciar';
}, [effectiveStoreId, storeInfoMap, user, selectedStoreId]);

    const canEditAlarmSnooze = useMemo(() => {
        if (!effectiveStoreId) return false;
        return canManageStoreAlarmConfig(effectiveStoreId);
    }, [canManageStoreAlarmConfig, effectiveStoreId]);

    const canEditStoreLogo = useMemo(() => {
        if (!effectiveStoreId) return false;
        return canManageStoreAlarmConfig(effectiveStoreId);
    }, [canManageStoreAlarmConfig, effectiveStoreId]);

    const currentStoreLogo = useMemo(() => {
        if (!effectiveStoreId) return null;
        return storeInfoMap?.[effectiveStoreId]?.branding?.logoUrl || null;
    }, [effectiveStoreId, storeInfoMap]);

    useEffect(() => {
        if (!effectiveStoreId) {
            setAlarmSnoozeInput(String(DEFAULT_ALARM_SNOOZE_MINUTES));
            setAlarmSnoozeMessage(null);
            return;
        }

        const resolved = resolveStoreAlarmSnoozeMinutes(effectiveStoreId, storeAlarmSnoozeMap, DEFAULT_ALARM_SNOOZE_MINUTES);
        setAlarmSnoozeInput(String(resolved));
        setAlarmSnoozeMessage(null);
    }, [effectiveStoreId, storeAlarmSnoozeMap]);

    useEffect(() => {
        setLogoMessage(null);
    }, [effectiveStoreId]);

    const validateLogoDimensions = useCallback((file) => {
        if (!file || file.type === 'image/svg+xml') {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const imageUrl = URL.createObjectURL(file);
            const image = new Image();

            image.onload = () => {
                const { width, height } = image;
                URL.revokeObjectURL(imageUrl);

                if (width < 200 || height < 60 || width > 2400 || height > 800) {
                    reject(new Error('Imagem fora do tamanho recomendado. Use algo próximo de 600x200 px (mínimo 200x60 e máximo 2400x800).'));
                    return;
                }

                resolve();
            };

            image.onerror = () => {
                URL.revokeObjectURL(imageUrl);
                reject(new Error('Não foi possível validar as dimensões da imagem selecionada.'));
            };

            image.src = imageUrl;
        });
    }, []);

    const handleUploadStoreLogo = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!effectiveStoreId) {
            setLogoMessage({ type: 'error', text: 'Selecione uma loja para editar a logo.' });
            event.target.value = '';
            return;
        }

        if (!canEditStoreLogo) {
            setLogoMessage({ type: 'error', text: 'Você não tem permissão para alterar a logo desta loja.' });
            event.target.value = '';
            return;
        }

        if (!ACCEPTED_LOGO_MIME_TYPES.includes(file.type)) {
            setLogoMessage({ type: 'error', text: 'Formato inválido. Envie PNG, JPG ou SVG.' });
            event.target.value = '';
            return;
        }

        if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
            setLogoMessage({ type: 'error', text: 'A logo deve ter no máximo 1MB.' });
            event.target.value = '';
            return;
        }

        setIsSavingLogo(true);
        setLogoMessage(null);

        try {
            await validateLogoDimensions(file);

            const ext = (file.name.split('.').pop() || 'png').toLowerCase();
            const logoPath = `lojas/${effectiveStoreId}/branding/logo.${ext}`;
            const logoRef = ref(storage, logoPath);

            await uploadBytes(logoRef, file, { contentType: file.type });
            const logoUrl = await getDownloadURL(logoRef);

            await setDoc(doc(db, 'lojas', effectiveStoreId), {
                branding: {
                    logoUrl,
                    logoPath,
                    updatedAt: serverTimestamp(),
                    updatedBy: user?.auth?.uid || user?.auth?.email || 'unknown'
                }
            }, { merge: true });

            onStoreBrandingChange(effectiveStoreId, { logoUrl, logoPath });

            if (localStorage.getItem('authDebug') === '1') {
                console.debug('[branding] Logo atualizada', { effectiveStoreId, logoPath });
            }

            setLogoMessage({ type: 'success', text: 'Logo atualizada com sucesso.' });
        } catch (error) {
            setLogoMessage({ type: 'error', text: error?.message || 'Não foi possível enviar a logo.' });
        } finally {
            setIsSavingLogo(false);
            event.target.value = '';
        }
    }, [canEditStoreLogo, effectiveStoreId, onStoreBrandingChange, user, validateLogoDimensions]);

    const handleRemoveStoreLogo = useCallback(async () => {
        if (!effectiveStoreId) {
            setLogoMessage({ type: 'error', text: 'Selecione uma loja para editar a logo.' });
            return;
        }

        if (!canEditStoreLogo) {
            setLogoMessage({ type: 'error', text: 'Você não tem permissão para alterar a logo desta loja.' });
            return;
        }

        setIsSavingLogo(true);
        setLogoMessage(null);

        try {
            const currentLogoPath = storeInfoMap?.[effectiveStoreId]?.branding?.logoPath;
            if (currentLogoPath) {
                try {
                    await deleteObject(ref(storage, currentLogoPath));
                } catch (storageError) {
                    if (storageError?.code !== 'storage/object-not-found') {
                        throw storageError;
                    }
                }
            }

            await setDoc(doc(db, 'lojas', effectiveStoreId), {
                branding: {
                    logoUrl: null,
                    logoPath: null,
                    updatedAt: serverTimestamp(),
                    updatedBy: user?.auth?.uid || user?.auth?.email || 'unknown'
                }
            }, { merge: true });

            onStoreBrandingChange(effectiveStoreId, { logoUrl: null, logoPath: null });

            if (localStorage.getItem('authDebug') === '1') {
                console.debug('[branding] Logo removida', { effectiveStoreId, currentLogoPath: currentLogoPath || null });
            }

            setLogoMessage({ type: 'success', text: 'Logo removida. A logo padrão será exibida.' });
        } catch (error) {
            setLogoMessage({ type: 'error', text: error?.message || 'Não foi possível remover a logo.' });
        } finally {
            setIsSavingLogo(false);
        }
    }, [canEditStoreLogo, effectiveStoreId, onStoreBrandingChange, storeInfoMap, user]);

    // States para Cupons
    const [cupons, setCupons] = useState([]);
    const [showCupomModal, setShowCupomModal] = useState(false);
    const [editingCupom, setEditingCupom] = useState(null);
    const [cupomFormData, setCupomFormData] = useState({});

    const [showStoreModal, setShowStoreModal] = useState(false);
    const [editingStoreId, setEditingStoreId] = useState(null);
    const [storeFormData, setStoreFormData] = useState({ nome: '', slug: '' });
    const [storeFormError, setStoreFormError] = useState('');
    const [isSavingStore, setIsSavingStore] = useState(false);

    const [storePendingDelete, setStorePendingDelete] = useState(null);
    const [deleteStoreInput, setDeleteStoreInput] = useState('');
    const [deleteStoreError, setDeleteStoreError] = useState('');
    const [isDeletingStore, setIsDeletingStore] = useState(false);
    const [storeAvailabilityConfig, setStoreAvailabilityConfig] = useState(null);
    const [storeAvailabilityLoading, setStoreAvailabilityLoading] = useState(false);

    const tenantSlugPreview = useMemo(() => {
        const storedTenantSlug = sessionStorage.getItem(AUTH_TENANT_SLUG_KEY) || '';
        const hostTenantSlug = getTenantSlugFromHostname(window.location.hostname) || '';
        const fallback = user?.tenantSlug || user?.brandSlug || '';
        return normalizeTenantSlug(storedTenantSlug || hostTenantSlug || fallback);
    }, [user]);

    const storeSlugSuggestion = useMemo(() => {
        if (storeFormData.slug && storeFormData.slug.trim()) {
            return normalizeStoreIdentifier(storeFormData.slug);
        }
        return slugifyStoreName(storeFormData.nome || '');
    }, [storeFormData.nome, storeFormData.slug]);

    const storeIdentifierError = useMemo(() => {
        const hasCustomIdentifier = Boolean((storeFormData.slug || '').trim());
        if (!hasCustomIdentifier) return '';

        if (!storeSlugSuggestion || storeSlugSuggestion.length < 3 || storeSlugSuggestion.length > 40) {
            return 'O identificador deve ter entre 3 e 40 caracteres.';
        }

        if (!/^[a-z0-9_-]+$/.test(storeSlugSuggestion)) {
            return 'Use apenas letras, números, hífen e underline no identificador.';
        }

        if (storeSlugSuggestion === STORE_ALL_KEY) {
            return 'Identificador inválido.';
        }

        return '';
    }, [storeFormData.slug, storeSlugSuggestion]);
	
	    const userRoles = useMemo(() => {
        const roles = new Set((usuarios || []).map((userItem) => userItem.role).filter(Boolean));
        return Array.from(roles);
    }, [usuarios]);

    const filteredUsuarios = useMemo(() => {
        const search = userSearchTerm.trim().toLowerCase();
        const exclude = userExcludeTerm.trim().toLowerCase();

        return (usuarios || []).filter((usuario) => {
            const name = (usuario.nome || '').toLowerCase();
            const email = (usuario.email || '').trim();
            const role = (usuario.role || '').toLowerCase();

            if (search && !name.includes(search)) return false;
            if (exclude && name.includes(exclude)) return false;

            if (userEmailFilter === 'empty' && email) return false;
            if (userEmailFilter === 'filled' && !email) return false;

            if (userRoleFilter !== 'all' && role !== userRoleFilter.toLowerCase()) return false;

            return true;
        });
    }, [usuarios, userSearchTerm, userExcludeTerm, userEmailFilter, userRoleFilter]);

    const hasUserFilters = useMemo(() => {
        return Boolean(
            userSearchTerm.trim() ||
            userExcludeTerm.trim() ||
            userEmailFilter !== 'any' ||
            userRoleFilter !== 'all'
        );
    }, [userSearchTerm, userExcludeTerm, userEmailFilter, userRoleFilter]);

    const sortedStores = useMemo(() => {
        return [...availableStores]
            .map((storeId) => ({ storeId, ...(storeInfoMap[storeId] || {}) }))
            .sort((a, b) => (a.nome || a.storeId).localeCompare((b.nome || b.storeId), 'pt-BR'));
    }, [availableStores, storeInfoMap]);

    const isDefaultStore = useCallback((store) => {
        return Boolean(store?.isDefault || store?.default || store?.isPrimary);
    }, []);

    const openCreateStoreModal = useCallback(() => {
        setEditingStoreId(null);
        setStoreFormData({ nome: '', slug: '' });
        setStoreFormError('');
        setStoreAvailabilityConfig(null);
        setShowStoreModal(true);
    }, []);

    const openEditStoreModal = useCallback((store) => {
        setEditingStoreId(store.storeId);
        setStoreFormData({
            nome: store.nome || '',
            slug: store.slug || ''
        });
        setStoreFormError('');
        setStoreAvailabilityConfig(null);
        setShowStoreModal(true);
    }, []);

    useEffect(() => {
        if (!showStoreModal) {
            setStoreAvailabilityConfig(null);
            setStoreAvailabilityLoading(false);
            return;
        }

        const nome = (storeFormData.nome || '').trim();
        if (!nome || storeIdentifierError || !storeSlugSuggestion) {
            setStoreAvailabilityConfig(null);
            setStoreAvailabilityLoading(false);
            return;
        }

        const timeout = setTimeout(async () => {
            setStoreAvailabilityLoading(true);
            try {
                const checkStoreAvailabilityFn = httpsCallable(functions, 'checkStoreAvailability');
                const result = await checkStoreAvailabilityFn({
                    name: nome,
                    slug: storeSlugSuggestion,
                    excludeStoreId: editingStoreId || null,
                });
                setStoreAvailabilityConfig((result && result.data) || null);
            } catch (error) {
                setStoreAvailabilityConfig({
                    available: false,
                    reason: 'Não foi possível validar a disponibilidade agora.'
                });
            } finally {
                setStoreAvailabilityLoading(false);
            }
        }, 400);

        return () => clearTimeout(timeout);
    }, [editingStoreId, showStoreModal, storeFormData.nome, storeIdentifierError, storeSlugSuggestion]);

    const hasStoreDependencies = useCallback(async (storeId) => {
        const [pedidosSnap, produtosSnap, usersPrimarySnap, usersSecondarySnap] = await Promise.all([
            getDocs(query(collection(db, 'lojas', storeId, 'pedidos'), limit(1))),
            getDocs(query(collection(db, 'lojas', storeId, 'produtos'), limit(1))),
            getDocs(query(collection(db, 'users'), where('lojaId', '==', storeId), limit(1))),
            getDocs(query(collection(db, 'users'), where('lojaIds', 'array-contains', storeId), limit(1)))
        ]);

        return (
            !pedidosSnap.empty ||
            !produtosSnap.empty ||
            !usersPrimarySnap.empty ||
            !usersSecondarySnap.empty
        );
    }, []);

    const handleClearUserFilters = useCallback(() => {
        setUserSearchTerm('');
        setUserExcludeTerm('');
        setUserEmailFilter('any');
        setUserRoleFilter('all');
    }, []);

    const handleSaveStore = useCallback(async (event) => {
        event.preventDefault();
        if (user?.role !== ROLE_OWNER) return;

        const nome = (storeFormData.nome || '').trim();
        const finalSlug = storeSlugSuggestion;

        if (!nome) {
            setStoreFormError('Informe o nome da loja.');
            return;
        }

        if (storeIdentifierError) {
            setStoreFormError(storeIdentifierError);
            return;
        }

        if (!storeAvailabilityConfig?.available) {
            setStoreFormError('Nome/identificador indisponível.');
            return;
        }

        setIsSavingStore(true);
        setStoreFormError('');

        try {
            if (!editingStoreId) {
                await onCreateStore({ storeId: finalSlug, nome, slug: finalSlug });
            } else {
                const payload = {
                    nome,
                    slug: finalSlug,
                    publicPath: buildStorePublicPath(tenantSlugPreview, finalSlug),
                    updatedAt: serverTimestamp(),
                    updatedBy: user?.uid || user?.email || 'unknown'
                };

                await setDoc(doc(db, 'lojas', editingStoreId), payload, { merge: true });
            }

            setShowStoreModal(false);
        } catch (error) {
            setStoreFormError(error?.message || 'Não foi possível salvar a loja.');
        } finally {
            setIsSavingStore(false);
        }
    }, [editingStoreId, onCreateStore, storeAvailabilityConfig?.available, storeFormData.nome, storeIdentifierError, storeSlugSuggestion, tenantSlugPreview, user]);

    const handleDeleteStore = useCallback(async () => {
        if (!storePendingDelete || user?.role !== ROLE_OWNER) return;

        const storeName = (storePendingDelete.nome || storePendingDelete.storeId || '').trim();
        if (deleteStoreInput.trim() !== storeName) {
            setDeleteStoreError('Digite o nome da loja para confirmar a exclusão.');
            return;
        }

        if (isDefaultStore(storePendingDelete)) {
            setDeleteStoreError('A loja padrão do sistema não pode ser excluída.');
            return;
        }

        setDeleteStoreError('');
        setIsDeletingStore(true);

        try {
            const hasDependencies = await hasStoreDependencies(storePendingDelete.storeId);
            if (hasDependencies) {
                setDeleteStoreError('Não é possível excluir uma loja com dados vinculados. Inative a loja.');
                return;
            }

            await setDoc(doc(db, 'lojas', storePendingDelete.storeId), {
                isDeleted: true,
                deletedAt: serverTimestamp(),
                deletedBy: user?.uid || user?.email || 'unknown',
                updatedAt: serverTimestamp(),
                updatedBy: user?.uid || user?.email || 'unknown'
            }, { merge: true });

            setStorePendingDelete(null);
            setDeleteStoreInput('');
        } catch (error) {
            setDeleteStoreError(error?.message || 'Não foi possível excluir a loja.');
        } finally {
            setIsDeletingStore(false);
        }
    }, [deleteStoreInput, hasStoreDependencies, isDefaultStore, storePendingDelete, user]);

    // 🔄 Carregar dados da aba ativa
    useEffect(() => {
        let unsubscribe = () => {}; // Função de cleanup vazia

        if (activeTab === 'users') {
            setUsuarios([]); // Limpa a lista atual para mostrar o carregamento
            // Chama a Cloud Function para buscar a lista de usuários do Authentication
            const listAllUsersFn = httpsCallable(functions, 'listAllUsers');
            listAllUsersFn()
                .then((result) => {
                    if (result.data.users) {
                        const normalizedUsers = result.data.users.map((u) => {
                            const lojas = Array.isArray(u.lojaIds)
                                ? u.lojaIds
                                : (u.lojaId ? [u.lojaId] : []);
                            const normalizedRole = normalizeRole(u.role);
                            return {
                                ...u,
                                role: normalizedRole,
                                lojaIds: lojas,
                                lojaId: lojas[0] || null,
                                permissions: sanitizePermissions(u.permissions, normalizedRole)
                            };
                        });

                        const filteredUsers = (!effectiveStoreId || user.role === ROLE_OWNER)
                            ? normalizedUsers
                            : normalizedUsers.filter(u => (u.lojaIds || []).includes(effectiveStoreId));

                        setUsuarios(filteredUsers)
                    }
                })
                .catch((error) => {
                    console.error("Erro ao buscar a lista de usuários:", error);
                    alert("Ocorreu um erro ao buscar usuários: " + error.message);
                });

        } else if (activeTab === 'cupons') {
            if (!effectiveStoreId) {
                setCupons([]);
            } else {
                const primaryRef = getStoreCollectionRef(effectiveStoreId, 'cupons');
                const legacyRef = getStoreCollectionRef(effectiveStoreId, 'cupons', true);
                let legacyUnsub = null;

                const unsubscribePrimary = onSnapshot(primaryRef, (snap) => {
                    const items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setCupons(items);

                    if (!items.length && !legacyUnsub) {
                        legacyUnsub = onSnapshot(legacyRef, (legacySnap) => {
                            if (items.length) return;
                            setCupons(legacySnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                        });
                    } else if (items.length && legacyUnsub) {
                        legacyUnsub();
                        legacyUnsub = null;
                    }
                });

                unsubscribe = () => {
                    unsubscribePrimary();
                    if (legacyUnsub) legacyUnsub();
                };
            }
        }
        
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [activeTab, effectiveStoreId, user]);
    
    // States para Configuração de Frete
    const defaultFreteConfig = { enderecoLoja: '', lat: '', lng: '', valorPorKm: '', valorMinimoFrete: '' };
    const [freteConfig, setFreteConfig] = useState(defaultFreteConfig);
    const [isSavingFrete, setIsSavingFrete] = useState(false);

    const normalizeDecimalInput = (value) => String(value ?? '').replace(',', '.').trim();
    const parseNonNegativeNumber = (value) => {
        const parsedValue = parseFloat(normalizeDecimalInput(value));
        return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
    };

    useEffect(() => {
        if (activeTab !== 'frete') return;

        if (!effectiveStoreId) {
            setFreteConfig(defaultFreteConfig);
            return;
        }

        const fetchFreteConfig = async () => {
            try {
                const configRef = getStoreConfigDocRef(effectiveStoreId);
                const configSnap = await getDoc(configRef);

                if (configSnap.exists()) {
                    const configData = configSnap.data() || {};
                    const freteData = configData.frete || configData;

                    if (freteData && Object.keys(freteData).length) {
                        setFreteConfig({ ...defaultFreteConfig, ...freteData });
                        return;
                    }
                }

                const legacyFreteRef = doc(db, 'lojas', effectiveStoreId, 'configuracoes', 'frete');
                const legacyFreteSnap = await getDoc(legacyFreteRef);
                if (legacyFreteSnap.exists()) {
                    const freteData = legacyFreteSnap.data();
                    setFreteConfig({ ...defaultFreteConfig, ...(freteData || {}) });
                    await setDoc(configRef, { frete: freteData || {} }, { merge: true });
                    return;
                }

                const legacyInfoSnap = await getDoc(doc(db, 'lojas', effectiveStoreId, 'info', 'dados'));
                if (legacyInfoSnap.exists()) {
                    const infoData = legacyInfoSnap.data();
                    const freteData = infoData?.frete || {};
                    if (Object.keys(freteData).length) {
                        setFreteConfig({ ...defaultFreteConfig, ...freteData });
                        await setDoc(configRef, { frete: freteData }, { merge: true });
                        return;
                    }
                }

                setFreteConfig(defaultFreteConfig);
            } catch (error) {
                console.error("Erro ao buscar configurações de frete:", error);
            }
        };
        fetchFreteConfig();
    }, [activeTab, effectiveStoreId]);
	
	//Limpeza quando o componente desmontar
        useEffect(() => {
          return () => {
                // Para o alarme quando o componente desmontar
                if (stopAlarmRef.current) {
                  stopAlarmRef.current();
                }
                if (stopAlarmFnRef.current) {
                  stopAlarmFnRef.current();
                }
          };
  }, []);
    
    const getCustomPermissionsForUser = useCallback(async (userProfile) => {
        const normalizedRole = normalizeRole(userProfile?.role || ROLE_ATTENDANT);
        const fallbackPermissions = sanitizePermissions(userProfile?.permissions, normalizedRole);
        const uid = userProfile?.uid || userProfile?.id;

        if (!uid) {
            return { permissions: fallbackPermissions, hasCustomProfile: false, normalizedRole };
        }

        try {
            const customProfileSnap = await getDoc(doc(db, 'customProfiles', uid));
            if (customProfileSnap.exists()) {
                return {
                    permissions: sanitizePermissions(customProfileSnap.data()?.permissions, normalizedRole),
                    hasCustomProfile: true,
                    normalizedRole,
                };
            }
        } catch (err) {
            console.error('Erro ao carregar perfil personalizado:', err);
        }

        return { permissions: fallbackPermissions, hasCustomProfile: false, normalizedRole };
    }, []);

    const buildUserFormState = useCallback(async (userToEdit = null) => {
        if (!userToEdit) {
            setEditingUser(null);
            setSelectedExistingUserId('');
            setUserFormData({
                email: "",
                senha: "",
                nome: "",
                role: ROLE_ATTENDANT,
                lojaId: effectiveStoreId || '',
                lojaIds: effectiveStoreId ? [effectiveStoreId] : [],
                permissions: getDefaultPermissionsForRole(ROLE_ATTENDANT),
                applyCustomProfile: true,
                uid: ''
            });
            return;
        }

        const lojas = Array.isArray(userToEdit.lojaIds)
            ? userToEdit.lojaIds
            : (userToEdit.lojaId ? [userToEdit.lojaId] : []);

        const { permissions, hasCustomProfile, normalizedRole } = await getCustomPermissionsForUser(userToEdit);
        setEditingUser(userToEdit);
        setSelectedExistingUserId(userToEdit.uid || userToEdit.id || '');
        setUserFormData({
            email: userToEdit.email || "",
            senha: "",
            nome: userToEdit.nome || "",
            role: normalizedRole,
            lojaId: lojas[0] || '',
            lojaIds: lojas,
            permissions,
            applyCustomProfile: hasCustomProfile,
            uid: userToEdit.uid || userToEdit.id || ''
        });
    }, [effectiveStoreId, getCustomPermissionsForUser]);

    // Handlers para Usuários
    const handleNewUser = () => {
        buildUserFormState();
        setShowUserModal(true);
    };

    const handleEditUser = (userToEdit) => {
        buildUserFormState(userToEdit);
        setShowUserModal(true);
    };

    const handleExistingUserSelect = async (uid) => {
        setSelectedExistingUserId(uid);
        if (!uid) {
            await buildUserFormState();
            return;
        }

        const selectedUser = (usuarios || []).find((u) => (u.uid || u.id) === uid);
        if (selectedUser) {
            await buildUserFormState(selectedUser);
        }
    };

	const handleUserSubmit = async (e) => {
	  e.preventDefault();
	  
	  if (!userFormData.email || !userFormData.nome || !userFormData.role) {
		alert('Por favor, preencha todos os campos obrigatórios');
		return;
	  }

	  if (!editingUser && (!userFormData.senha || userFormData.senha.length < 6)) {
		alert('A senha é obrigatória e deve ter pelo menos 6 caracteres');
		return;
	  }
          try {
                const selectedRole = normalizeRole(userFormData.role);
                const singleStoreId = selectedRole === ROLE_OWNER ? null : (userFormData.lojaId || effectiveStoreId);
                if (selectedRole !== ROLE_OWNER && !singleStoreId) {
                    alert('Selecione uma loja para este usuário.');
                    return;
                }

                const lojasSelecionadas = selectedRole === ROLE_OWNER
                    ? (userFormData.lojaIds && userFormData.lojaIds.length ? userFormData.lojaIds : [])
                    : (singleStoreId ? [singleStoreId] : []);
                const sanitizedPermissions = sanitizePermissions(userFormData.permissions, selectedRole);
                const applyCustomProfile = Boolean(userFormData.applyCustomProfile);
                const permissionsToPersist = applyCustomProfile
                    ? sanitizedPermissions
                    : getDefaultPermissionsForRole(selectedRole);

                let updatedUserId = editingUser?.uid || editingUser?.id;

                if (editingUser) {
                  const updateUserFn = httpsCallable(functions, 'updateUser');
                  await updateUserFn({
                        uid: editingUser.uid,
                        nome: userFormData.nome,
                        role: selectedRole,
                        email: userFormData.email,
                        lojaId: singleStoreId || null,
                        lojaIds: lojasSelecionadas,
                        permissions: permissionsToPersist
                  });
                  updatedUserId = editingUser.uid;
                  alert('Usuário atualizado com sucesso!');
                } else {
                  const createUserFn = httpsCallable(functions, 'createUser');
                  const result = await createUserFn({
                        email: userFormData.email,
                        senha: userFormData.senha,
                        nome: userFormData.nome,
                        role: selectedRole,
                        lojaId: singleStoreId || null,
                        lojaIds: lojasSelecionadas,
                        permissions: permissionsToPersist
                  });
                  updatedUserId = result?.data?.uid || updatedUserId;
                  alert('Usuário criado com sucesso!');
                }

                if (!applyCustomProfile && updatedUserId) {
                    try {
                        await deleteDoc(doc(db, 'customProfiles', updatedUserId));
                    } catch (deleteError) {
                        console.error('Erro ao remover perfil personalizado:', deleteError);
                    }
                }

                if (updatedUserId && user?.auth?.uid === updatedUserId) {
                    setUser((prev) => prev ? {
                        ...prev,
                        permissions: permissionsToPersist,
                        customPermissions: applyCustomProfile ? permissionsToPersist : null,
                        hasCustomProfile: applyCustomProfile,
                    } : prev);
                }

                setShowUserModal(false);

                // Recarrega a lista de usuários
                const listAllUsersFn = httpsCallable(functions, 'listAllUsers');
                const result = await listAllUsersFn();
                if (result.data.users) {
                    const normalizedUsers = result.data.users.map((u) => {
                        const lojas = Array.isArray(u.lojaIds)
                            ? u.lojaIds
                            : (u.lojaId ? [u.lojaId] : []);
                        const normalizedRole = normalizeRole(u.role);
                        return {
                            ...u,
                            role: normalizedRole,
                            lojaIds: lojas,
                            lojaId: lojas[0] || null,
                            permissions: sanitizePermissions(u.permissions, normalizedRole)
                        };
                    });

                    const filteredUsers = (!effectiveStoreId || user.role === ROLE_OWNER)
                        ? normalizedUsers
                        : normalizedUsers.filter(u => (u.lojaIds || []).includes(effectiveStoreId));

                    setUsuarios(filteredUsers);
                }

          } catch (error) {
                console.error('Erro completo:', error);
                alert("Erro ao salvar usuário: " + error.message);
          }
	};

    const handleDeleteUser = async (userToDelete) => {
        const deleteUserFn = httpsCallable(functions, "deleteUser");
        try {
            await deleteUserFn({ uid: userToDelete.uid || userToDelete.id });
            // A remoção do Firestore já pode ser feita pela cloud function ou aqui como fallback
            await deleteDoc(doc(db, "users", userToDelete.id));
            setConfirmDelete({ isOpen: false, onConfirm: () => {} });
        } catch (err) {
            alert("Erro ao deletar usuário: " + err.message);
        }
    };
    
    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (!editingUser) return;
        const updatePasswordFn = httpsCallable(functions, "updateUserPassword");
        try {
          await updatePasswordFn({ uid: editingUser.uid || editingUser.id, newPassword });
          alert("Senha alterada com sucesso!");
          setShowPasswordModal(false);
          setNewPassword("");
        } catch (err) {
          alert("Erro ao alterar senha: " + err.message);
        }
    };
    
    const resetCupomForm = () => { 
        setEditingCupom(null); 
        setCupomFormData({ codigo: '', tipoDesconto: 'percentual', valor: '', limiteUso: '', valorMinimo: '', status: 'Ativo' }); 
    };
    
    const handleNewCupom = () => { 
        resetCupomForm(); 
        setShowCupomModal(true); 
    };
    
    const handleEditCupom = (cupom) => { 
        setEditingCupom(cupom); 
        setCupomFormData({
            ...cupom, 
            valor: String(cupom.valor || ''), 
            limiteUso: String(cupom.limiteUso || ''), 
            valorMinimo: String(cupom.valorMinimo || '')
        }); 
        setShowCupomModal(true); 
    };
    
    const handleCupomSubmit = async (e) => {
      e.preventDefault();

      try {
        if (!effectiveStoreId) {
            alert('Selecione uma loja para gerenciar cupons.');
            return;
        }

        const dataToSave = {
          codigo: cupomFormData.codigo.toUpperCase().trim(),
          tipoDesconto: cupomFormData.tipoDesconto,
          valor: parseFloat(cupomFormData.valor || 0),
          limiteUso: parseInt(cupomFormData.limiteUso || 0),
          valorMinimo: parseFloat(cupomFormData.valorMinimo || 0),
          status: cupomFormData.status || 'Ativo'
        };

        if (editingCupom) {
          await updateItem('cupons', editingCupom.id, dataToSave);
          alert('Cupom atualizado com sucesso!');
        } else {
          await addItem('cupons', { ...dataToSave, usos: 0 });
          alert('Cupom criado com sucesso!');
        }
        
        setShowCupomModal(false);
        resetCupomForm();
      } catch (error) {
        console.error('Erro ao salvar cupom:', error);
        alert('Erro ao salvar cupom: ' + error.message);
      }
    };

    const handleSaveAlarmSnoozeConfig = async (e) => {
        e.preventDefault();

        if (!effectiveStoreId) {
            setAlarmSnoozeMessage({ type: 'error', text: 'Selecione uma loja para configurar.' });
            return;
        }

        if (!canEditAlarmSnooze) {
            setAlarmSnoozeMessage({ type: 'error', text: 'Você não tem permissão para alterar esta configuração.' });
            return;
        }

        const parsedMinutes = Number(alarmSnoozeInput);
        if (!Number.isFinite(parsedMinutes) || parsedMinutes < MIN_ALARM_SNOOZE_MINUTES || parsedMinutes > MAX_ALARM_SNOOZE_MINUTES) {
            setAlarmSnoozeMessage({ type: 'error', text: `Informe um valor entre ${MIN_ALARM_SNOOZE_MINUTES} e ${MAX_ALARM_SNOOZE_MINUTES} minutos.` });
            return;
        }

        setIsSavingAlarmSnooze(true);
        setAlarmSnoozeMessage(null);

        try {
            const savedMinutes = await onSaveStoreAlarmSnoozeMinutes(effectiveStoreId, parsedMinutes);
            setAlarmSnoozeInput(String(savedMinutes));
            setAlarmSnoozeMessage({ type: 'success', text: 'Tempo de pausa do alarme salvo com sucesso.' });
        } catch (error) {
            console.error('[Configurações] Erro ao salvar tempo de soneca do alarme:', error);
            setAlarmSnoozeMessage({ type: 'error', text: 'Não foi possível salvar agora. Tente novamente.' });
        } finally {
            setIsSavingAlarmSnooze(false);
        }
    };

    const handleSaveFreteConfig = async (e) => {
        e.preventDefault();
        setIsSavingFrete(true);
        try {

            if (!effectiveStoreId) {
                alert('Selecione uma loja específica para salvar as configurações.');
                return;
            }

            const valorPorKm = parseNonNegativeNumber(freteConfig.valorPorKm);
            const valorMinimoFrete = parseNonNegativeNumber(freteConfig.valorMinimoFrete);

            const sanitizedFreteConfig = {
                ...freteConfig,
                valorPorKm,
                valorMinimoFrete,
            };

            const freteDoc = getStoreConfigDocRef(effectiveStoreId);
            await setDoc(freteDoc, {
                frete: {
                    ...sanitizedFreteConfig,
                    updatedAt: new Date(),
                    updatedBy: user?.auth?.email || 'Sistema'
                }
            }, { merge: true });
            await setDoc(doc(db, 'lojas', effectiveStoreId, 'info', 'dados'), {
                frete: {
                    ...sanitizedFreteConfig,
                    updatedAt: new Date(),
                    updatedBy: user?.auth?.email || 'Sistema'
                }
            }, { merge: true });
            await setDoc(doc(db, 'lojas', effectiveStoreId, 'configuracoes', 'frete'), {
                ...sanitizedFreteConfig,
                updatedAt: new Date(),
                updatedBy: user?.auth?.email || 'Sistema'
            }, { merge: true });
            setFreteConfig((prev) => ({ ...prev, valorPorKm, valorMinimoFrete }));
            alert('Configurações de frete salvas com sucesso!');
        } catch (error) {
            console.error("Erro ao salvar frete:", error);
            alert('Ocorreu um erro ao salvar as configurações.');
        } finally {
            setIsSavingFrete(false);
        }
    };

    const processedLogs = useMemo(() => {
        if (!data.logs || !Array.isArray(data.logs)) return [];
        return data.logs.map(log => {
            const { action = '', details = '' } = log;
            let formattedDetails = details;
            
            const updateMatch = details.match(/alterações: (\{.*\})/);
            if (action.includes('atualizado') && updateMatch) {
                try {
                    const changes = JSON.parse(updateMatch[1]);
                    const field = Object.keys(changes)[0];
                    const { old: oldVal, new: newVal } = changes[field];
                    const idMatch = details.match(/ID (\w+)/);
                    const id = idMatch ? idMatch[1].substring(0,8) + '...' : 'ID desconhecido';

                    formattedDetails = `Item "ID ${id}" atualizado (${field}: "${oldVal}" para "${newVal}")`;
                } catch (e) { 
                }
            } else {
                const idMatch = details.match(/ID:? (\w+)/);
                if (idMatch) {
                    const id = idMatch[1];
                    let collectionName = null;
                    if (action.includes('produtos')) collectionName = 'produtos';
                    else if (action.includes('clientes')) collectionName = 'clientes';
                    else if (action.includes('pedidos')) collectionName = 'pedidos';
                    else if (action.includes('fornecedores')) collectionName = 'fornecedores';
                    else if (action.includes('estoque')) collectionName = 'estoque';
                    else if (action.includes('cupons')) collectionName = 'cupons';

                    if (collectionName && data[collectionName] && Array.isArray(data[collectionName])) {
                        const item = data[collectionName].find(d => d.id === id);
                        if (item) {
                            const itemName = item.nome || item.clienteNome || item.codigo || `(ID: ${id})`;
                            formattedDetails = details.replace(`ID: ${id}`, `"${itemName}"`).replace(`ID ${id}`, `"${itemName}"`);
                        }
                    }
                }
            }
            
            return {
                ...log,
                user: log.userEmail || 'Não registrado',
                formattedDetails: formattedDetails,

                lojaNome: log.lojaId ? (storeInfoMap[log.lojaId]?.nome || log.lojaId) : effectiveStoreName
            };
        }).sort((a, b) => {
            const dateA = getJSDate(a.timestamp) || new Date(0);
            const dateB = getJSDate(b.timestamp) || new Date(0);
            return dateB - dateA;
        });

    }, [data, storeInfoMap, effectiveStoreName]);


    useEffect(() => {
        if (activeTab !== 'funcionamento') return undefined;
        if (!effectiveStoreId || effectiveStoreId === STORE_ALL_KEY) {
            setStoreAvailabilityConfig(null);
            return undefined;
        }

        setStoreAvailabilityLoading(true);
        const unsubscribe = onSnapshot(doc(db, 'lojas', effectiveStoreId), (snapshot) => {
            setStoreAvailabilityConfig(snapshot.exists() ? (snapshot.data() || {}) : null);
            setStoreAvailabilityLoading(false);
        }, () => {
            setStoreAvailabilityLoading(false);
        });

        return () => unsubscribe();
    }, [activeTab, effectiveStoreId]);

    const userColumns = [
        { header: "Nome", key: "nome" },
        { header: "Email", key: "email" },
        { header: "Permissão", render: (row) => <span className={`px-3 py-1 rounded-full text-xs font-medium ${normalizeRole(row.role) === ROLE_OWNER ? 'bg-purple-100 text-purple-800' : normalizeRole(row.role) === ROLE_MANAGER ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{normalizeRole(row.role)}</span> },
        { header: "Loja", render: (row) => {
            const lojas = Array.isArray(row.lojaIds) ? row.lojaIds : (row.lojaId ? [row.lojaId] : []);
            if (normalizeRole(row.role) === ROLE_OWNER && lojas.length === 0) {
                return 'Todas as lojas';
            }
            if (!lojas.length) {
                return 'Não definida';
            }
            return lojas.map((id) => storeInfoMap[id]?.nome || id).join(', ');
        } }
    ];
    const userActions = [ 
        { icon: Edit, label: "Editar", onClick: handleEditUser }, 
        { icon: Key, label: "Alterar Senha", onClick: (u) => { setEditingUser(u); setShowPasswordModal(true); } },
        { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => handleDeleteUser(row) }) } 
    ];
    
    const cupomColumns = [
        { header: 'Código', key: 'codigo' },
        { header: 'Desconto', render: (row) => `${row.valor || 0} ${row.tipoDesconto === 'percentual' ? '%' : 'R$'}` },
        { header: 'Uso', render: (row) => `${row.usos || 0} / ${row.limiteUso || 0}` },
        { header: 'Valor Mínimo', render: (row) => `R$ ${(row.valorMinimo || 0).toFixed(2)}` },
        { header: 'Status', render: (row) => <span className={`px-3 py-1 rounded-full text-xs font-medium ${row.status === 'Ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{row.status || 'Ativo'}</span> }
    ];
    const cupomActions = [ 
        { icon: Edit, label: "Editar", onClick: handleEditCupom }, 
        { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('cupons', row.id) }) } 
    ];

    const logColumns = [
        { header: "Data/Hora", render: (row) => {
            const date = getJSDate(row.timestamp);
            return date ? date.toLocaleString('pt-BR') : '-';
        }},
        { header: "Usuário", key: "user" },
		{ header: "Loja", render: (row) => row.lojaNome || '-' },

        { header: "Ação", key: "action" },
        { header: "Detalhes", key: "formattedDetails" },
    ];
    
    return (
        <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Configurações</h1>
              <p className="text-gray-600 mt-1">Gerencie usuários, cupons, frete, alarme e visualize os logs do sistema</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2">
                <div className="flex space-x-2">
                    <button onClick={() => setActiveTab('users')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'users' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        Usuários
                    </button>
                    <button onClick={() => setActiveTab('cupons')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'cupons' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        Cupons
                    </button>
                    <button onClick={() => setActiveTab('frete')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'frete' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        Frete
                    </button>
                    <button onClick={() => setActiveTab('funcionamento')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'funcionamento' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        Funcionamento
                    </button>
                    <button onClick={() => setActiveTab('alarme')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'alarme' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        Alarme
                    </button>
                    <button onClick={() => setActiveTab('logo')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'logo' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        Identidade / Logo
                    </button>
                    <button onClick={() => setActiveTab('logs')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'logs' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                        Logs de Atividade
                    </button>
                    {user?.role === ROLE_OWNER && (
                        <button onClick={() => setActiveTab('lojas')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'lojas' ? 'bg-pink-600 text-white' : 'hover:bg-pink-100'}`}>
                            Gerenciar Lojas
                        </button>
                    )}
                </div>
            </div>
            
            {activeTab === 'users' && (

            <div>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-4">
                    <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleClearUserFilters}
                            disabled={!hasUserFilters}
                        >
                            Limpar filtros
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Input
                            label="Buscar por nome"
                            placeholder="Ex: Ana"
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                        />
                        <Input
                            label="Excluir nome"
                            placeholder="Ex: João"
                            value={userExcludeTerm}
                            onChange={(e) => setUserExcludeTerm(e.target.value)}
                        />
                        <Select
                            label="E-mail"
                            value={userEmailFilter}
                            onChange={(e) => setUserEmailFilter(e.target.value)}
                        >
                            <option value="any">Todos</option>
                            <option value="empty">Apenas vazios</option>
                            <option value="filled">Apenas preenchidos</option>
                        </Select>
                        <Select
                            label="Permissão"
                            value={userRoleFilter}
                            onChange={(e) => setUserRoleFilter(e.target.value)}
                        >
                            <option value="all">Todas</option>
                            {userRoles.map((roleOption) => (
                                <option key={roleOption} value={roleOption}>
                                    {roleOption.charAt(0).toUpperCase() + roleOption.slice(1)}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className="flex justify-end my-4">
                    <Button onClick={handleNewUser}><Plus className="w-4 h-4" /> Novo Usuário</Button>
                </div>
				
                {(!usuarios || usuarios.length === 0) ? (
                    <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                        <p className="text-gray-500">Nenhum usuário encontrado.</p>
                        <p className="text-sm text-gray-400 mt-2">Clique em "Novo Usuário" para criar o primeiro usuário.</p>
                    </div>

				) : (filteredUsuarios.length === 0 ? (
                    <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                        <p className="text-gray-500">Nenhum usuário corresponde aos filtros selecionados.</p>
                        <p className="text-sm text-gray-400 mt-2">Ajuste os filtros ou limpe-os para visualizar todos os usuários.</p>
                    </div>								
                ) : (
					<Table columns={userColumns} data={filteredUsuarios} actions={userActions} />
                ))}
                  
              </div>
            )}
            
            {activeTab === 'cupons' && (
              <div>

                {!effectiveStoreId ? (
                    <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                        <p className="text-gray-500">Selecione uma loja no topo da página para gerenciar os cupons.</p>
                    </div>
                ) : (
                    <>
                        <div className="flex justify-between items-center my-4">
                            <p className="text-sm text-gray-500">Gerenciando cupons da loja <strong>{effectiveStoreName}</strong></p>
                            <Button onClick={handleNewCupom}><Ticket className="w-4 h-4" /> Novo Cupom</Button>
                        </div>
                        {(!cupons || cupons.length === 0) ? (
                            <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                                <p className="text-gray-500">Nenhum cupom cadastrado.</p>
                                <p className="text-sm text-gray-400 mt-2">Clique em "Novo Cupom" para criar o primeiro cupom.</p>
                            </div>
                        ) : (
                            <Table columns={cupomColumns} data={cupons} actions={cupomActions} />
                        )}
                    </>
                )}
              </div>
            )}

            {activeTab === 'alarme' && (
                !effectiveStoreId ? (
                    <div className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-center text-gray-500">
                        Selecione uma loja para configurar o tempo de pausa do alarme.
                    </div>
                ) : (
                    <div className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 max-w-xl">
                        <form onSubmit={handleSaveAlarmSnoozeConfig} className="space-y-4">
                            <h3 className="text-xl font-bold text-gray-800">Tempo de pausa do alarme</h3>
                            <p className="text-sm text-gray-500">
                                Loja atual: <strong>{effectiveStoreName}</strong>. Defina por quantos minutos o alarme ficará em soneca.
                            </p>
                            <Input
                                label="Tempo de pausa (minutos)"
                                type="number"
                                min={MIN_ALARM_SNOOZE_MINUTES}
                                max={MAX_ALARM_SNOOZE_MINUTES}
                                value={alarmSnoozeInput}
                                onChange={(e) => setAlarmSnoozeInput(e.target.value)}
                                disabled={!canEditAlarmSnooze || isSavingAlarmSnooze}
                            />
                            {!canEditAlarmSnooze && (
                                <p className="text-sm text-amber-600">Você não tem permissão para editar esta configuração.</p>
                            )}
                            {alarmSnoozeMessage && (
                                <p className={`text-sm ${alarmSnoozeMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                    {alarmSnoozeMessage.text}
                                </p>
                            )}
                            <div className="pt-2">
                                <Button type="submit" disabled={!canEditAlarmSnooze || isSavingAlarmSnooze}>
                                    <Save className="w-4 h-4" /> {isSavingAlarmSnooze ? 'Salvando...' : 'Salvar tempo de pausa'}
                                </Button>
                            </div>
                        </form>
                    </div>
                )
            )}


            {activeTab === 'funcionamento' && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6"> 
                    {!effectiveStoreId || effectiveStoreId === STORE_ALL_KEY ? (
                        <p className="text-sm text-gray-600">Selecione uma loja específica para configurar o horário de funcionamento.</p>
                    ) : storeAvailabilityLoading ? (
                        <p className="text-sm text-gray-600">Carregando configuração de funcionamento...</p>
                    ) : (
                        <StoreOperatingHoursPanel
                            storeId={effectiveStoreId}
                            currentStoreConfig={storeAvailabilityConfig}
                            user={{ role: user?.role, uid: user?.auth?.uid }}
                        />
                    )}
                </div>
            )}

            {activeTab === 'logo' && (
                !effectiveStoreId ? (
                    <div className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-center text-gray-500">
                        Selecione uma loja para editar a logo.
                    </div>
                ) : (
                    <div className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 max-w-2xl space-y-4">
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">Identidade da loja</h3>
                            <p className="text-sm text-gray-500">Loja atual: <strong>{effectiveStoreName}</strong></p>
                        </div>
                        <div className="border border-dashed border-gray-300 rounded-xl p-4 bg-gray-50 flex items-center justify-center min-h-32">
                            {currentStoreLogo ? (
                                <img src={currentStoreLogo} alt="Logo da loja" className="max-h-24 max-w-full object-contain" />
                            ) : (
                                <img src="logotipo.png" alt="Logo padrão" className="max-h-24 max-w-full object-contain opacity-80" />
                            )}
                        </div>
                        <p className="text-xs text-gray-500">Formatos aceitos: PNG, JPG ou SVG. Tamanho máximo: 1MB. Recomendado: 600x200 px.</p>

                        {logoMessage && (
                            <p className={`text-sm ${logoMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                {logoMessage.text}
                            </p>
                        )}

                        {!canEditStoreLogo ? (
                            <p className="text-sm text-amber-600">Você não tem permissão para alterar a logo.</p>
                        ) : (
                            <div className="flex flex-wrap gap-3">
                                <input
                                    ref={logoFileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                    className="hidden"
                                    onChange={handleUploadStoreLogo}
                                />
                                <Button type="button" onClick={() => logoFileInputRef.current?.click()} disabled={isSavingLogo}>
                                    <ImageIcon className="w-4 h-4" /> {isSavingLogo ? 'Enviando...' : 'Enviar/Alterar logo'}
                                </Button>
                                <Button type="button" variant="secondary" onClick={handleRemoveStoreLogo} disabled={isSavingLogo}>
                                    <Trash2 className="w-4 h-4" /> Remover logo
                                </Button>
                            </div>
                        )}
                    </div>
                )
            )}

            {activeTab === 'logs' && (
                <div className="mt-4">
                    {(!data.logs || data.logs.length === 0) ? (
                        <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
                            <p className="text-gray-500">Nenhum log de atividade encontrado.</p>
                            <p className="text-sm text-gray-400 mt-2">Os logs aparecerão conforme você usar o sistema.</p>
                        </div>
                    ) : (
                        <Table columns={logColumns} data={processedLogs} />
                    )}
                </div>
            )}
            
            {activeTab === 'lojas' && user?.role === ROLE_OWNER && (
                <div className="mt-4 space-y-4"> 
                    <div className="flex justify-end"> 
                        <Button onClick={openCreateStoreModal}> 
                            <Plus className="w-4 h-4" /> Nova Loja
                        </Button>
                    </div>
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"> 
                        <table className="min-w-full divide-y divide-gray-200"> 
                            <thead className="bg-gray-50"> 
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nome</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Criada em</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Atualizada em</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100"> 
                                {sortedStores.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">Nenhuma loja cadastrada.</td>
                                    </tr>
                                ) : sortedStores.map((store) => {
                                    const createdAt = getJSDate(store.createdAt);
                                    const updatedAt = getJSDate(store.updatedAt);
                                    const status = store.isDeleted ? 'Inativa' : (store.status || (store.ativo === false ? 'Inativa' : 'Ativa'));

                                    return (
                                        <tr key={store.storeId}>
                                            <td className="px-4 py-3 text-sm text-gray-800 font-medium">{store.nome || store.storeId}</td>
                                            <td className="px-4 py-3 text-xs text-gray-500 font-mono">{store.storeId}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{status}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{createdAt ? createdAt.toLocaleDateString('pt-BR') : '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{updatedAt ? updatedAt.toLocaleDateString('pt-BR') : '-'}</td>
                                            <td className="px-4 py-3"> 
                                                <div className="flex justify-end gap-2"> 
                                                    <Button size="sm" variant="secondary" onClick={() => openEditStoreModal(store)}>
                                                        <Edit className="w-4 h-4" /> Editar
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        disabled={isDefaultStore(store)}
                                                        onClick={() => {
                                                            setStorePendingDelete(store);
                                                            setDeleteStoreInput('');
                                                            setDeleteStoreError('');
                                                        }}
                                                    >
                                                        <Trash2 className="w-4 h-4" /> Excluir
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'frete' && (

                !effectiveStoreId ? (
                    <div className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-center text-gray-500">
                        Selecione uma loja no topo da página para configurar o frete.
                    </div>
                ) : (
                    <div className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                        <form onSubmit={handleSaveFreteConfig} className="space-y-4 max-w-lg">
                            <h3 className="text-xl font-bold text-gray-800">Configurações de Entrega</h3>
                            <p className="text-sm text-gray-500">
                                Defina o endereço de partida dos seus pedidos e o valor cobrado por quilômetro. As coordenadas podem ser encontradas no Google Maps.
                            </p>
                            <Input
                                label="Endereço da Loja (para referência)"
                                placeholder="Av. Comercial, 433 - Jardim Nova Esperança, Goiânia - GO"
                                value={freteConfig.enderecoLoja || ''}
                                onChange={e => setFreteConfig({ ...freteConfig, enderecoLoja: e.target.value })}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Latitude da Loja"
                                    placeholder="-16.6725019"
                                    value={freteConfig.lat || ''}
                                    onChange={e => setFreteConfig({ ...freteConfig, lat: e.target.value })}
                                    required
                                />
                                <Input
                                    label="Longitude da Loja"
                                    placeholder="-49.3274707"
                                    value={freteConfig.lng || ''}
                                    onChange={e => setFreteConfig({ ...freteConfig, lng: e.target.value })}
                                    required
                                />
                            </div>
                            <Input
                                label="Valor por KM (R$)"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Ex: 1.50"
                                value={freteConfig.valorPorKm || ''}
                                onChange={e => setFreteConfig({ ...freteConfig, valorPorKm: e.target.value.replace(',', '.') })}
                                required
                            />
                            <Input
                                label="Valor mínimo do frete (R$)"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Ex: 5.00"
                                value={freteConfig.valorMinimoFrete ?? ''}
                                onChange={e => setFreteConfig({ ...freteConfig, valorMinimoFrete: e.target.value.replace(',', '.') })}
                            />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input 
                                label="Latitude da Loja" 
                                placeholder="-16.6725019"
                                value={freteConfig.lat || ''} 
                                onChange={e => setFreteConfig({ ...freteConfig, lat: e.target.value })} 
                                required 
                            />
                            <Input 
                                label="Longitude da Loja" 
                                placeholder="-49.3274707"
                                value={freteConfig.lng || ''} 
                                onChange={e => setFreteConfig({ ...freteConfig, lng: e.target.value })} 
                                required 
                            />
                        </div>
                            <div className="pt-4">
                                <Button type="submit" disabled={isSavingFrete}>
                                    <Save className="w-4 h-4" /> {isSavingFrete ? 'Salvando...' : 'Salvar Configurações'}
                                </Button>
                            </div>
                        </form>
                    </div>
                )
            )}
            
                        <Modal isOpen={showStoreModal} onClose={() => setShowStoreModal(false)} title={editingStoreId ? 'Editar Loja' : 'Nova Loja'}>
                <form onSubmit={handleSaveStore} className="space-y-4">
                    <Input
                        label="Nome da loja"
                        value={storeFormData.nome}
                        onChange={(e) => setStoreFormData((prev) => ({ ...prev, nome: e.target.value }))}
                        required
                    />
                    <Input
                        label="Identificador (opcional)"
                        value={storeFormData.slug}
                        onChange={(e) => setStoreFormData((prev) => ({ ...prev, slug: normalizeStoreIdentifier(e.target.value) }))}
                        placeholder="Ex: loja-centro"
                    />
                    {storeIdentifierError && <p className="text-xs text-red-500">{storeIdentifierError}</p>}
                    <div className="space-y-1">
                        <p className="text-xs text-gray-600">
                            {storeAvailabilityLoading ? '⏳ Verificando disponibilidade...' : storeAvailabilityConfig?.available ? '✅ Nome disponível' : (storeAvailabilityConfig?.reason ? '❌ Nome indisponível' : 'Digite o nome para validar disponibilidade')}
                        </p>
                        {storeAvailabilityConfig?.suggestion && (
                            <p className="text-xs text-amber-600">Sugestão: {storeAvailabilityConfig.suggestion}</p>
                        )}
                        <p className="text-xs text-gray-500">URL da loja: apfood.com.br/{buildStorePublicPath(tenantSlugPreview, storeSlugSuggestion || 'loja')}</p>
                    </div>
                    {storeFormError && <p className="text-sm text-red-500">{storeFormError}</p>}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={() => setShowStoreModal(false)}>Cancelar</Button>
                        <Button type="submit" disabled={isSavingStore || !storeFormData.nome.trim() || Boolean(storeIdentifierError) || !storeAvailabilityConfig?.available}>
                            <Save className="w-4 h-4" /> {isSavingStore ? 'Salvando...' : (editingStoreId ? 'Salvar loja' : 'Criar loja')}
                        </Button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={Boolean(storePendingDelete)} onClose={() => setStorePendingDelete(null)} title="Confirmar exclusão de loja">
                <div className="space-y-4">
                    <p className="text-sm text-gray-700">Tem certeza que deseja excluir a loja <strong>{storePendingDelete?.nome || storePendingDelete?.storeId}</strong>?</p>
                    <Input
                        label="Digite o nome da loja para confirmar"
                        value={deleteStoreInput}
                        onChange={(e) => {
                            setDeleteStoreInput(e.target.value);
                            if (deleteStoreError) setDeleteStoreError('');
                        }}
                        placeholder={storePendingDelete?.nome || storePendingDelete?.storeId || ''}
                    />
                    {deleteStoreError && <p className="text-sm text-red-500">{deleteStoreError}</p>}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={() => setStorePendingDelete(null)}>Cancelar</Button>
                        <Button type="button" variant="danger" onClick={handleDeleteStore} disabled={isDeletingStore}>
                            <Trash2 className="w-4 h-4" /> {isDeletingStore ? 'Excluindo...' : 'Excluir loja'}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)} title={editingUser ? "Editar Usuário" : "Novo Usuário"}>
                 <form onSubmit={handleUserSubmit} className="space-y-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700" title="Escolha um usuário já cadastrado para carregar os dados automaticamente">Selecionar usuário existente</label>
                        <select
                            value={selectedExistingUserId}
                            onChange={(e) => handleExistingUserSelect(e.target.value)}
                            className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        >
                            <option value="">Cadastrar novo usuário</option>
                            {(usuarios || []).map((u) => (
                                <option key={u.uid || u.id} value={u.uid || u.id}>
                                    {(u.nome || u.email || 'Usuário').trim()} • {(u.email || 'sem email')} ({normalizeRole(u.role)})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500">Use esta lista para localizar rapidamente um perfil e editar as permissões personalizadas.</p>
                    </div>
                    <Input label="Nome" value={userFormData.nome || ''} onChange={e => setUserFormData({...userFormData, nome: e.target.value})} required />
                    <Input
                        label="Email"
                        type="email"
                        value={userFormData.email || ''}
                        onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                        required
                    />
                    {!editingUser && (
                        <Input
                            label="Senha"
                            type="password"
                            value={userFormData.senha || ''}
                            onChange={(e) => setUserFormData({...userFormData, senha: e.target.value})}
                            required
                            minLength="6"
                            placeholder="Mínimo 6 caracteres"
                        />
                    )}

                    <Select
                        label="Permissão"
                        value={userFormData.role || ROLE_ATTENDANT}
                        onChange={(e) => {
                            const newRole = normalizeRole(e.target.value);
                            if (newRole === ROLE_OWNER) {
                                setUserFormData({
                                    ...userFormData,
                                    role: newRole,
                                    lojaId: '',
                                    lojaIds: userFormData.lojaIds || [],
                                    permissions: getDefaultPermissionsForRole(newRole)
                                });
                            } else {
                                setUserFormData({
                                    ...userFormData,
                                    role: newRole,
                                    lojaId: userFormData.lojaId || effectiveStoreId || '',
                                    lojaIds: userFormData.lojaId ? [userFormData.lojaId] : (effectiveStoreId ? [effectiveStoreId] : []),
                                    permissions: getDefaultPermissionsForRole(newRole)
                                });
                            }
                        }}
                        required
                    >
                        <option value={ROLE_ATTENDANT}>Atendente</option>
                        <option value={ROLE_MANAGER}>Gerente</option>
                        <option value={ROLE_OWNER}>Dono</option>
                    </Select>

                    {normalizeRole(userFormData.role) === ROLE_OWNER ? (
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">Lojas com acesso</label>
                            <select
                                multiple
                                value={userFormData.lojaIds || []}
                                onChange={(e) => {
                                    const values = Array.from(e.target.selectedOptions).map(opt => opt.value);
                                    setUserFormData({ ...userFormData, lojaIds: values });
                                }}
                                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                            >
                                {availableStores.map(storeId => (
                                    <option key={storeId} value={storeId}>{storeInfoMap[storeId]?.nome || storeId}</option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500">Deixe sem seleção para conceder acesso a todas as lojas.</p>
                        </div>
                    ) : (
                        <Select
                            label="Loja"
                            value={userFormData.lojaId || ''}
                            onChange={(e) => setUserFormData({ ...userFormData, lojaId: e.target.value })}
                            required
                            disabled={!availableStores.length}
                        >
                            <option value="">Selecione uma loja</option>
                            {availableStores.map(storeId => (
                                <option key={storeId} value={storeId}>{storeInfoMap[storeId]?.nome || storeId}</option>
                            ))}
                        </Select>
                    )}
                    {!availableStores.length && normalizeRole(userFormData.role) !== ROLE_OWNER && (
                        <p className="text-xs text-red-500">Nenhuma loja disponível. Ajuste a seleção no topo da página antes de criar o usuário.</p>
                    )}

                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">Permissões personalizadas</p>
                                <p className="text-xs text-gray-500">Selecione quais menus o usuário pode acessar.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="flex items-center gap-2 text-xs text-gray-600" title="Ative para personalizar o menu deste usuário">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(userFormData.applyCustomProfile)}
                                        onChange={(e) => {
                                            const useCustom = e.target.checked;
                                            setUserFormData((prev) => ({
                                                ...prev,
                                                applyCustomProfile: useCustom,
                                                permissions: useCustom ? prev.permissions : getDefaultPermissionsForRole(prev.role)
                                            }));
                                        }}
                                    />
                                    Ativar perfil personalizado
                                </label>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    title="Voltar para o padrão do papel selecionado"
                                    onClick={() => setUserFormData({
                                        ...userFormData,
                                        permissions: getDefaultPermissionsForRole(userFormData.role),
                                        applyCustomProfile: false,
                                    })}
                                >
                                    Usar padrão do papel
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    title="Recarrega a checklist com o padrão do papel, mantendo o perfil personalizado ativo"
                                    onClick={() => setUserFormData({
                                        ...userFormData,
                                        permissions: getDefaultPermissionsForRole(userFormData.role),
                                        applyCustomProfile: true,
                                    })}
                                >
                                    Restaurar checklist
                                </Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {allMenuItems.map((item) => (
                                <label
                                    key={item.id}
                                    className={`flex items-center gap-2 text-sm text-gray-700 ${!userFormData.applyCustomProfile ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    title={`Permitir acesso ao menu "${item.label}"`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={Boolean(userFormData.permissions?.[item.id])}
                                        disabled={!userFormData.applyCustomProfile}
                                        onChange={(e) => {
                                            setUserFormData({
                                                ...userFormData,
                                                permissions: {
                                                    ...sanitizePermissions(userFormData.permissions, userFormData.role),
                                                    [item.id]: e.target.checked
                                                }
                                            });
                                        }}
                                    />
                                    {item.label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="secondary" type="button" onClick={() => setShowUserModal(false)}>Cancelar</Button>
                        <Button type="submit">
                            <Save className="w-4 h-4" />
                            Salvar
                        </Button>
                    </div>
                </form>
            </Modal>
            
            <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Alterar Senha" size="sm">
                <form onSubmit={handlePasswordChange} className="space-y-4">
                    <Input label="Nova Senha" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required placeholder="Mínimo 6 caracteres" />
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="secondary" type="button" onClick={() => setShowPasswordModal(false)}>Cancelar</Button>
                        <Button type="submit"><Save className="w-4 h-4"/> Alterar</Button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={showCupomModal} onClose={() => setShowCupomModal(false)} title={editingCupom ? "Editar Cupom" : "Novo Cupom"} size="lg">
                <form onSubmit={handleCupomSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input 
                            label="Nome/Código do Cupom" 
                            value={cupomFormData.codigo || ''} 
                            onChange={e => setCupomFormData({ ...cupomFormData, codigo: e.target.value.toUpperCase() })} 
                            required 
                            disabled={!!editingCupom}
                        />
                        <Select label="Tipo de Desconto" value={cupomFormData.tipoDesconto || 'percentual'} onChange={e => setCupomFormData({ ...cupomFormData, tipoDesconto: e.target.value })}>
                            <option value="percentual">Percentual (%)</option>
                            <option value="fixo">Valor Fixo (R$)</option>
                        </Select>
                        <Input label="Valor do Desconto" type="number" step="0.01" value={cupomFormData.valor || ''} onChange={e => setCupomFormData({ ...cupomFormData, valor: e.target.value })} required />
                        <Input label="Quantidade Máxima de Uso" type="number" value={cupomFormData.limiteUso || ''} onChange={e => setCupomFormData({ ...cupomFormData, limiteUso: e.target.value })} required />
                        <Input label="Valor Mínimo do Pedido (R$)" type="number" step="0.01" value={cupomFormData.valorMinimo || ''} onChange={e => setCupomFormData({ ...cupomFormData, valorMinimo: e.target.value })} required />
                        <Select label="Status" value={cupomFormData.status || 'Ativo'} onChange={e => setCupomFormData({ ...cupomFormData, status: e.target.value })}>
                            <option value="Ativo">Ativo</option>
                            <option value="Inativo">Inativo</option>
                        </Select>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="secondary" type="button" onClick={() => setShowCupomModal(false)}>Cancelar</Button>
                        <Button type="submit"><Save className="w-4 h-4" /> Salvar</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
  };
  
  const Pedidos = () => {
    // Helper para obter a data de hoje no formato YYYY-MM-DD
    const getTodayString = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [searchTerm, setSearchTerm] = usePersistentState("pedidos_searchTerm", "");
    // **MELHORIA:** O padrão inicial continua sendo o dia de hoje.
    const [startDateFilter, setStartDateFilter] = usePersistentState("pedidos_startDateFilter", getTodayString());
    const [endDateFilter, setEndDateFilter] = usePersistentState("pedidos_endDateFilter", getTodayString());

    const [statusFilter, setStatusFilter] = usePersistentState("pedidos_statusFilter", 'Todos');
    const [showModal, setShowModal] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [formData, setFormData] = useState({ clienteId: '', clienteNome: '', itens: [], subtotal: 0, desconto: 0, total: 0, status: 'Pendente', origem: 'Manual', categoria: 'Delivery', dataEntrega: '', observacao: '', formaPagamento: 'Pix', cupom: null });
    const [viewingOrder, setViewingOrder] = useState(null);
            const [orderToSendToDeliverer, setOrderToSendToDeliverer] = useState(null);
    const [descontoValor, setDescontoValor] = useState('');
    const [descontoPercentual, setDescontoPercentual] = useState('');
    const [productSearchTerm, setProductSearchTerm] = useState('');

    const deliveryProviders = useMemo(
        () => (data.fornecedores || []).filter(f => (f.status || 'Ativo') !== 'Inativo'),
        [data.fornecedores]
    );

    const canSendToDeliverer = (order) => {
        if (!order) return false;
        const { enderecoTexto } = getOrderAddressDetails(order, data.clientes);
        if (!enderecoTexto || enderecoTexto === 'Não informado' || enderecoTexto === 'Retirar na Loja') {
            return false;
        }
        return deliveryProviders.length > 0;
    };

    const pedidosComNomes = (data.pedidos || []).map(pedido => {
        const cliente = data.clientes.find(c => c.id === pedido.clienteId);
        return { ...pedido, clienteNome: cliente ? cliente.nome : (pedido.clienteNome || 'Cliente não encontrado') };
    });

    const filteredProducts = useMemo(() => {
        const term = productSearchTerm.trim().toLowerCase();

        return (data.produtos || [])
            .filter(p => p.categoria === formData.categoria && p.status === 'Ativo')
            .filter(p => {
                if (!term) return true;

                const nome = (p.nome || '').toLowerCase();
                const descricao = (p.descricao || '').toLowerCase();

                return nome.includes(term) || descricao.includes(term);
            })
            .sort((a, b) => a.nome.localeCompare(b.nome, undefined, { sensitivity: 'base' }));
    }, [data.produtos, formData.categoria, productSearchTerm]);

    const filteredOrders = useMemo(() => pedidosComNomes.filter(p => {
        // **MELHORIA:** Lógica de busca por nome do cliente OU ID do pedido
        const term = searchTerm.toLowerCase();
        const searchMatch = !term ||
            (p.clienteNome && p.clienteNome.toLowerCase().includes(term)) ||
            (p.id && p.id.toLowerCase().includes(term));
        
        const dateMatch = (() => {
            if (!startDateFilter && !endDateFilter) return true;
            
            const orderDate = getJSDate(p.createdAt);
            if (!orderDate) return false;
            
            const startDate = startDateFilter ? new Date(startDateFilter) : null;
            if(startDate) startDate.setHours(0, 0, 0, 0);

            const endDate = endDateFilter ? new Date(endDateFilter) : null;
            if(endDate) endDate.setHours(23, 59, 59, 999);

            if (startDate && orderDate < startDate) return false;
            if (endDate && orderDate > endDate) return false;
            
            return true;
        })();

        const statusMatch = statusFilter === 'Todos' || p.status === statusFilter;

        return searchMatch && dateMatch && statusMatch;
    }).sort((a, b) => {
        const dateA = getJSDate(a.createdAt) || 0;
        const dateB = getJSDate(b.createdAt) || 0;
        return dateB - dateA; // Mais recentes primeiro
    }), [pedidosComNomes, searchTerm, startDateFilter, endDateFilter, statusFilter]);

    const resetForm = () => {
        setEditingOrder(null);
        setFormData({ clienteId: '', clienteNome: '', itens: [], subtotal: 0, desconto: 0, total: 0, status: 'Pendente', origem: 'Manual', categoria: 'Delivery', dataEntrega: '', observacao: '', formaPagamento: 'Pix', cupom: null });
        setDescontoValor('');
        setDescontoPercentual('');
        setProductSearchTerm('');
    };
    
    // **MELHORIA:** Função para limpar todos os filtros, incluindo as datas
    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('Todos');
        setStartDateFilter('');
        setEndDateFilter('');
    };


    const handleNewOrder = () => {
        resetForm();
        setShowModal(true);
    };

    const handleAddItemToOrder = (produto) => {
      setFormData(prev => {
          const existingItem = prev.itens.find(item => item.id === produto.id);
          let newItens;
          if (existingItem) {
              newItens = prev.itens.map(item =>
                  item.id === produto.id ? { ...item, quantity: item.quantity + 1 } : item
              );
          } else {
              newItens = [...prev.itens, { ...produto, quantity: 1 }];
          }
          const newSubtotal = newItens.reduce((sum, item) => sum + ((item.preco || 0) * (item.quantity || 1)), 0);
          const currentDiscount = prev.cupom?.valorDesconto || prev.desconto || 0;
          const newTotal = newSubtotal - currentDiscount;
          return { ...prev, itens: newItens, subtotal: newSubtotal, total: newTotal };
      });
    };

    const handleRemoveItemFromOrder = (produtoId) => {
        setFormData(prev => {
            const newItens = prev.itens.filter(item => item.id !== produtoId);
            const newSubtotal = newItens.reduce((sum, item) => sum + ((item.preco || 0) * (item.quantity || 1)), 0);
             const currentDiscount = prev.cupom?.valorDesconto || prev.desconto || 0;
            const newTotal = newSubtotal - currentDiscount;
            return { ...prev, itens: newItens, subtotal: newSubtotal, total: newTotal };
        });
    };
    
    const handleApplyDiscount = () => {
        const valor = parseFloat(descontoValor) || 0;
        const percent = parseFloat(descontoPercentual) || 0;
        const subtotal = formData.subtotal || 0;

        if (valor > 0 && percent > 0) {
            alert("Por favor, aplique o desconto em valor OU em percentual, não ambos.");
            return;
        }

        let newDiscount = 0;
        if (valor > 0) {
            newDiscount = valor;
        } else if (percent > 0) {
            newDiscount = (subtotal * percent) / 100;
        }

        if (newDiscount > subtotal) {
            alert("O desconto não pode ser maior que o subtotal.");
             setDescontoValor('');
             setDescontoPercentual('');
            return;
        }
        
        if (newDiscount < 0) {
            alert("O desconto não pode ser negativo.");
             setDescontoValor('');
             setDescontoPercentual('');
            return;
        }

        setFormData(prev => ({
            ...prev,
            desconto: newDiscount,
            total: subtotal - newDiscount,
            cupom: null // Remove cupom se aplicar desconto manual
        }));
    };

const handleSubmit = async (e) => {
    e.preventDefault();
    // Garante que clienteNome seja definido mesmo se não for encontrado
    const clienteSelecionado = data.clientes.find(c => c.id === formData.clienteId);
    const orderData = { 
        ...formData, 
        clienteNome: clienteSelecionado ? clienteSelecionado.nome : 'Cliente não selecionado' 
    };
    
    if (editingOrder) {
        const { id, ...updateData } = orderData;
        await updateItem('pedidos', editingOrder.id, updateData);
        
        // Atualiza estoque se status mudou para/de Finalizado
        if (orderData.status === 'Finalizado' && editingOrder.status !== 'Finalizado') {
            await updateStockForOrder(orderData.itens, 'decrease');
        }
        else if (orderData.status !== 'Finalizado' && editingOrder.status === 'Finalizado') {
            await updateStockForOrder(editingOrder.itens, 'increase');
        }
    } else {
        await addItem('pedidos', orderData);
        
        // Atualiza estoque se novo pedido já é Finalizado
        if (orderData.status === 'Finalizado') {
            await updateStockForOrder(orderData.itens, 'decrease');
        }
    }
    
    setShowModal(false);
    resetForm();
};

	// Função para atualizar estoque (com verificação de item.id)
	const updateStockForOrder = async (itens, operation) => {
		if (!itens || itens.length === 0) return;
		
		try {
			for (const item of itens) {
                // --- CORREÇÃO: Adiciona verificação para item.id ---
                if (!item.id) {
                    console.warn("Item de pedido sem ID, pulando atualização de estoque:", item);
                    continue; // Pula este item e vai para o próximo
                }
				const productRef = doc(db, 'produtos', item.id);
				const productSnap = await getDoc(productRef);
				
				if (productSnap.exists()) {
					const productData = productSnap.data();
					// Garante que estoque seja um número, tratando NaN ou undefined
                    let currentStock = Number(productData.estoque);
                    if (isNaN(currentStock)) {
                        console.warn(`Estoque inválido para ${item.nome} (ID: ${item.id}). Definindo como 0.`);
                        currentStock = 0;
                    }

                    // ✅✅✅ CORREÇÃO APLICADA AQUI ✅✅✅
                    // 'const' foi mudado para 'let' para permitir reatribuição no 'if' abaixo
                    let quantityChange = Number(item.quantity || 1);
                     if (isNaN(quantityChange)) {
                        console.warn(`Quantidade inválida para ${item.nome} no pedido. Usando 1.`);
                        quantityChange = 1;
                    }
					
					let newStock = currentStock;
					
					if (operation === 'decrease') {
						newStock = Math.max(0, currentStock - quantityChange);
					} else if (operation === 'increase') {
						newStock = currentStock + quantityChange;
					}
					
                    // Só atualiza se o estoque mudou
                    if (newStock !== currentStock) {
                        await updateDoc(productRef, { estoque: newStock });
                        console.log(`Estoque atualizado: ${item.nome} - ${operation === 'decrease' ? 'Baixa' : 'Restauração'} de ${quantityChange}. Estoque anterior: ${currentStock}, Novo estoque: ${newStock}`);
                    } else {
                         console.log(`Estoque de ${item.nome} permaneceu ${currentStock}. Operação: ${operation}, Quantidade: ${quantityChange}`);
                    }
				} else {
                    console.warn(`Produto com ID ${item.id} (${item.nome || 'Nome desconhecido'}) não encontrado no estoque.`);
                }
			}
		} catch (error) {
			console.error('Erro geral ao atualizar estoque:', error);
			alert('Erro ao atualizar estoque dos produtos. Verifique o console para mais detalhes.');
		}
	};
    
    const handleEdit = (order) => {
        setEditingOrder(order);
        const subtotal = (order.itens || []).reduce((sum, item) => sum + ((item.preco || 0) * (item.quantity || 1)), 0);
        const desconto = order.cupom?.valorDesconto || order.desconto || 0;
        const total = subtotal - desconto;
        
        // Garante que todos os campos necessários estejam presentes, mesmo que vazios

        const defaultOrderData = {
            clienteId: '',
            clienteNome: '',
            itens: [],
            subtotal: 0,
            desconto: 0,
            total: 0,
            status: 'Pendente',
            origem: 'Manual',
            categoria: 'Delivery',
            dataEntrega: '',
            observacao: '',
            formaPagamento: 'Pix',
            cupom: null
        };

        setFormData({
            ...defaultOrderData,
            ...order,
            subtotal,
            desconto,
            total,
            dataEntrega: order.dataEntrega ? (getJSDate(order.dataEntrega)?.toISOString().split('T')[0] || '') : ''
        });
        
        setDescontoValor(order.desconto && !order.cupom ? String(order.desconto) : ''); // Preenche desconto manual se houver
        setDescontoPercentual(''); // Limpa percentual ao editar
        setShowModal(true);
    };

    const getStatusClass = (status) => { switch (status) { case 'Pendente': return 'bg-yellow-100 text-yellow-800'; case 'Em Produção': return 'bg-blue-100 text-blue-800'; case 'Finalizado': return 'bg-green-100 text-green-800'; case 'Cancelado': return 'bg-red-100 text-red-800'; default: return 'bg-gray-100 text-gray-800'; } };
    const columns = [ { header: "ID do Pedido", render: (row) => <span className="font-mono text-xs text-gray-500">{row.id?.substring(0, 8) || 'N/A'}</span> }, { header: "Cliente", key: "clienteNome" }, { header: "Total", render: (row) => <span className="font-semibold text-green-600">R$ {(row.total || 0).toFixed(2)}</span> }, { header: "Data", render: (row) => { const date = getJSDate(row.createdAt); return date ? date.toLocaleDateString('pt-BR') : '-'; } }, { header: "Origem", key: "origem"}, { header: "Status", render: (row) => <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(row.status)}`}>{row.status}</span> } ];
    const actions = [ { icon: Eye, label: "Ver", onClick: (row) => setViewingOrder(row) }, { icon: Edit, label: "Editar", onClick: handleEdit }, { icon: Trash2, label: "Excluir", onClick: (row) => setConfirmDelete({ isOpen: true, onConfirm: () => deleteItem('pedidos', row.id) }) } ];
    
    return (
        <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                <div><h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Gestão de Pedidos</h1><p className="text-gray-600 mt-1">Acompanhe e gerencie todos os pedidos</p></div>
                <Button onClick={handleNewOrder} className="w-full md:w-auto"><Plus className="w-4 h-4" /> Novo Pedido</Button>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 bg-white rounded-2xl shadow-lg border border-gray-100 flex-wrap">
                <div className="relative flex-grow w-full md:w-auto">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    {/* **MELHORIA:** Placeholder do campo de busca atualizado */}
                    <input type="text" placeholder="Buscar por cliente ou ID do pedido..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500" />
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto md:flex-grow">
                    <Input label="Data Inicial" type="date" value={startDateFilter} onChange={(e) => setStartDateFilter(e.target.value)} className="py-2.5"/>
                    <Input label="Data Final" type="date" value={endDateFilter} onChange={(e) => setEndDateFilter(e.target.value)} className="py-2.5"/>
                </div>
                <div className="flex-grow w-full md:w-auto">
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full">
                        <option value="Todos">Todos os Status</option>
                        <option value="Pendente">Pendente</option>
                        <option value="Em Produção">Em Produção</option>
                        <option value="Pronto para Entrega">Pronto para Entrega</option>
                        <option value="Finalizado">Finalizado</option>
                        <option value="Cancelado">Cancelado</option>
                    </Select>
                </div>
                <Button variant="secondary" onClick={handleClearFilters} className="w-full md:w-auto">
                    Limpar Filtros
                </Button>
            </div>

            <Table columns={columns} data={filteredOrders} actions={actions} />
            
            <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={editingOrder ? "Editar Pedido" : "Novo Pedido"} size="xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Select label="Cliente" value={formData.clienteId} onChange={(e) => setFormData({...formData, clienteId: e.target.value})} required><option value="">Selecione um cliente</option>{data.clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}</Select>
                        <Select label="Status" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} required><option>Pendente</option><option>Em Produção</option><option>Pronto para Entrega</option><option>Finalizado</option><option>Cancelado</option></Select>
                        <Select label="Categoria do Pedido" value={formData.categoria} onChange={(e) => setFormData({...formData, categoria: e.target.value, itens: [], total: 0})} required>
                            <option value="Delivery">Delivery</option>
                            <option value="Festa">Festa</option>
                        </Select>
                        <Select label="Forma de Pagamento" value={formData.formaPagamento} onChange={(e) => setFormData({...formData, formaPagamento: e.target.value})} required>
                            <option>Pix</option>
                            <option>Cartão de Crédito</option>
                            <option>Cartão de Débito</option>
                            <option>Dinheiro</option>
                            <option>Link de Pagamento</option>
                        </Select>
                        {formData.categoria === 'Festa' && (
                            <Input 
                                label="Data de Entrega" 
                                type="date" 
                                value={formData.dataEntrega} 
                                onChange={(e) => setFormData({...formData, dataEntrega: e.target.value})}
                                min={getTodayString()}
                                required 
                            />
                        )}
                    </div>
                     <Textarea 
                        label="Observação" 
                        rows="3" 
                        value={formData.observacao || ''} 
                        onChange={(e) => setFormData({...formData, observacao: e.target.value})} 
                        placeholder="Ex: Bolo sem cobertura, entregar para a secretária, etc."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-2">
                            <h3 className="font-semibold">Adicionar Produtos</h3>
                            <Input
                                label="Buscar produto"
                                placeholder="Buscar por nome ou descrição"
                                value={productSearchTerm}
                                onChange={(e) => setProductSearchTerm(e.target.value)}
                            />
                            <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1">
                                {filteredProducts
                                    .map(p => (<div key={p.id} className="flex justify-between items-center p-2 rounded hover:bg-gray-50"><span>{p.nome} - R$ {(p.preco || 0).toFixed(2)}</span><Button size="sm" variant="secondary" onClick={() => handleAddItemToOrder(p)}>+</Button></div>))}
                            </div>
                        </div>
                        <div className="space-y-2">
                          <h3 className="font-semibold">Itens no Pedido</h3>
                          <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1">{formData.itens.length === 0 ? <p className="text-sm text-gray-500 text-center p-4">Nenhum item</p> : formData.itens.map(item => (<div key={item.id} className="flex justify-between items-center p-2 rounded bg-pink-50"><span>{item.quantity}x {item.nome}</span><div className="flex items-center gap-2"><span className="text-sm">R$ {((item.preco || 0) * (item.quantity || 1)).toFixed(2)}</span><button type="button" onClick={() => handleRemoveItemFromOrder(item.id)} className="text-red-500"><Trash2 size={14}/></button></div></div>))}</div>
                           <div className="text-right mt-2 space-y-1">
                                <p className="text-sm text-gray-600">Subtotal: R$ {(formData.subtotal || 0).toFixed(2)}</p>
                                { (formData.cupom || formData.desconto > 0) && <p className="text-sm text-red-600">Desconto: - R$ {(formData.cupom?.valorDesconto || formData.desconto || 0).toFixed(2)}</p>}
                                {formData.cupom && <p className="text-xs text-green-600">Cupom: {formData.cupom.codigo}</p>}
                                <p className="font-bold text-lg text-gray-800">Total: R$ {(formData.total || 0).toFixed(2)}</p>
                           </div>
                        </div>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg mt-4">
                        <h4 className="font-semibold mb-2 text-gray-700">Aplicar Desconto (Manual)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <Input label="Valor do desconto (R$)" type="number" step="0.01" value={descontoValor} onChange={e => { setDescontoValor(e.target.value); setDescontoPercentual(''); setFormData(prev => ({...prev, cupom: null})); }} placeholder="Ex: 10.00" />
                            <Input label="Percentual do desconto (%)" type="number" value={descontoPercentual} onChange={e => { setDescontoPercentual(e.target.value); setDescontoValor(''); setFormData(prev => ({...prev, cupom: null})); }} placeholder="Ex: 15" />
                            <Button variant="secondary" onClick={handleApplyDiscount} className="w-full">Aplicar desconto</Button>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4"><Button variant="secondary" type="button" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</Button><Button type="submit"><Save className="w-4 h-4" />{editingOrder ? "Salvar Alterações" : "Criar Pedido"}</Button></div>
                </form>
            </Modal>
            <Modal isOpen={!!viewingOrder} onClose={() => setViewingOrder(null)} title="Detalhes do Pedido" size="lg">
                {viewingOrder && (() => {
                    const cliente = data.clientes.find(c => c.id === viewingOrder.clienteId);
                    const endereco = viewingOrder.clienteEndereco || cliente?.enderecos?.[0] || 'Não informado';
                    const telefone = viewingOrder.telefone || cliente?.telefone || '';
                    const subtotal = (viewingOrder.itens || []).reduce((sum, item) => sum + ((item.preco || 0) * (item.quantity || 1)), 0);
					const frete = parseFloat(viewingOrder.valorFrete ?? viewingOrder.frete ?? 0) || 0;

                    
                    const handleSendToWhatsApp = () => {
                        if (!telefone) {
                           alert("Telefone do cliente não encontrado para enviar mensagem.");
                           return;
                        }
            
                        const formattedPhone = telefone.replace(/\D/g, '');
                        // Adiciona 55 se não tiver, e garante que tenha 11 ou 13 dígitos (com 55)
                        const whatsappNumber = formattedPhone.length === 11 ? `55${formattedPhone}` : formattedPhone.length === 13 && formattedPhone.startsWith('55') ? formattedPhone : `55${formattedPhone}`; // Assume DDD + 9 dígitos se não tiver 55

                        let message = `Olá, *${viewingOrder.clienteNome || 'Cliente'}*!\n\n`;
                        message += `Aqui está um resumo do seu pedido na APFood:\n\n`;
                        if (endereco !== 'Não informado') {
                            message += `*Endereço de Entrega:*\n${endereco}\n\n`;
                        }
                        message += `*Itens do Pedido:*\n`;
                        (viewingOrder.itens || []).forEach(item => {
                            message += `  • ${item.quantity || 1}x ${item.nome}\n`;
                        });
                        message += `\n`;

                        if (viewingOrder.cupom?.valorDesconto > 0) {
                            message += `*Subtotal:* R$ ${subtotal.toFixed(2)}\n`;
                            message += `*Desconto (${viewingOrder.cupom.codigo}):* - R$ ${viewingOrder.cupom.valorDesconto.toFixed(2)}\n`;
                        } else if (viewingOrder.desconto > 0) {
                             message += `*Subtotal:* R$ ${subtotal.toFixed(2)}\n`;
                             message += `*Desconto Manual:* - R$ ${viewingOrder.desconto.toFixed(2)}\n`;
                        }

                        message += `*Frete:* R$ ${frete.toFixed(2)}\n`;
                        message += `*Total:* R$ ${(viewingOrder.total || 0).toFixed(2)}\n`;
                        if(viewingOrder.formaPagamento) message += `*Pagamento:* ${viewingOrder.formaPagamento}\n`;
                        message += `*Status:* ${viewingOrder.status}\n\n`;
                        if(viewingOrder.observacao) message += `*Observações:* ${viewingOrder.observacao}\n\n`;
                        
                        message += `Agradecemos a sua preferência! ❤`;

                        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
                        window.open(whatsappUrl, '_blank');
                    };
                    
                    const handlePrint = () => {
                        const printWindow = window.open('', '_blank');
                        if (!printWindow) {
                            alert("Por favor, habilite pop-ups para imprimir.");
                            return;
                        }
                        printWindow.document.write('<html><head><title>Cupom do Pedido</title>');
                        printWindow.document.write('<style> body { font-family: monospace; margin: 0; padding: 10px; width: 300px; font-size: 10pt; } h2, h3 { text-align: center; margin: 5px 0; } hr { border: none; border-top: 1px dashed black; margin: 5px 0; } table { width: 100%; border-collapse: collapse; margin-bottom: 5px;} td { padding: 1px 0; vertical-align: top;} .right { text-align: right; } p { margin: 2px 0; } .total { font-weight: bold; font-size: 11pt;} </style>');
                        printWindow.document.write('</head><body>');
                        printWindow.document.write('<h2>APFood</h2>');
                        printWindow.document.write(`<p>Cliente: ${viewingOrder.clienteNome || 'N/A'}</p>`);
                        if (endereco !== 'Não informado') printWindow.document.write(`<p>Endereço: ${endereco}</p>`);
                        if (telefone) printWindow.document.write(`<p>Telefone: ${telefone}</p>`);
                        printWindow.document.write(`<p>Data: ${getJSDate(viewingOrder.createdAt)?.toLocaleString('pt-BR') || '-'}</p>`);
                        if (viewingOrder.dataEntrega) printWindow.document.write(`<p>Entrega: ${new Date(viewingOrder.dataEntrega + 'T03:00:00Z').toLocaleDateString('pt-BR')}</p>`);
                        printWindow.document.write('<hr>');
                        printWindow.document.write('<h3>Itens</h3>');
                        printWindow.document.write('<table>');
                        (viewingOrder.itens || []).forEach(item => {
                            printWindow.document.write(`<tr><td>${item.quantity || 1}x ${item.nome}</td><td class="right">R$ ${((item.preco || 0) * (item.quantity || 1)).toFixed(2)}</td></tr>`);
                        });
                        printWindow.document.write('</table>');
                        printWindow.document.write('<hr>');

                         if (viewingOrder.cupom?.valorDesconto > 0 || viewingOrder.desconto > 0) {
                            printWindow.document.write(`<p>Subtotal:<span style="float: right;">R$ ${subtotal.toFixed(2)}</span></p>`);
                             if (viewingOrder.cupom) {
                                printWindow.document.write(`<p>Desconto (${viewingOrder.cupom.codigo}):<span style="float: right;">- R$ ${viewingOrder.cupom.valorDesconto.toFixed(2)}</span></p>`);
                            } else {
                                printWindow.document.write(`<p>Desconto:<span style="float: right;">- R$ ${viewingOrder.desconto.toFixed(2)}</span></p>`);
                            }
                        }
                        
                        printWindow.document.write(`<p class="total">Total:<span style="float: right;">R$ ${(viewingOrder.total || 0).toFixed(2)}</span></p>`);
                        if(viewingOrder.formaPagamento) printWindow.document.write(`<p>Pagamento: ${viewingOrder.formaPagamento}</p>`);

                        if(viewingOrder.observacao) {
                            printWindow.document.write(`<hr><h3>Observações:</h3><p>${viewingOrder.observacao}</p>`);
                        }
                        printWindow.document.write('<hr><p style="text-align: center;">Obrigado!</p>');
                        printWindow.document.write('</body></html>');
                        printWindow.document.close();
                         // Adiciona um pequeno delay para garantir que o conteúdo foi escrito antes de imprimir
                        setTimeout(() => {
                           printWindow.focus(); // Necessário para alguns navegadores
                           printWindow.print();
                           // printWindow.close(); // Comentar se quiser manter a janela aberta para debug
                        }, 250);
                    };

                    return (
                        <div className="space-y-4 text-sm text-gray-700">
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-bold text-lg text-gray-800 mb-2">Informações do Cliente</h3>
                                <p><strong>Nome:</strong> {viewingOrder.clienteNome || 'N/A'}</p>
                                <p><strong>Endereço:</strong> {endereco}</p>
                                <p><strong>Telefone:</strong> {telefone || 'Não informado'}</p>
                            </div>

                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-bold text-lg text-gray-800 mb-2">Informações do Pedido</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <p><strong>Status:</strong> <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusClass(viewingOrder.status)}`}>{viewingOrder.status}</span></p>
                                    <p><strong>Data do Pedido:</strong> {viewingOrder.createdAt ? getJSDate(viewingOrder.createdAt)?.toLocaleString('pt-BR') : '-'}</p>
                                    <p><strong>Origem:</strong> {viewingOrder.origem}</p>
                                    <p><strong>Pagamento:</strong> {viewingOrder.formaPagamento || 'Não informado'}</p>
                                    {viewingOrder.categoria && (<p><strong>Categoria:</strong> {viewingOrder.categoria}</p>)}
                                    {viewingOrder.dataEntrega && (<p><strong>Data de Entrega:</strong> {new Date(viewingOrder.dataEntrega + 'T03:00:00Z').toLocaleDateString('pt-BR')}</p>)}
                                </div>
                            </div>
                            
                             {viewingOrder.observacao && (
                                <div className="p-4 bg-yellow-50 rounded-lg">
                                    <h3 className="font-bold text-lg text-yellow-800 mb-2">Observações</h3>
                                    <p>{viewingOrder.observacao}</p>
                                </div>
                            )}

                            <div>
                                <h4 className="font-bold text-lg text-gray-800 mt-4 mb-2">Itens do Pedido:</h4>
                                <ul className="space-y-2">
                                    {(viewingOrder.itens || []).map((item, index) => (
                                        <li key={item.id || index} className="flex justify-between items-center p-2 bg-pink-50/50 rounded-md">
                                            <span>{item.quantity || 1}x {item.nome}</span>
                                            <span>R$ {((item.preco || 0) * (item.quantity || 1)).toFixed(2)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="text-right pt-4 border-t mt-4 space-y-1">
                                { (viewingOrder.cupom?.valorDesconto > 0 || viewingOrder.desconto > 0) && (
                                    <>
                                        <p className="text-sm text-gray-600">Subtotal: R$ {subtotal.toFixed(2)}</p>
                                        <p className="text-sm text-red-600">
                                            Desconto {viewingOrder.cupom ? `(${viewingOrder.cupom.codigo})` : ''}: 
                                            - R$ {(viewingOrder.cupom?.valorDesconto || viewingOrder.desconto || 0).toFixed(2)}
                                        </p>
                                    </>
                                )}
                                <p className="text-sm text-gray-600">Frete: R$ {frete.toFixed(2)}</p>
                                <p className="font-bold text-2xl text-pink-600">
                                    Total: R$ ${(viewingOrder.total || 0).toFixed(2)}
                                </p>
                           </div>

                            <div className="flex flex-wrap justify-end pt-4 mt-4 border-t gap-3">
                                 <Button 
                                    onClick={handlePrint}
                                    variant="secondary"
                                    size="sm"
                                >
                                    <Printer className="w-4 h-4" />
                                    Imprimir Cupom
                                </Button>
                                <Button
                                    onClick={handleSendToWhatsApp}
                                    disabled={!telefone}
                                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 disabled:shadow-none disabled:transform-none"
                                    size="sm"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    Enviar Resumo Cliente
                                </Button>
                                <Button
                                    onClick={() => setOrderToSendToDeliverer(viewingOrder)}
                                    disabled={!canSendToDeliverer(viewingOrder)}
                                    className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
                                    size="sm"
                                >
                                    <Truck className="w-4 h-4" />
                                    Enviar Endereço para Entregador
                                </Button>
                            </div>
                        </div>
                    );
                })()}
            </Modal>
            <DeliveryModal
                isOpen={!!orderToSendToDeliverer}
                order={orderToSendToDeliverer}
                clientes={data.clientes}
                fornecedores={data.fornecedores}
                onClose={() => setOrderToSendToDeliverer(null)}
            />
        </div>
    );
  }
  
  const Agenda = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDay, setSelectedDay] = useState(null);
    const [viewingOrder, setViewingOrder] = useState(null);
    const [orderToSendToDeliverer, setOrderToSendToDeliverer] = useState(null);

    const deliveryProviders = useMemo(
        () => (data.fornecedores || []).filter(f => (f.status || 'Ativo') !== 'Inativo'),
        [data.fornecedores]
    );

    const canSendToDeliverer = (order) => {
        if (!order) return false;
        const { enderecoTexto } = getOrderAddressDetails(order, data.clientes);
        if (!enderecoTexto || enderecoTexto === 'Não informado' || enderecoTexto === 'Retirar na Loja') {
            return false;
        }
        return deliveryProviders.length > 0;
    };
    
    const getStatusClass = (status) => { 
        switch (status) { 
            case 'Pendente': return 'bg-yellow-400'; 
            case 'Em Produção': return 'bg-blue-400'; 
            case 'Finalizado': return 'bg-green-400'; 
            case 'Cancelado': return 'bg-red-400'; 
            default: return 'bg-gray-400'; 
        } 
    };
    
    const getStatusClassText = (status) => { 
        switch (status) { 
            case 'Pendente': return 'bg-yellow-100 text-yellow-800'; 
            case 'Em Produção': return 'bg-blue-100 text-blue-800'; 
            case 'Finalizado': return 'bg-green-100 text-green-800'; 
            case 'Cancelado': return 'bg-red-100 text-red-800'; 
            default: return 'bg-gray-100 text-gray-800'; 
        } 
    };

    const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

    const changeMonth = (offset) => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    };

    const pedidosDoMes = (data.pedidos || []).filter(p => {
		const relevantDateStr = p.categoria === 'Festa' && p.dataEntrega ? p.dataEntrega : p.createdAt;
		let pedidoDate = getJSDate(relevantDateStr);
		if (p.categoria === 'Festa' && p.dataEntrega) {
			const [year, month, day] = p.dataEntrega.split('-');
			const y = parseInt(year, 10);
			const m = parseInt(month, 10) - 1;
			const d = parseInt(day, 10);
			pedidoDate = new Date(Date.UTC(y, m, d));
		  }
	  return pedidoDate && pedidoDate.getFullYear() === currentDate.getFullYear() &&
			 pedidoDate.getMonth() === currentDate.getMonth();
	});

    const clientes = data.clientes || [];
    const aniversariantesDoMes = useMemo(() => {
        return clientes.filter(cliente => {
            if (!cliente.aniversario || !/^\d{4}-\d{2}-\d{2}$/.test(cliente.aniversario)) return false;
            // Usa UTC para evitar problemas de fuso
            const [year, month, day] = cliente.aniversario.split('-');
            const birthMonth = parseInt(month, 10) - 1; // Mês é 0-indexado
            return birthMonth === currentDate.getMonth();
        });
    }, [data.clientes, currentDate]);

    return (
        <div className="p-4 md:p-6 space-y-6 bg-gradient-to-br from-pink-50/30 to-rose-50/30 min-h-screen">
             <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Agenda</h1>
                <p className="text-gray-600 mt-1">Visualize entregas e aniversários</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6">
                <div className="flex justify-between items-center mb-4">
                    <Button variant="secondary" size="sm" onClick={() => changeMonth(-1)}><ChevronLeft/></Button>
                    <h2 className="text-xl font-bold text-gray-800 text-center">{currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}</h2>
                    <Button variant="secondary" size="sm" onClick={() => changeMonth(1)}><ChevronRight/></Button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center text-sm font-semibold text-gray-600">
                    {daysOfWeek.map(day => <div key={day} className="py-2">{day}</div>)}
                </div>

                <div className="grid grid-cols-7 gap-1 md:gap-2">
                    {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`empty-${i}`} className="border rounded-lg aspect-square"></div>)}
                    {Array.from({ length: daysInMonth }).map((_, day) => {
                        const dayNumber = day + 1;
                        
                        const today = new Date();
                        const isToday = today.getDate() === dayNumber && today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();
                        
                        const pedidosDoDia = pedidosDoMes.filter(p => {
                            const relevantDateStr = p.categoria === 'Festa' && p.dataEntrega ? p.dataEntrega : p.createdAt;
                            const pedidoDate = getJSDate(relevantDateStr);
                            if (!pedidoDate) return false;
                            // Se for Festa, compara com UTC
                             if (p.categoria === 'Festa' && p.dataEntrega) {
                                const [year, month, d] = p.dataEntrega.split('-');
                                return parseInt(d, 10) === dayNumber;
                             }
                             // Senão, compara data local
                            return pedidoDate.getDate() === dayNumber;
                        });

                        const aniversariantesDoDia = aniversariantesDoMes.filter(c => {

                             const [, , dayString] = c.aniversario.split('-');
                             return parseInt(dayString, 10) === dayNumber;
                        });

                        const hasEvents = pedidosDoDia.length > 0 || aniversariantesDoDia.length > 0;
                        
                        return (
                            <div key={dayNumber} onClick={() => hasEvents && setSelectedDay({ day: dayNumber, pedidos: pedidosDoDia, aniversariantes: aniversariantesDoDia })} className={`border rounded-lg p-1 md:p-2 aspect-square flex flex-col ${hasEvents ? 'cursor-pointer hover:bg-pink-50' : ''} transition-colors ${isToday ? 'bg-pink-100' : ''}`}>
                                <span className={`font-bold text-xs md:text-base ${isToday ? 'text-pink-600' : 'text-gray-800'}`}>{dayNumber}</span>
                                <div className="mt-1 space-y-1 overflow-y-auto text-[10px] md:text-xs">
                                    {pedidosDoDia.map(p => (
                                        <div key={p.id} className={`w-full text-white rounded px-1 truncate ${getStatusClass(p.status)}`} title={`${p.clienteNome} (${p.status})`}>
                                            {p.categoria === 'Festa' ? <Gift size={10} className="inline mr-1"/> : <ShoppingCart size={10} className="inline mr-1"/>}
                                            {p.clienteNome}
                                        </div>
                                    ))}
                                    {aniversariantesDoDia.map(c => (
                                        <div key={c.id} className="w-full bg-yellow-300 text-yellow-800 rounded px-1 truncate flex items-center gap-1" title={`${c.nome} (Aniversário)`}>
                                            <Cake size={10} />
                                            {c.nome}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            <Modal isOpen={!!selectedDay} onClose={() => setSelectedDay(null)} title={`Eventos do dia ${selectedDay?.day}`}>
                {selectedDay && (
                    <div className="space-y-4">
                        {selectedDay.pedidos.length > 0 && (
                            <div>
                                <h3 className="font-bold text-lg mb-2 text-gray-700">Pedidos ({selectedDay.pedidos.length})</h3>
                                <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                                {selectedDay.pedidos.map(p => (
                                    <div key={p.id} onClick={() => { setSelectedDay(null); setViewingOrder(p); }} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer flex justify-between items-center">
                                        <div>
                                            <p className="font-bold flex items-center gap-1">
                                                {p.categoria === 'Festa' ? <Gift size={14} className="text-purple-500"/> : <ShoppingCart size={14} className="text-blue-500"/>}
                                                {p.clienteNome}
                                            </p>
                                            <p className="text-sm text-gray-600">Total: R$ {p.total.toFixed(2)}</p>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusClassText(p.status)}`}>{p.status}</span>
                                    </div>
                                ))}
                                </div>
                            </div>
                        )}
                        {selectedDay.aniversariantes.length > 0 && (
                             <div>
                                <h3 className="font-bold text-lg mb-2 text-gray-700">Aniversariantes ({selectedDay.aniversariantes.length})</h3>
                                 <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                                {selectedDay.aniversariantes.map(c => (
                                    <div key={c.id} className="p-3 bg-yellow-50 rounded-lg flex items-center gap-3">
                                        <Cake className="w-5 h-5 text-yellow-600" />
                                        <p className="font-semibold text-yellow-800">{c.nome}</p>
                                    </div>
                                ))}
                                </div>
                            </div>
                        )}
                        {selectedDay.pedidos.length === 0 && selectedDay.aniversariantes.length === 0 && <p>Nenhum evento para este dia.</p>}
                    </div>
                )}
            </Modal>
             {/* Modal de Detalhes do Pedido (igual ao do componente Pedidos) */}
             <Modal isOpen={!!viewingOrder} onClose={() => setViewingOrder(null)} title="Detalhes do Pedido" size="lg">
                 {/* Reutiliza a mesma lógica de exibição do modal de detalhes */}
                 {viewingOrder && (() => {
                    const cliente = data.clientes.find(c => c.id === viewingOrder.clienteId);
                    const endereco = viewingOrder.clienteEndereco || cliente?.enderecos?.[0] || 'Não informado';
                    const telefone = viewingOrder.telefone || cliente?.telefone || '';
                    const handleSendToWhatsApp = () => { /* ... (código igual ao do Pedidos.js) ... */ };
                    const handlePrint = () => { /* ... (código igual ao do Pedidos.js) ... */ };

                    return ( 
                         <div className="space-y-4 text-sm text-gray-700">
                            {/* ... (Conteúdo idêntico ao modal do Pedidos.js) ... */}
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-bold text-lg text-gray-800 mb-2">Informações do Cliente</h3>
                                <p><strong>Nome:</strong> {viewingOrder.clienteNome || 'N/A'}</p>
                                <p><strong>Endereço:</strong> {endereco}</p>
                                <p><strong>Telefone:</strong> {telefone || 'Não informado'}</p>
                            </div>
                            {/* ... (resto do conteúdo) ... */}
                             <div className="flex flex-wrap justify-end pt-4 mt-4 border-t gap-3">
                                 <Button 
                                    onClick={handlePrint}
                                    variant="secondary"
                                    size="sm"
                                >
                                    <Printer className="w-4 h-4" />
                                    Imprimir Cupom
                                </Button>
                        <Button
                    onClick={handleSendToWhatsApp}
                    disabled={!telefone}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 disabled:shadow-none disabled:transform-none"
                    size="sm"
                >
                    <MessageCircle className="w-4 h-4" />
                    Enviar Resumo Cliente
                </Button>
                <Button
                    onClick={() => setOrderToSendToDeliverer(viewingOrder)}
                    disabled={!canSendToDeliverer(viewingOrder)}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
                    size="sm"
                >
                    <Truck className="w-4 h-4" />
                    Enviar Endereço para Entregador
                </Button>
              </div>
            </div>
         );
      })()}
    </Modal>
    <DeliveryModal
        isOpen={!!orderToSendToDeliverer}
        order={orderToSendToDeliverer}
        clientes={data.clientes}
        fornecedores={data.fornecedores}
        onClose={() => setOrderToSendToDeliverer(null)}
    />
        </div>
    );
  };


  const PlaceholderPage = ({ title }) => (<div className="p-6"><h1 className="text-3xl font-bold text-pink-600">{title}</h1><p>Em desenvolvimento...</p></div>);
  const userHasPermission = useCallback((menuId) => {
    if (!user) return menuId === 'pagina-inicial';

    if (user.role === ROLE_OWNER && !user?.hasCustomProfile) return true;

    const menuItem = allMenuItems.find(item => item.id === menuId);
    const permissionKey = menuItem?.permission || menuId;

    if (user.customPermissions) {
      return Boolean(user.customPermissions[permissionKey]);
    }

    const normalizedPermissions = sanitizePermissions(user.permissions, user.role);

    if (menuItem?.roles?.includes(user.role)) {
      return true;
    }

    return Boolean(normalizedPermissions[permissionKey]);
  }, [allMenuItems, user]);

  const canCreateStores = useMemo(() => {
    if (!user) return false;
    return user.role === ROLE_OWNER || user.role === ROLE_MANAGER;
  }, [user]);

  const effectiveStoreId = useMemo(() => {
    if (!user) return null;
    if (user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
      return null;
    }
    try {
      return resolveActiveStoreForWrite();
    } catch (error) {
      return null;
    }
  }, [resolveActiveStoreForWrite, selectedStoreId, user]);

  const currentStoreIdForDisplay = useMemo(() => {
    if (!user) return null;
    if (user.role === ROLE_OWNER && selectedStoreId === STORE_ALL_KEY) {
      return STORE_ALL_KEY;
    }
    if (selectedStoreId) return selectedStoreId;
    const ids = resolveStoreIdsForView();
    return ids.length ? ids[0] : null;
  }, [user, selectedStoreId, resolveStoreIdsForView]);


  const canManageStoreAlarmConfig = useCallback((storeId) => {
    if (!user || !storeId || storeId === STORE_ALL_KEY) return false;

    if (user.role === ROLE_OWNER) return true;
    if (user.role !== ROLE_MANAGER) return false;

    const allowedStores = new Set([
      ...(Array.isArray(user.lojaIds) ? user.lojaIds : []),
      user.lojaId,
    ].filter(Boolean));

    return allowedStores.has(storeId);
  }, [user]);

  const handleSaveStoreAlarmSnoozeMinutes = useCallback(async (storeId, minutes) => {
    if (!canManageStoreAlarmConfig(storeId)) {
      throw new Error('Você não tem permissão para alterar o tempo de pausa desta loja.');
    }

    const normalizedMinutes = await saveStoreAlarmSnoozeMinutes(storeId, minutes);
    setStoreAlarmSnoozeMap((prev) => ({ ...prev, [storeId]: normalizedMinutes }));
    return normalizedMinutes;
  }, [canManageStoreAlarmConfig]);

  const handleStoreBrandingChange = useCallback((storeId, brandingPatch) => {
    if (!storeId) return;

    setStoreInfoMap((prev) => {
      const previousStore = prev[storeId] || {};
      return {
        ...prev,
        [storeId]: {
          ...previousStore,
          branding: {
            ...(previousStore.branding || {}),
            ...(brandingPatch || {})
          }
        }
      };
    });
  }, []);

  const renderCurrentPage = () => {
    if (authLoading || (loading && user)) {
      return (<div className="flex h-full w-full items-center justify-center"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-pink-500"></div></div>);
    }

    switch (currentPage) {
      case 'pagina-inicial': return <PaginaInicial />;
      case 'dashboard': return userHasPermission('dashboard') ? <Dashboard
                                        handleStopAndSnoozeAlarm={handleStopAndSnoozeAlarm}
                                        isAlarmPlaying={isAlarmPlaying}
                                        isAlarmSnoozed={isAlarmSnoozed}
                                        snoozeEndTime={snoozeEndTime}
                                        hasNewPendingOrders={hasNewPendingOrders}
                                        // --- REMOVIDO: unlockAudio e audioUnlocked ---
                                        /> : <PaginaInicial />;
      case 'clientes': return userHasPermission('clientes') ? <Clientes /> : <PaginaInicial />;
      case 'produtos': return userHasPermission('produtos') ? <Produtos /> : <PaginaInicial />;
      case 'pedidos': return userHasPermission('pedidos') ? <Pedidos /> : <PaginaInicial />;
      case 'agenda': return userHasPermission('agenda') ? <Agenda /> : <PaginaInicial />;
      case 'fornecedores': return userHasPermission('fornecedores') ? <Fornecedores data={data} addItem={addItem} updateItem={updateItem} deleteItem={deleteItem} setConfirmDelete={setConfirmDelete} effectiveStoreId={effectiveStoreId} updateStock={updateStock} currentUser={user} /> : <PaginaInicial />;
      case 'relatorios': return userHasPermission('relatorios') ? <Relatorios data={data} /> : <PaginaInicial />;
      case 'meu-espaco': return userHasPermission('meu-espaco') ? (
        <MeuEspaco
          user={user}
          resolveActiveStoreForWrite={resolveActiveStoreForWrite}
          currentStoreIdForDisplay={currentStoreIdForDisplay}
        />
      ) : <PaginaInicial />;
          case 'financeiro': return userHasPermission('financeiro') ? <Financeiro data={data} addItem={addItem} updateItem={updateItem} deleteItem={deleteItem} setConfirmDelete={setConfirmDelete} /> : <PaginaInicial />;
      case 'configuracoes': return userHasPermission('configuracoes') ? <Configuracoes user={user} setConfirmDelete={setConfirmDelete} data={data} addItem={addItem} updateItem={updateItem} deleteItem={deleteItem} availableStores={availableStores} storeInfoMap={storeInfoMap} resolveActiveStoreForWrite={resolveActiveStoreForWrite} selectedStoreId={selectedStoreId} storeAlarmSnoozeMap={storeAlarmSnoozeMap} onSaveStoreAlarmSnoozeMinutes={handleSaveStoreAlarmSnoozeMinutes} canManageStoreAlarmConfig={canManageStoreAlarmConfig} onCreateStore={handleCreateStore} onStoreBrandingChange={handleStoreBrandingChange} /> : <PaginaInicial />;
      case 'financeiro': return user?.role === 'admin' ? <Financeiro data={data} addItem={addItem} updateItem={updateItem} deleteItem={deleteItem} setConfirmDelete={setConfirmDelete} /> : <PaginaInicial />;
      case 'configuracoes': return user?.role === 'admin' ? <Configuracoes user={user} setConfirmDelete={setConfirmDelete} data={data} addItem={addItem} updateItem={updateItem} deleteItem={deleteItem} availableStores={availableStores} storeInfoMap={storeInfoMap} resolveActiveStoreForWrite={resolveActiveStoreForWrite} selectedStoreId={selectedStoreId} storeAlarmSnoozeMap={storeAlarmSnoozeMap} onSaveStoreAlarmSnoozeMinutes={handleSaveStoreAlarmSnoozeMinutes} canManageStoreAlarmConfig={canManageStoreAlarmConfig} onCreateStore={handleCreateStore} onStoreBrandingChange={handleStoreBrandingChange} /> : <PaginaInicial />;
      default: return user ? <PlaceholderPage title={allMenuItems.find(i=>i.id===currentPage)?.label || "Página"} /> : <PaginaInicial />;
    }
  };

  const sidebarLogoUrl = currentStoreIdForDisplay && currentStoreIdForDisplay !== STORE_ALL_KEY
    ? storeInfoMap[currentStoreIdForDisplay]?.branding?.logoUrl
    : null;

  return (
    // --- REMOVIDO: onClick={unlockAudio} da div principal ---
    <div className="relative md:flex h-screen bg-gray-100 font-sans">
        {/* --- NOVO: Botão de ativação global renderizado condicionalmente --- */}
                {showActivateSoundButton && (
             <button
                id="btn-ativar-som"
                onClick={async () => {
                    await audioManager.userUnlock({ userGesture: true });
                    setAudioAllowed(audioManager.unlocked);
                    setShowActivateSoundButton(!audioManager.unlocked); // Esconde se desbloqueado
                }}
                className="fixed bottom-4 right-4 z-[9999] px-4 py-2 rounded-xl bg-pink-600 text-white border-none shadow-lg hover:bg-pink-700 transition-colors cursor-pointer"
             >
                🔊 Ativar som de pedidos
             </button>
        )}

        {isiOS && !soundUnlocked && (
          <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 text-center max-w-sm w-full space-y-3">
              <p className="text-lg font-semibold text-gray-800">Toque na tela para ativar o som</p>
              <p className="text-sm text-gray-600">Precisamos da sua interação para liberar os alertas de pedidos no iOS.</p>
            </div>
          </div>
        )}

        {!isDesktop && sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black/50 z-30"></div>}
        
        <div className={`fixed md:relative flex flex-col bg-white shadow-lg h-full transition-transform duration-300 z-40 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${isDesktop ? (sidebarOpen ? 'w-64' : 'w-20') : 'w-64'}`}>
            <div className="flex items-center justify-between p-4 border-b h-16">
                <img src={sidebarLogoUrl || 'logotipo.png'} alt="Logotipo APFood" className={`h-8 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`} />
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-pink-50 hidden md:block">
                    <Menu className="w-6 h-6 text-gray-600" />
                </button>
            </div>
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto"> {/* Adicionado overflow */}
                {menuItems.map((item) => (
                    <button key={item.id} onClick={() => {setCurrentPage(item.id); if(!isDesktop) setSidebarOpen(false);}} className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${currentPage === item.id ? 'bg-pink-100 text-pink-700' : 'hover:bg-pink-50 text-gray-700'} ${!sidebarOpen ? 'justify-center' : ''}`}>
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {(sidebarOpen || !isDesktop) && <span className="font-medium text-sm">{item.label}</span>} {/* Diminuído font size */}
                    </button>
                ))}
            </nav>
            {user && (
            <div className="p-4 border-t">
                <button onClick={handleLogout} className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-pink-50 text-gray-700 ${!sidebarOpen ? 'justify-center' : ''}`}>
                <LogOut className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || !isDesktop) && <span className="text-sm">Sair</span>} {/* Diminuído font size */}
                </button>
            </div>
            )}
        </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 bg-white shadow-sm h-16 flex-shrink-0"> {/* Adicionado flex-shrink-0 */}
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-pink-50 md:hidden">
                <Menu className="w-6 h-6 text-gray-600" />
            </button>

            <div className="flex-1 flex items-center justify-center md:justify-start">
                {user && (
                    user.role === ROLE_OWNER ? (
                        <div className="flex items-center gap-3 flex-wrap">
                            {availableStores.length > 0 && (
                                <>
                                    <span className="text-sm text-gray-500 hidden sm:block">Visão:</span>
                                    <select value={currentStoreIdForDisplay || ''} onChange={handleStoreChange} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500">
                                        <option value={STORE_ALL_KEY}>Visão Geral</option>
                                        {availableStores.map(storeId => (
                                            <option key={storeId} value={storeId}>{storeInfoMap[storeId]?.nome || storeId}</option>
                                        ))}
                                    </select>
                                </>
                            )}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowStoreManager(true)}
                                className="hidden sm:flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Adicionar Loja
                            </Button>
                            <button
                                type="button"
                                onClick={() => setShowStoreManager(true)}
                                className="sm:hidden inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 text-pink-600 hover:bg-pink-50 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                title="Adicionar Loja"
                            >
                                <Plus className="w-5 h-5" />
                                <span className="sr-only">Adicionar Loja</span>
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 flex-wrap">
                            {availableStores.length > 0 ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500 hidden sm:block">Loja:</span>
                                    <select value={currentStoreIdForDisplay || ''} onChange={handleStoreChange} className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500">
                                        {availableStores.map(storeId => (
                                            <option key={storeId} value={storeId}>{storeInfoMap[storeId]?.nome || storeId}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <span className="text-sm text-gray-500 hidden sm:block">Nenhuma loja cadastrada</span>
                            )}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowStoreManager(true)}
                                className="hidden sm:flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Adicionar Loja
                            </Button>
                            <button
                                type="button"
                                onClick={() => setShowStoreManager(true)}
                                className="sm:hidden inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 text-pink-600 hover:bg-pink-50 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                title="Adicionar Loja"
                            >
                                <Plus className="w-5 h-5" />
                                <span className="sr-only">Adicionar Loja</span>
                            </button>
                        </div>
                    )
                )}
            </div>
            <div className="flex items-center justify-end gap-2 sm:gap-3 flex-wrap">
                <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    aria-label="Selecionar localização de entrega"
                >
                    <MapPin className="w-4 h-4" aria-hidden="true" />
                    <span>Entregar em: Setor Central, Goiânia</span>
                </button>

                <button
                    type="button"
                    onClick={() => setCurrentPage('pedidos')}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    aria-label="Abrir carrinho"
                >
                    <ShoppingCart className="w-4 h-4" aria-hidden="true" />
                    <span>Carrinho</span>
                </button>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => {
                            if (!user) {
                                setShowLogin(true);
                                setShowPasswordReset(false);
                                setPasswordResetMessage({ text: '', type: '' });
                                return;
                            }
                            setShowUserMenu(!showUserMenu);
                        }}
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        aria-label={user ? 'Abrir menu da conta' : 'Entrar'}
                    >
                        <UserIcon className="w-4 h-4" aria-hidden="true" />
                        <span>Entrar</span>
                    </button>
                    {user && showUserMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-xl z-20 border p-2">
                            <p className="px-2 py-1 text-sm text-gray-700 font-semibold truncate">{user.auth.displayName || user.auth.email}</p>
                            <button onClick={() => { setCurrentPage('configuracoes'); setShowUserMenu(false); }} className="w-full text-left px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded">Configurações</button>
                            <button onClick={handleLogout} className="w-full text-left px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded">Sair</button>
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button
                        onClick={() => setShowNotifications(!showNotifications)}
                        className="relative inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white p-2 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        aria-label="Abrir notificações"
                    >
                        <Bell className="w-5 h-5" aria-hidden="true" />
                        {pendingOrders.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center animate-pulse">
                                {pendingOrders.length}
                            </span>
                        )}
                    </button>
                    {showNotifications && (
                        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-20 border">
                            <div className="p-4 font-bold border-b">Pedidos Pendentes ({pendingOrders.length})</div>
                            <div className="p-2 max-h-96 overflow-y-auto">
                                {pendingOrders.length > 0 ? (
                                    pendingOrders.map(order => (
                                        <div key={order.id} className="p-2 border-b hover:bg-gray-50 cursor-pointer" onClick={() => { setCurrentPage('pedidos'); setShowNotifications(false); }}>
                                            <p className="font-semibold">{order.clienteNome || 'Cliente'}</p>
                                            <p className="text-sm text-gray-500">ID: {order.id?.substring(0,8) || 'N/A'}</p>
                                            <p className="text-sm text-gray-500">Data: {getJSDate(order.createdAt)?.toLocaleDateString() || '-'}</p>
                                            <p className="text-sm">Status: <span className="font-medium">{order.status}</span></p>
                                        </div>
                                    ))
                                ) : (
                                    <p className="p-4 text-center text-gray-500">Nenhum pedido pendente.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
        <main className="flex-1 overflow-y-auto">
            {renderCurrentPage()}
        </main>
      </div>

      <StoreManagerModal
        isOpen={showStoreManager}
        onClose={() => setShowStoreManager(false)}
        availableStores={availableStores}
        storeInfoMap={storeInfoMap}
        onCreateStore={handleCreateStore}
        onSelectStore={selectStoreById}
        canCreate={canCreateStores}
        allowAllOption={user?.role === ROLE_OWNER}
        currentStoreId={currentStoreIdForDisplay}
        isCreatingStore={isCreatingStore}
      />

      <Modal isOpen={showLogin} onClose={() => {setShowLogin(false); setLoginError(''); setPasswordResetMessage({ text: '', type: '' });}} title={showPasswordReset ? "Recuperar Senha" : "Login"} size="sm">
        {showPasswordReset ? (
            <div className="space-y-4">
                <p className="text-sm text-gray-600">Insira seu e-mail para enviarmos um link de recuperação.</p>
                <Input label="Email" type="email" placeholder="seu@email.com" value={passwordResetEmail} onChange={(e) => setPasswordResetEmail(e.target.value)} />
                {passwordResetMessage.text && (
                    <p className={`text-sm ${passwordResetMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {passwordResetMessage.text}
                    </p>
                )}
                <div className="flex flex-col gap-2">
                    <Button onClick={handlePasswordReset} disabled={passwordResetMessage.type === 'loading'}>
                        {passwordResetMessage.type === 'loading' ? 'Enviando...' : 'Enviar Email de Recuperação'}
                    </Button>
                    <button onClick={() => setShowPasswordReset(false)} className="text-sm text-pink-600 hover:underline text-center">
                        Voltar para o Login
                    </button>
                </div>
            </div>
        ) : (
            <div className="space-y-4">
                <Input label="Email" type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Input label="Senha" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                <button onClick={() => setShowPasswordReset(true)} className="text-sm text-pink-600 hover:underline text-left w-full">
                    Esqueci a senha
                </button>
                {loginError && <p className="text-red-500 text-sm text-center">{loginError}</p>}
                <div className="flex flex-col gap-4 pt-2">
                    <Button onClick={handleLogin} disabled={isAuthInProgress}>{isAuthInProgress ? 'Entrando...' : 'Entrar'}</Button>
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="bg-white px-2 text-gray-500">ou</span>
                        </div>
                    </div>
                    <Button onClick={handleGoogleSignIn} variant="secondary" disabled={isAuthInProgress}>
                        <svg className="w-5 h-5" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                            <path fill="none" d="M0 0h48v48H0z"></path>
                        </svg>
                        Entrar com Google
                    </Button>
                </div>
            </div>
        )}
      </Modal>

      <Modal isOpen={confirmDelete.isOpen} onClose={() => setConfirmDelete({ isOpen: false, onConfirm: ()=>{} })} title="Confirmar Exclusão" size="sm">
        <div className="space-y-6">
            <p className="text-gray-600">Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.</p>
            <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={() => setConfirmDelete({ isOpen: false, onConfirm: ()=>{} })}>Cancelar</Button>
                <Button variant="danger" onClick={() => {
                  confirmDelete.onConfirm();
                  setConfirmDelete({ isOpen: false, onConfirm: () => {} });
                }}>Excluir</Button>
            </div>
        </div>
      </Modal>
      
    </div>
  );
}

export default App;
